import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { 
    ArrowLeft, Book, Search, ChevronRight, Image, Video, 
    Settings, Users, Building2, Calendar, FileText, Database,
    BarChart3, Briefcase, Shield, Wand2, Link as LinkIcon
} from 'lucide-react';

const CLIENT_HELP_SECTIONS = new Set(['events', 'reports']);

export default function HelpPage() {
    const navigate = useNavigate();
    const { role } = useOutletContext() || {};
    const isClient = role === 'client';
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSection, setSelectedSection] = useState(null);
    const [selectedSubsection, setSelectedSubsection] = useState(null);
    const [expandedSections, setExpandedSections] = useState(() => new Set());

    // Helper component for rendering images
    const HelpImage = ({ src, alt, caption }) => (
        <div className="my-4">
            <img 
                src={src} 
                alt={alt || 'Help screenshot'} 
                className="w-full rounded-lg border border-gray-300 shadow-sm"
                onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                }}
            />
            <div className="hidden bg-gray-100 rounded-lg p-8 border-2 border-dashed border-gray-300 text-center text-gray-500">
                <Image size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Image not found: {src}</p>
                <p className="text-xs mt-1">Please add the image to the public folder or update the path</p>
            </div>
            {caption && <p className="text-sm text-gray-600 mt-2 italic text-center">{caption}</p>}
        </div>
    );

    // Helper component for rendering videos
    const HelpVideo = ({ src, type = 'youtube', title }) => {
        if (type === 'youtube') {
            // Extract YouTube video ID from URL
            const getYouTubeId = (url) => {
                const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                const match = url.match(regExp);
                return (match && match[2].length === 11) ? match[2] : null;
            };
            const videoId = getYouTubeId(src);
            if (!videoId) {
                return (
                    <div className="bg-gray-100 rounded-lg p-8 border-2 border-dashed border-gray-300 text-center text-gray-500 my-4">
                        <Video size={32} className="mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Invalid YouTube URL: {src}</p>
                    </div>
                );
            }
            return (
                <div className="my-4">
                    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                        <iframe
                            className="absolute top-0 left-0 w-full h-full rounded-lg"
                            src={`https://www.youtube.com/embed/${videoId}`}
                            title={title || 'Video tutorial'}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                    {title && <p className="text-sm text-gray-600 mt-2 italic text-center">{title}</p>}
                </div>
            );
        }
        // Direct video file
        return (
            <div className="my-4">
                <video 
                    src={src} 
                    controls 
                    className="w-full rounded-lg border border-gray-300"
                    title={title || 'Video tutorial'}
                >
                    Your browser does not support the video tag.
                </video>
                {title && <p className="text-sm text-gray-600 mt-2 italic text-center">{title}</p>}
            </div>
        );
    };

    // Helper function to render formatted instruction text
    const renderInstructionText = (text) => {
        if (!text) return null;

        // Check for markdown headers
        const isH2 = /^##\s/.test(text.trim());
        const isH3 = /^###\s/.test(text.trim());
        const isH4 = /^####\s/.test(text.trim());
        
        // Check for blockquotes
        const isBlockquote = /^>\s/.test(text.trim());
        
        // Check for code blocks (standalone line with ```)
        const isCodeBlock = /^```/.test(text.trim());
        
        // Check for code blocks (backticks)
        const codeBlockRegex = /```([\s\S]*?)```/g;
        const inlineCodeRegex = /`([^`]+)`/g;
        
        // Check for bold text
        const boldRegex = /\*\*([^*]+)\*\*/g;
        
        // Check for section headers (lines ending with ':')
        const isHeader = text.endsWith(':') && text.length < 100 && !isH2 && !isH3 && !isH4;
        
        // Check for examples
        const isExample = /^(Example|Examples|Example:|Examples:)/i.test(text.trim());
        
        // Check for notes/important
        const isNote = /^(Note|Important|Warning|Tip|Best Practice|Troubleshooting):/i.test(text.trim());
        
        // Check for bullet points
        const isBullet = /^\s*[•\-*]\s/.test(text);
        
        // Check for numbered list items
        const isNumbered = /^\s*\d+\.\s/.test(text);

        // Process code blocks first
        let processedText = text;
        const codeBlocks = [];
        let codeBlockIndex = 0;
        
        processedText = processedText.replace(codeBlockRegex, (match, code) => {
            const id = `codeblock-${codeBlockIndex++}`;
            codeBlocks.push({ id, code: code.trim() });
            return `__CODEBLOCK_${id}__`;
        });

        // Process inline code
        const inlineCodes = [];
        let inlineCodeIndex = 0;
        processedText = processedText.replace(inlineCodeRegex, (match, code) => {
            const id = `inlinecode-${inlineCodeIndex++}`;
            inlineCodes.push({ id, code });
            return `__INLINECODE_${id}__`;
        });

        // Process bold text
        const boldTexts = [];
        let boldIndex = 0;
        processedText = processedText.replace(boldRegex, (match, bold) => {
            const id = `bold-${boldIndex++}`;
            boldTexts.push({ id, text: bold });
            return `__BOLD_${id}__`;
        });

        // Handle markdown headers
        if (isH2) {
            const headerText = processedText.replace(/^##\s+/, '');
            return (
                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4 pb-2 border-b-2 border-gray-300">
                    {renderFormattedLine(headerText, inlineCodes, boldTexts, codeBlocks)}
                </h2>
            );
        }

        if (isH3) {
            const headerText = processedText.replace(/^###\s+/, '');
            return (
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                    {renderFormattedLine(headerText, inlineCodes, boldTexts, codeBlocks)}
                </h3>
            );
        }

        if (isH4) {
            const headerText = processedText.replace(/^####\s+/, '');
            return (
                <h4 className="text-lg font-semibold text-gray-900 mt-4 mb-2">
                    {renderFormattedLine(headerText, inlineCodes, boldTexts, codeBlocks)}
                </h4>
            );
        }

        // Handle blockquotes
        if (isBlockquote) {
            const quoteText = processedText.replace(/^>\s+/, '');
            return (
                <blockquote className="my-4 pl-4 border-l-4 border-blue-500 italic text-gray-700 bg-blue-50 py-2 rounded-r">
                    {renderFormattedLine(quoteText, inlineCodes, boldTexts, codeBlocks)}
                </blockquote>
            );
        }

        // Handle standalone code blocks
        if (isCodeBlock) {
            const code = processedText.replace(/^```\s*/, '').replace(/\s*```$/, '').trim();
            if (code) {
                return (
                    <pre className="my-4 p-4 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto font-mono text-sm border border-gray-700 shadow-lg">
                        <code>{code}</code>
                    </pre>
                );
            }
            return null;
        }

        // Render based on type
        if (isExample) {
            return (
                <div className="my-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg shadow-sm">
                    <div className="flex items-start gap-2 mb-2">
                        <span className="font-semibold text-blue-900">Example:</span>
                    </div>
                    <div className="text-gray-800">
                        {processedText.split('\n').map((line, idx) => {
                            if (idx === 0) return null; // Skip the "Example:" line
                            if (line.trim().startsWith('```') || line.includes('__CODEBLOCK_')) {
                                // Extract code block
                                const codeMatch = line.match(/__CODEBLOCK_([^_]+)__/);
                                if (codeMatch) {
                                    const codeId = codeMatch[1];
                                    const codeBlock = codeBlocks.find(cb => cb.id === codeId);
                                    if (codeBlock) {
                                        return (
                                            <pre key={idx} className="mt-2 mb-2 p-3 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto font-mono text-sm shadow-inner">
                                                <code>{codeBlock.code}</code>
                                            </pre>
                                        );
                                    }
                                }
                            }
                            return <div key={idx}>{renderFormattedLine(line, inlineCodes, boldTexts, codeBlocks)}</div>;
                        })}
                    </div>
                </div>
            );
        }

        if (isNote) {
            const noteType = text.match(/^(Note|Important|Warning|Tip|Best Practice|Troubleshooting):/i)?.[1] || 'Note';
            const bgColor = noteType.toLowerCase().includes('important') || noteType.toLowerCase().includes('warning') 
                ? 'bg-yellow-50 border-yellow-500' 
                : noteType.toLowerCase().includes('troubleshooting')
                ? 'bg-red-50 border-red-500'
                : 'bg-green-50 border-green-500';
            const textColor = noteType.toLowerCase().includes('important') || noteType.toLowerCase().includes('warning')
                ? 'text-yellow-900'
                : noteType.toLowerCase().includes('troubleshooting')
                ? 'text-red-900'
                : 'text-green-900';
            
            return (
                <div className={`my-4 p-4 ${bgColor} border-l-4 rounded-r-lg shadow-sm`}>
                    <div className="flex items-start gap-2 mb-2">
                        <span className={`font-semibold ${textColor}`}>{noteType}:</span>
                    </div>
                    <div className={`${textColor.replace('900', '800')}`}>
                        {renderFormattedLine(text.replace(/^(Note|Important|Warning|Tip|Best Practice|Troubleshooting):\s*/i, ''), inlineCodes, boldTexts, codeBlocks)}
                    </div>
                </div>
            );
        }

        if (isHeader) {
            return (
                <h5 className="text-lg font-semibold text-gray-900 mt-6 mb-3">
                    {renderFormattedLine(text.replace(':', ''), inlineCodes, boldTexts, codeBlocks)}
                </h5>
            );
        }

        if (isBullet || isNumbered) {
            return (
                <div className="ml-6 mb-2 text-gray-700">
                    {renderFormattedLine(text, inlineCodes, boldTexts, codeBlocks)}
                </div>
            );
        }

        // Regular paragraph
        return (
            <p className="mb-3 text-gray-700 leading-relaxed">
                {renderFormattedLine(processedText, inlineCodes, boldTexts, codeBlocks)}
            </p>
        );
    };

    // Helper to render a line with formatting
    const renderFormattedLine = (line, inlineCodes, boldTexts, codeBlocks) => {
        if (!line) return null;

        // Handle code blocks
        const codeBlockMatch = line.match(/__CODEBLOCK_([^_]+)__/);
        if (codeBlockMatch) {
            const codeId = codeBlockMatch[1];
            const codeBlock = codeBlocks.find(cb => cb.id === codeId);
            if (codeBlock) {
                return (
                    <pre key={codeId} className="my-3 p-4 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto font-mono text-sm border border-gray-700">
                        <code>{codeBlock.code}</code>
                    </pre>
                );
            }
        }

        // Split by placeholders and render
        const parts = line.split(/(__INLINECODE_[^_]+__|__BOLD_[^_]+__)/g);
        
        return (
            <>
                {parts.map((part, idx) => {
                    // Inline code
                    const inlineMatch = part.match(/__INLINECODE_([^_]+)__/);
                    if (inlineMatch) {
                        const codeId = inlineMatch[1];
                        const inlineCode = inlineCodes.find(ic => ic.id === codeId);
                        if (inlineCode) {
                            return (
                                <code key={idx} className="px-2 py-1 bg-gray-100 text-gray-800 rounded font-mono text-sm border border-gray-300">
                                    {inlineCode.code}
                                </code>
                            );
                        }
                    }
                    
                    // Bold text
                    const boldMatch = part.match(/__BOLD_([^_]+)__/);
                    if (boldMatch) {
                        const boldId = boldMatch[1];
                        const bold = boldTexts.find(b => b.id === boldId);
                        if (bold) {
                            return <strong key={idx} className="font-semibold text-gray-900">{bold.text}</strong>;
                        }
                    }
                    
                    return <span key={idx}>{part}</span>;
                })}
            </>
        );
    };

    const sections = [
        {
            id: 'getting-started',
            title: 'Getting Started',
            icon: Book,
            description: 'Introduction and basic setup',
            subsections: [
                {
                    id: 'overview',
                    title: 'Overview',
                    content: 'Introduction to ClientBox - Your comprehensive dealership management and onboarding system',
                    instructions: [
                        '## Welcome to ClientBox',
                        'ClientBox is a comprehensive dealership management and onboarding system designed to streamline your operations. This platform helps you manage OEMs (Original Equipment Manufacturers), dealership groups, locations, events, appointments, and much more from a single, unified interface.',
                        '### System Architecture',
                        'The system is organised into several main sections accessible from the sidebar navigation, each providing specific functionality tailored to different aspects of dealership management.',
                        '### Key Features',
                        'ClientBox provides a comprehensive suite of tools for dealership management:',
                        '  • **Dealership Onboarding**: Complete onboarding and configuration management for new dealerships',
                        '  • **Event Management**: Create and manage events with customer booking capabilities',
                        '  • **Job List & Invoicing**: Track work orders, invoices, and client information',
                        '  • **Data Management**: Import and export tools for bulk data operations',
                        '  • **Workflow Automation**: Create and manage automated workflows for business processes',
                        '  • **Reporting & Analytics**: Comprehensive reporting and analytics dashboards',
                        '  • **User Management**: Role-based access control and user management',
                        '### Role-Based Access',
                        'The system uses **role-based permissions**, ensuring users only see and can access features relevant to their responsibilities. Common roles include:',
                        '  • **Admin**: Full system access and configuration',
                        '  • **Setup**: Dealership onboarding and configuration',
                        '  • **Event Manager**: Event and appointment management',
                        '  • **Job List User**: Work orders and invoicing',
                        '### Getting Help',
                        'Use the **Help** button (question mark icon) in the top bar to access this documentation at any time. The help system provides:',
                        '  • Step-by-step instructions for all features',
                        '  • Examples and code snippets',
                        '  • Troubleshooting guides',
                        '  • Best practices and tips',
                        '## Best Practices',
                        '### Getting Started',
                        '  • **Familiarise yourself with navigation**: Review the sidebar structure before starting',
                        '  • **Understand your role**: Review your assigned role and permissions to understand what you can access',
                        '  • **Bookmark help**: Keep this help documentation bookmarked for quick reference',
                        '  • **Contact support**: Contact your system administrator if you need additional permissions or access',
                        '### Working Efficiently',
                        '  • Use search functionality to quickly find records',
                        '  • Utilise filters to narrow down large datasets',
                        '  • Take advantage of bulk operations where available',
                        '  • Regularly review and update your data',
                        '## Troubleshooting',
                        '### Common Issues',
                        '**Problem**: Cannot see certain sections',
                        '  • **Solution**: Check with your administrator about your role permissions',
                        '  • **Solution**: Verify that the section is enabled for your organisation',
                        '**Problem**: Pages load slowly',
                        '  • **Solution**: Check your internet connection',
                        '  • **Solution**: Clear your browser cache and cookies',
                        '  • **Solution**: Try refreshing the page',
                        '**Problem**: Error messages appear',
                        '  • **Solution**: Note the error details (error code, message, and what you were doing)',
                        '  • **Solution**: Contact support with the error information',
                        '  • **Solution**: Check if other users are experiencing the same issue'
                    ],
                    image: null, // Add path like '/help-images/overview.png'
                    video: null // Add YouTube URL or video path
                },
                {
                    id: 'login',
                    title: 'Login & Authentication',
                    content: 'How to log in, manage your account, and understand authentication',
                    instructions: [
                        'Understanding Authentication:',
                        'ClientBox uses secure email and password authentication to protect your account and data.',
                        'Each user must have a valid account created by a system administrator before they can log in.',
                        'Step-by-Step Login Process:',
                        '1. Navigate to the login page using your web browser.',
                        '2. Enter your email address in the "Email" field (this is the email address associated with your account).',
                        '3. Enter your password in the "Password" field (passwords are case-sensitive).',
                        '4. Click the "Sign In" button to authenticate.',
                        '5. Upon successful login, you will be redirected to your dashboard based on your role and permissions.',
                        'Your Dashboard:',
                        'After logging in, you will see your personalised dashboard showing relevant information based on your role.',
                        'Admin users see system-wide statistics and management tools.',
                        'Setup users see dealership-specific onboarding and configuration options.',
                        'Event users see event management and appointment information.',
                        'Job List users see work orders, invoices, and client information.',
                        'Account Information:',
                        'Your user information and role are displayed in the top-right corner of the screen.',
                        'This shows your name, email address, and current role.',
                        'You can view your account details by clicking on your name or profile icon.',
                        'Password Management:',
                        'If you have forgotten your password, contact your system administrator to reset it.',
                        'Administrators can reset passwords and send new login credentials via email.',
                        'For security reasons, users cannot reset their own passwords directly.',
                        'Logging Out:',
                        'To log out, click the "Logout" button in the top bar.',
                        'Always log out when finished, especially when using shared or public computers.',
                        'Your session will remain active until you log out or it expires due to inactivity.',
                        'Best Practices:',
                        '  • Use a strong, unique password for your account',
                        '  • Never share your login credentials with others',
                        '  • Log out when finished, especially on shared devices',
                        '  • Contact your administrator immediately if you suspect unauthorised access',
                        'Troubleshooting:',
                        '  • Cannot log in: Verify your email address is correct and contact your administrator if the issue persists',
                        '  • "Invalid credentials" error: Check that your email and password are correct (case-sensitive)',
                        '  • Account locked: Contact your administrator to unlock your account',
                        '  • Redirected to wrong dashboard: Contact your administrator to verify your role assignments'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'navigation',
                    title: 'Navigation',
                    content: 'Understanding the interface, menus, and how to move around the system',
                    instructions: [
                        'Understanding the Interface:',
                        'ClientBox uses a standard sidebar navigation layout with a top bar for quick actions and account information.',
                        'The interface is designed to be intuitive and responsive, adapting to different screen sizes.',
                        'Sidebar Navigation:',
                        'The main navigation is located in the left sidebar, providing access to all major sections of the system.',
                        'Menu items are organised by function and role-based permissions determine which sections you can see.',
                        'Main Menu Sections (visible based on your role):',
                        '  • **Setup** - Dealership onboarding, configuration, and management',
                        '    - Dealership Configuration: Dealership Management, OEMs, Dealership Onboarding',
                        '    - Templates & Content: Workflows, Workflow Builder, Workflow Templates, Message Suites, Message Templates, Brand Templates',
                        '    - Data Management: Imported Contact Plans',
                        '    - When viewing a specific dealership: Dashboard, Checklist, Locations, Sign Off',
                        '  • **Job List & Invoicing** - Job management, clients, invoices, and pipeline tracking',
                        '    - Dashboard, Clients, Jobs, Invoices, Pipeline, Rate Card',
                        '  • **Event Management** - Event creation, appointments, and reporting',
                        '    - Events, Appointments, View Reports, Calendar Sync',
                        '  • **Administration** (Admin only) - System administration and configuration',
                        '    - Field Mapping: Checklist, Fact Find, Links, Sign Off',
                        '    - Reference Data: Brands, OEMs, Departments, Dealership Groups',
                        '    - User Management: Users, Roles & Permissions',
                        '    - Data Operations: Data Imports, Data Exports, Database Config, Table Browser',
                        '    - Integrations: Field Integration, VoiceBox Sync, Xero Config',
                        '    - System: General Settings, Data Cleanup, Audit Logs, Financial Years',
                        'Click on any menu item to navigate to that section.',
                        'Some menu items have sub-items that expand when you click on them, revealing additional options.',
                        'Expandable menu items show a chevron icon (►) that rotates when expanded.',
                        'The sidebar can be collapsed by clicking the menu icon (hamburger icon), showing only icons to save screen space.',
                        'When collapsed, hover over icons to see tooltips with the full menu item name.',
                        'Top Bar:',
                        'The top bar displays the ClientBox logo on the left side.',
                        'On the right side, you will see:',
                        '  • Your name and role',
                        '  • Help button (question mark icon) - opens this documentation',
                        '  • Logout button - securely logs you out of the system',
                        'Main Content Area:',
                        'The main content area displays the current page you are viewing.',
                        'This area changes based on which section you navigate to.',
                        'Pages may include tables, forms, charts, and other interactive elements.',
                        'Breadcrumbs:',
                        'Breadcrumbs may appear at the top of pages to show your current location in the system.',
                        'Example: Home > Admin > Users > Edit User',
                        'Click on any breadcrumb item to navigate back to that level.',
                        'Breadcrumbs help you understand where you are and provide quick navigation to parent sections.',
                        'Search and Filtering:',
                        'Many pages include search functionality to quickly find specific records.',
                        'Use the search box to filter lists by name, email, or other relevant fields.',
                        'Advanced filters may be available on some pages for more complex searches.',
                        'Keyboard Shortcuts:',
                        '  • Tab - Navigate between form fields',
                        '  • Enter - Submit forms or confirm actions',
                        '  • Esc - Close modals or cancel actions',
                        '  • Ctrl/Cmd + F - Search within the current page',
                        'Best Practices:',
                        '  • Use the sidebar to quickly access frequently used sections',
                        '  • Collapse the sidebar when working with wide tables or detailed views',
                        '  • Use breadcrumbs to navigate back to previous sections',
                        '  • Bookmark the Help section for quick access to documentation',
                        'Troubleshooting:',
                        '  • Menu items not appearing: Check with your administrator about role permissions',
                        '  • Sidebar not responding: Refresh the page or clear your browser cache',
                        '  • Can\'t find a section: Use the search function in the Help section or contact your administrator',
                        '  • Navigation feels slow: Check your internet connection and browser performance'
                    ],
                    image: null,
                    video: null
                }
            ]
        },
        {
            id: 'setup',
            title: 'Setup & Configuration',
            icon: Settings,
            description: 'System configuration and settings',
            subsections: [
                {
                    id: 'oems',
                    title: 'OEMs Management',
                    content: 'Managing Original Equipment Manufacturers (OEMs) and their associated brands',
                    instructions: [
                        'Understanding OEMs:',
                        'OEMs (Original Equipment Manufacturers) represent vehicle manufacturers or major equipment suppliers in the system.',
                        'Each OEM can have multiple brands associated with it, helping organise your dealership structure.',
                        'OEMs are used throughout the system for filtering, reporting, and organising dealership locations and events.',
                        'Accessing OEM Management:',
                        'Navigate to Admin → OEMs to access the OEMs management page.',
                        'You will see a list of all OEMs in the system, showing their name, description, and active status.',
                        'Creating a New OEM:',
                        '1. Click the "Add OEM" button at the top of the page.',
                        '2. Fill in the required information:',
                        '   • Name (required) - The official name of the OEM (e.g., "BMW Group", "Mercedes-Benz")',
                        '   • Description - Optional description of the OEM',
                        '   • Website URL - Optional link to the OEM\'s official website',
                        '   • Logo URL - Optional link to the OEM\'s logo image (use full URL or relative path)',
                        '3. Set the "Active" status toggle:',
                        '   • Active - The OEM will appear in dropdowns and can be used throughout the system',
                        '   • Inactive - The OEM is hidden but not deleted (useful for historical data)',
                        '4. Click "Save" to create the OEM.',
                        'Editing an Existing OEM:',
                        '1. Find the OEM in the list.',
                        '2. Click on the OEM row or use the edit icon/button.',
                        '3. Modify any of the fields (name, description, URLs, active status).',
                        '4. Click "Save" to update the OEM.',
                        'Managing Brands:',
                        'Brands represent specific vehicle or product lines within an OEM.',
                        'To add brands to an OEM:',
                        '1. Expand the OEM details by clicking on it or using the expand icon.',
                        '2. In the brands section, click "Add Brand".',
                        '3. You have two options:',
                        '   • Create a new brand - Enter a new brand name that will be created and assigned to this OEM',
                        '   • Assign existing brand - Select from a list of existing brands (including independent brands)',
                        '4. The brand will be associated with the OEM.',
                        'Removing Brands from OEMs:',
                        '1. Find the brand in the OEM\'s brand list.',
                        '2. Click the "X" button next to the brand name.',
                        '3. Confirm the removal when prompted.',
                        '4. Removed brands will be automatically assigned to the "Independent" OEM category.',
                        'The "Independent" OEM:',
                        'This is a special system category for brands not associated with a specific OEM.',
                        'Brands moved here can be reassigned to other OEMs later if needed.',
                        'Best Practices:',
                        '  • Use consistent naming conventions for OEMs (e.g., "BMW Group" not "BMW" or "Bayerische Motoren Werke")',
                        '  • Keep OEM descriptions concise but informative',
                        '  • Use high-quality logo images for better visual representation',
                        '  • Set OEMs to inactive rather than deleting them to preserve historical data',
                        '  • Organise brands logically under their parent OEMs',
                        'Troubleshooting:',
                        '  • Cannot create OEM: Verify you have Admin role permissions',
                        '  • Brand not appearing: Check that the brand is assigned to the correct OEM',
                        '  • Logo not displaying: Verify the logo URL is correct and accessible',
                        '  • Cannot remove brand: Check if the brand is associated with any locations (remove associations first)'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'groups',
                    title: 'Dealership Groups',
                    content: 'Setting up and managing dealership groups and their associated locations',
                    instructions: [
                        'Understanding Dealership Groups:',
                        'Dealership groups represent organisations that own or manage multiple dealership locations.',
                        'Groups help organise locations under a single entity, making it easier to manage settings, reports, and configurations across multiple sites.',
                        'Each group can contain multiple locations, and each location can be associated with one or more brands.',
                        'Accessing Group Management:',
                        'Navigate to Admin → Groups to manage dealership groups.',
                        'You will see a table showing all groups with their name and the number of locations in each group.',
                        'Creating a New Dealership Group:',
                        '1. Click the "Add Group" button at the top of the page.',
                        '2. Enter the group name (required) - This should be the official name of the dealership organisation.',
                        '3. Optionally add any additional details or notes about the group.',
                        '4. Click "Save" to create the group.',
                        'The new group will appear in the list, ready to have locations added to it.',
                        'Editing a Group:',
                        '1. Find the group in the list.',
                        '2. Click the edit icon next to the group name.',
                        '3. Update the group name or other details as needed.',
                        '4. Click "Save" to apply the changes.',
                        'Note: Changing a group name does not affect the locations within it.',
                        'Deleting a Group:',
                        'Warning: Deleting a group will also delete all associated locations and their data.',
                        '1. Click the delete icon next to the group name.',
                        '2. Confirm the deletion when prompted.',
                        '3. All locations, addresses, and brand associations within the group will be permanently removed.',
                        'Best Practice: Deactivate locations or groups instead of deleting them to preserve historical data.',
                        'Viewing and Managing Locations:',
                        '1. Expand a group by clicking on it or using the expand icon.',
                        '2. You will see a list of all locations within that group.',
                        '3. The expanded view shows location names, addresses, and associated brands.',
                        'Adding Locations to a Group:',
                        '1. Expand the group where you want to add a location.',
                        '2. Click "Add Location" within the expanded group section.',
                        '3. Fill in the location details:',
                        '   • Location name (required) - e.g., "ABC Motors - Downtown Branch"',
                        '   • Address details including building name/number, street, locality, town/city, county, postcode, and country',
                        '   • Address type (e.g., Business, Mailing)',
                        '4. Select brands to associate with the location from the available brands dropdown.',
                        '5. You can select multiple brands if the location represents multiple franchises.',
                        '6. Click "Save" to create the location.',
                        'Managing Brand Associations:',
                        'Each location can have multiple brands associated with it.',
                        'To add a brand to a location:',
                        '1. Find the location in the expanded group view.',
                        '2. In the brands section, select a brand from the dropdown.',
                        '3. Click "Add Brand" to associate it with the location.',
                        'To remove a brand from a location:',
                        '1. Find the brand in the location\'s brand list.',
                        '2. Click the "X" button next to the brand name.',
                        '3. The brand association will be removed (the brand itself is not deleted).',
                        'Editing and Deleting Locations:',
                        'Locations can be edited or deleted from within the group view.',
                        'To edit: Click the edit icon next to the location name and modify the details.',
                        'To delete: Click the delete icon and confirm (this action cannot be undone).',
                        'Best Practices:',
                        '  • Use clear, descriptive group names that match the organisation\'s official name',
                        '  • Keep location names specific and unique (include branch/area identifiers)',
                        '  • Ensure all address fields are complete for accurate reporting and mapping',
                        '  • Associate all relevant brands with each location for proper filtering',
                        '  • Use groups to manage locations that belong to the same organisation',
                        '  • Deactivate rather than delete groups/locations to preserve historical data',
                        'Troubleshooting:',
                        '  • Cannot create group: Verify you have Admin role permissions',
                        '  • Location not appearing: Check that the location is saved and the group is expanded',
                        '  • Cannot add brand: Verify the brand exists in the system (check Admin → Brands)',
                        '  • Delete not working: Check if the location has associated events or appointments (remove associations first)',
                        '  • Address validation errors: Ensure all required address fields are filled correctly'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'locations',
                    title: 'Dealership Locations',
                    content: 'Configuring individual dealership locations with addresses, brands, and contact information',
                    instructions: [
                        'Understanding Dealership Locations:',
                        'Dealership locations represent individual physical sites or branches within a dealership group.',
                        'Each location can be associated with multiple brands, have its own address, contact information, and settings.',
                        'Locations are used throughout the system for event management, appointments, reporting, and onboarding configuration.',
                        'Accessing Location Management:',
                        'Dealership locations are managed within their parent groups.',
                        'Navigate to Admin → Groups and expand a group to see its locations.',
                        'The expanded view shows all locations within that group, along with their details.',
                        'Creating a New Location:',
                        '1. First, select or create a dealership group (locations must belong to a group).',
                        '2. Expand the group by clicking on it.',
                        '3. Within the expanded group, click "Add Location" to create a new location.',
                        '4. Fill in the location details:',
                        '   • Location name (required) - Use a clear, descriptive name (e.g., "ABC Motors - City Centre")',
                        '   • Address details:',
                        '     - Building name/number',
                        '     - Street name',
                        '     - Locality',
                        '     - Town/City',
                        '     - County',
                        '     - Postcode',
                        '     - Country',
                        '   • Address type - Select from dropdown (e.g., Business, Mailing, Delivery)',
                        '5. Associate brands with the location:',
                        '   • Select from available brands in the dropdown',
                        '   • Click "Add Brand" to associate each brand',
                        '   • You can select multiple brands if the location represents multiple franchises',
                        '6. Add contact information (optional but recommended):',
                        '   • Phone numbers (primary and secondary)',
                        '   • Email addresses',
                        '   • Website URL',
                        '7. Click "Save" to create the location.',
                        'Managing Brand Associations:',
                        'Each location can have multiple brands associated with it.',
                        'This is useful for multi-franchise locations or locations that sell multiple brand lines.',
                        'To add a brand:',
                        '1. Find the location in the expanded group view.',
                        '2. In the brands section, select a brand from the dropdown.',
                        '3. Click "Add Brand" to associate it.',
                        'To remove a brand:',
                        '1. Find the brand in the location\'s brand list.',
                        '2. Click the "X" button next to the brand name.',
                        '3. The brand association is removed (the brand itself remains in the system).',
                        'Editing Location Details:',
                        '1. Find the location in the expanded group view.',
                        '2. Click the edit icon next to the location name.',
                        '3. Modify any of the fields (name, address, brands, contact information).',
                        '4. Click "Save" to apply changes.',
                        'Activating and Deactivating Locations:',
                        'Locations can be activated or deactivated as needed.',
                        'Active locations appear in dropdowns and can be used for events and appointments.',
                        'Inactive locations are hidden but not deleted, preserving historical data.',
                        'Deactivate locations instead of deleting them to maintain data integrity.',
                        'Location Usage Throughout the System:',
                        'Locations are used in:',
                        '  • Event management - Linking events to specific locations',
                        '  • Appointment booking - Customers book appointments at specific locations',
                        '  • Onboarding - Each location can have its own onboarding checklist and configuration',
                        '  • Reporting - Location-based analytics and metrics',
                        '  • Workflows - Location-specific communication strategies',
                        'Best Practices:',
                        '  • Use consistent naming conventions for locations (include group name or area identifier)',
                        '  • Ensure all address fields are complete and accurate for mapping and reporting',
                        '  • Associate all relevant brands with each location for proper filtering',
                        '  • Keep contact information up to date',
                        '  • Use clear, descriptive location names that distinguish between branches',
                        '  • Deactivate rather than delete locations to preserve historical data',
                        'Troubleshooting:',
                        '  • Cannot add location: Verify you have Admin role and the group is expanded',
                        '  • Brand not in dropdown: Check that the brand exists in the system (Admin → Brands)',
                        '  • Address validation errors: Ensure all required address fields are filled correctly',
                        '  • Location not appearing in events: Verify the location is active and properly saved',
                        '  • Cannot delete location: Check if location has associated events, appointments, or onboarding data (remove associations first)'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'brands',
                    title: 'Brands Management',
                    content: 'Managing vehicle and product brands and their associations with OEMs and locations',
                    instructions: [
                        'Understanding Brands:',
                        'Brands represent different vehicle or product brands in the system (e.g., "BMW 3 Series", "Mercedes C-Class", "Ford Focus").',
                        'Brands are organised under OEMs (Original Equipment Manufacturers) or can be set as "Independent".',
                        'Brands can be associated with multiple dealership locations, allowing you to track which brands are available at which locations.',
                        'Accessing Brand Management:',
                        'Navigate to Admin → Brands to view and manage all brands in the system.',
                        'You will see a table showing all brands with their name and associated OEM.',
                        'The table helps you quickly see which brands belong to which OEMs.',
                        'Creating New Brands:',
                        'Brands are typically created when adding them to an OEM.',
                        'Method 1 - Through OEMs Page:',
                        '1. Navigate to Admin → OEMs.',
                        '2. Expand or select an OEM.',
                        '3. Click "Add Brand" in the brands section.',
                        '4. You have two options:',
                        '   • Create a new brand - Enter a new brand name that will be created and assigned to this OEM',
                        '   • Assign existing brand - Select from a list of existing brands (including independent brands)',
                        '5. The brand will be associated with the OEM.',
                        'Method 2 - Create Independent Brand First:',
                        '1. Create a brand through an OEM (can be moved to Independent later).',
                        '2. Remove it from the OEM (it will automatically move to Independent).',
                        '3. Reassign it to the desired OEM when ready.',
                        'Brand-OEM Associations:',
                        'Brands can be associated with OEMs or set as "Independent".',
                        'The "Independent" OEM is a special system category for brands not associated with a specific OEM.',
                        'This is useful for:',
                        '  • Generic or third-party brands',
                        '  • Brands temporarily without an OEM assignment',
                        '  • Brands that span multiple OEMs',
                        'Removing Brands from OEMs:',
                        '1. Navigate to Admin → OEMs.',
                        '2. Find the OEM containing the brand.',
                        '3. Expand the OEM to see its brands.',
                        '4. Click the "X" button next to the brand name.',
                        '5. Confirm the removal.',
                        '6. The brand will automatically be moved to the "Independent" OEM category.',
                        'Editing Brand Names:',
                        '1. Navigate to Admin → OEMs.',
                        '2. Find the OEM containing the brand.',
                        '3. Expand the OEM and find the brand.',
                        '4. Click the edit icon next to the brand name.',
                        '5. Modify the brand name.',
                        '6. Click "Save" to update.',
                        'Brand-Location Associations:',
                        'Brands can be associated with multiple dealership locations.',
                        'This allows you to:',
                        '  • Track which brands are available at which locations',
                        '  • Filter events and appointments by brand',
                        '  • Generate brand-specific reports',
                        '  • Organise multi-franchise locations',
                        'Brand associations with locations are managed in the Groups/Locations section (Admin → Groups).',
                        'Deleting Brands:',
                        'Brands cannot be deleted if they are associated with any locations.',
                        'To delete a brand:',
                        '1. Remove all brand associations from locations (Admin → Groups).',
                        '2. Remove the brand from its OEM (moves to Independent).',
                        '3. Then the brand can be deleted (if this functionality is available).',
                        'Best Practice: Keep brands even if unused, as they may be needed for historical data.',
                        'Using Brands Throughout the System:',
                        'Brands are used to:',
                        '  • Organise and filter events by brand',
                        '  • Filter appointments and bookings',
                        '  • Generate brand-specific reports and analytics',
                        '  • Associate vehicle models with brands in events',
                        '  • Track brand performance across locations',
                        'Best Practices:',
                        '  • Use consistent naming conventions for brands',
                        '  • Keep brand names clear and descriptive',
                        '  • Organise brands logically under their parent OEMs',
                        '  • Associate brands with all relevant locations',
                        '  • Use the Independent category for brands without a clear OEM',
                        '  • Avoid deleting brands that have historical associations',
                        'Troubleshooting:',
                        '  • Brand not appearing in location dropdown: Verify the brand exists and is not deleted',
                        '  • Cannot remove brand from OEM: Check if brand is associated with locations (remove associations first)',
                        '  • Brand in wrong OEM: Remove from current OEM, then add to correct OEM',
                        '  • Cannot delete brand: Remove all location associations first, then remove from OEM',
                        '  • Brand missing from list: Check if it was moved to Independent category'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'users',
                    title: 'User Management',
                    content: 'Creating and managing user accounts with roles and permissions',
                    instructions: [
                        'Understanding User Management:',
                        'User management allows administrators to create, edit, and manage user accounts for the ClientBox system.',
                        'Each user must have at least one role assigned, which determines what they can access and do in the system.',
                        'Users can have multiple roles for flexible access control.',
                        'Accessing User Management:',
                        'Navigate to Admin → Users to manage system users.',
                        'You will see a table showing all users with their name, email, role(s), and active status.',
                        'The table can be sorted and filtered to help you find specific users quickly.',
                        'Creating a New User:',
                        '1. Click the "Add User" button at the top of the page.',
                        '2. Fill in the user details:',
                        '   • Email address (required and must be unique) - This will be used for login',
                        '   • First name (required)',
                        '   • Last name (required)',
                        '3. Select a primary role from the dropdown:',
                        '   • Admin - Full system access',
                        '   • Setup - Dealership onboarding and configuration',
                        '   • Job List - Job management, invoices, clients, pipeline',
                        '   • Event Admin - Full event management control',
                        '   • Event Manager - Create and edit events',
                        '   • Event Viewer - View events only',
                        '4. Assign additional roles (optional):',
                        '   • Select from the "Additional Roles" multi-select dropdown',
                        '   • Users can have multiple roles for flexible access',
                        '   • Example: A user could be both "Setup" and "Event Manager"',
                        '5. Set the user\'s active status:',
                        '   • Active - User can log in and access the system',
                        '   • Inactive - User cannot log in (account is preserved)',
                        '6. Click "Save" to create the user account.',
                        '7. The system will automatically send login credentials to the user\'s email address.',
                        'Editing User Information:',
                        '1. Find the user in the list.',
                        '2. Click the edit icon next to their name.',
                        '3. Modify any of the fields:',
                        '   • Name and email (email changes may require re-authentication)',
                        '   • Primary role',
                        '   • Additional roles',
                        '   • Active status',
                        '4. Click "Save" to apply changes.',
                        'User Permissions:',
                        'User permissions are determined by their assigned roles.',
                        'Each role has specific permissions that control:',
                        '  • Which sections appear in the navigation menu',
                        '  • What actions the user can perform (create, edit, delete, view)',
                        '  • Which data the user can access',
                        'Users with multiple roles have the combined permissions of all their roles.',
                        'Deactivating Users:',
                        'Deactivate users instead of deleting them to preserve audit history and maintain data integrity.',
                        'Deactivated users:',
                        '  • Cannot log in to the system',
                        '  • Are hidden from most user lists',
                        '  • Retain all their historical data and associations',
                        '  • Can be reactivated later if needed',
                        'To deactivate: Edit the user and set their status to "Inactive".',
                        'Searching and Filtering Users:',
                        'Use the search box to quickly find users by name or email.',
                        'Filter options may be available to narrow down the list (e.g., by role, status).',
                        'Viewing User Details:',
                        'Click on a user to view their full details including:',
                        '  • All assigned roles and permissions',
                        '  • Account creation date',
                        '  • Last login information',
                        '  • Associated records (if applicable)',
                        'Best Practices:',
                        '  • Use unique email addresses for each user account',
                        '  • Assign only the roles necessary for each user\'s job function',
                        '  • Regularly review user accounts and deactivate unused accounts',
                        '  • Keep user information up to date (names, email addresses)',
                        '  • Document role assignments for audit purposes',
                        '  • Deactivate rather than delete users to preserve history',
                        '  • Use clear, professional email addresses',
                        'Troubleshooting:',
                        '  • Cannot create user: Verify you have Admin role permissions',
                        '  • Email already exists: Each email must be unique - check for existing accounts',
                        '  • User cannot log in: Check that the user account is active and has at least one role',
                        '  • User missing menu items: Verify the user has the appropriate roles assigned',
                        '  • Login credentials not received: Check email address is correct and check spam folder',
                        '  • Cannot edit user: Verify you have Admin permissions',
                        '  • User sees wrong sections: Review and update their role assignments'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'roles',
                    title: 'Roles & Permissions',
                    content: 'Understanding roles, permissions, and access control in ClientBox',
                    instructions: [
                        'Understanding Roles and Permissions:',
                        'Roles define what users can access and do within the ClientBox system.',
                        'Each role has a specific set of permissions that control access to features, sections, and actions.',
                        'Users are assigned one or more roles, and their combined permissions determine their access level.',
                        'Accessing Role Information:',
                        'Navigate to Admin → Roles to view and manage roles.',
                        'You will see a list of all available roles with their descriptions and key permissions.',
                        'Click on a role to view detailed information about its permissions and assigned users.',
                        'Standard Roles in ClientBox:',
                        'Admin Role:',
                        '  • Full access to all system features and settings',
                        '  • Can create, edit, and delete all types of records',
                        '  • Can manage users, roles, and system configuration',
                        '  • Can access all reports and analytics',
                        '  • Highest level of access - use carefully',
                        'Setup Role:',
                        '  • Access to dealership onboarding and configuration pages',
                        '  • Can manage onboarding checklists, workflows, signoff templates, and links',
                        '  • Can configure dealership-specific settings',
                        '  • Cannot access admin functions or user management',
                        'Job List Role:',
                        '  • Access to job management, invoices, clients, and pipeline',
                        '  • Can create and manage work orders',
                        '  • Can generate and manage invoices',
                        '  • Can track sales opportunities in the pipeline',
                        '  • Cannot access admin or event management functions',
                        'Event Admin Role:',
                        '  • Full control over events including creation, editing, and deletion',
                        '  • Can manage appointments and view all event reports',
                        '  • Can configure event settings, banners, and branding',
                        '  • Can access detailed analytics and visitor tracking',
                        '  • Can manage nginx configuration for custom domain routing',
                        '  • Can generate and manage SSL certificates',
                        'Event Manager Role:',
                        '  • Can create and edit events',
                        '  • Can manage appointments',
                        '  • Limited access to reports (basic metrics only)',
                        '  • Cannot delete events or access advanced analytics',
                        '  • Can manage nginx configuration for custom domain routing (needed for domain setup)',
                        '  • Can generate SSL certificates for custom domains',
                        'Event Viewer Role:',
                        '  • Can only view events and basic information',
                        '  • Can view appointments (read-only)',
                        '  • Cannot create, edit, or delete events',
                        '  • Minimal access - suitable for reporting purposes only',
                        'Understanding Permissions:',
                        'Permissions are granular and control access to specific features and actions.',
                        'Each permission determines whether a user can:',
                        '  • View a section or feature',
                        '  • Create new records',
                        '  • Edit existing records',
                        '  • Delete records',
                        '  • Export data',
                        '  • Access reports',
                        'Viewing Role Permissions:',
                        '1. Navigate to Admin → Roles.',
                        '2. Click on a role name to view its details.',
                        '3. You will see a list of all permissions assigned to that role.',
                        '4. Permissions are typically organised by feature or section.',
                        'Assigning Roles to Users:',
                        'Roles are assigned to users in the User Management section (Admin → Users).',
                        'When creating or editing a user:',
                        '1. Select a primary role (required).',
                        '2. Optionally select additional roles from the "Additional Roles" multi-select.',
                        '3. Users can have multiple roles for flexible access control.',
                        '4. The user\'s permissions will be the combination of all assigned roles.',
                        'Role-Based Navigation:',
                        'Role assignments determine what sections appear in the user\'s navigation menu.',
                        'Users only see menu items for features they have permission to access.',
                        'This keeps the interface clean and prevents confusion.',
                        'Customising Permissions:',
                        'Permissions can be customised for each role as needed, but this typically requires system configuration.',
                        'Contact your system administrator if you need to modify role permissions.',
                        'Best Practices:',
                        '  • Assign only the roles necessary for each user\'s job function (principle of least privilege)',
                        '  • Use multiple roles for users who need access to multiple areas',
                        '  • Regularly review role assignments to ensure they match current job responsibilities',
                        '  • Document role assignments for audit and compliance purposes',
                        '  • Use Event Viewer role for users who only need to view information',
                        '  • Reserve Admin role for system administrators only',
                        '  • Create custom roles if standard roles don\'t match your organisation\'s needs',
                        'Troubleshooting:',
                        '  • User cannot see a section: Verify they have a role that includes access to that section',
                        '  • User cannot perform an action: Check their role permissions - they may need a different role',
                        '  • Too many menu items: Review role assignments - user may have unnecessary roles',
                        '  • Missing permissions: Contact administrator to review and update role permissions',
                        '  • Cannot assign role: Verify you have Admin role permissions',
                        '  • Role not appearing: Check that the role exists and is active in the system'
                    ],
                    image: null,
                    video: null
                }
            ]
        },
        {
            id: 'onboarding',
            title: 'Dealership Onboarding',
            icon: Building2,
            description: 'Onboarding new dealerships',
            subsections: [
                {
                    id: 'checklist',
                    title: 'Master Checklist',
                    content: 'Using the onboarding checklist to track and complete dealership onboarding tasks',
                    instructions: [
                        'Understanding the Onboarding Checklist:',
                        'The master checklist is a comprehensive task list used to track all onboarding tasks for new dealerships.',
                        'It ensures that all necessary steps are completed during the onboarding process, from initial setup to final signoff.',
                        'The checklist is organised into categories, making it easy to find and complete related tasks.',
                        'Accessing the Checklist:',
                        'Navigate to Setup → [Select Dealership] → Checklist.',
                        'First, select the dealership group from the dropdown at the top of the Setup section.',
                        'Then click on "Checklist" in the navigation or menu.',
                        'You will see the checklist organised by categories and task types.',
                        'Checklist Structure:',
                        'The checklist is organised into:',
                        '  • Categories - Groups of related tasks (e.g., "Documentation", "System Setup", "Training")',
                        '  • Tasks - Individual items that need to be completed',
                        '  • Types - Tasks are separated into Sales and Service types',
                        'Each task belongs to a category and is either a Sales task, Service task, or both.',
                        'Completing Tasks:',
                        '1. Find the task you want to complete in the checklist.',
                        '2. Review the task description and any additional information provided.',
                        '3. Complete the required work or action for that task.',
                        '4. Fill in any required information fields based on the task\'s field type:',
                        '   • Text fields - Enter free-form text (e.g., notes, descriptions)',
                        '   • Number fields - Enter numeric values only',
                        '   • Date fields - Use the date picker to select a date',
                        '   • Dropdown fields - Select from a list of available options',
                        '   • Checkbox fields - Check or uncheck as appropriate',
                        '   • Email fields - Enter a valid email address',
                        '   • URL fields - Enter a valid web address',
                        '5. Mark the task as complete by checking the checkbox next to the task name.',
                        '6. The system automatically saves your progress.',
                        'Task Field Types Explained:',
                        'Text Fields:',
                        '  • Allow free-form text entry',
                        '  • Use for notes, descriptions, or any text-based information',
                        '  • No character limit unless specified',
                        'Number Fields:',
                        '  • Accept numeric input only (integers or decimals)',
                        '  • Use for quantities, amounts, or numeric data',
                        '  • May have minimum/maximum value constraints',
                        'Date Fields:',
                        '  • Include a date picker calendar for easy date selection',
                        '  • Click the calendar icon to open the date picker',
                        '  • Use for deadlines, completion dates, or scheduled events',
                        'Dropdown Fields:',
                        '  • Show a list of available options',
                        '  • Click the dropdown to see all options',
                        '  • Select the appropriate option',
                        '  • Options are configured by administrators',
                        'Checkbox Fields:',
                        '  • Simple check/uncheck to indicate yes/no or true/false',
                        '  • Use for binary choices or confirmations',
                        'Tracking Progress:',
                        'Progress is tracked automatically as tasks are completed.',
                        'You can see:',
                        '  • Total number of tasks',
                        '  • Number of completed tasks',
                        '  • Percentage of completion',
                        '  • Progress indicators for each category',
                        'Categories help organise related tasks together, making it easier to focus on specific areas.',
                        'Master Checklist Template:',
                        'Administrators can customise the master checklist template in Admin → Checklist.',
                        'The master template serves as the starting point for all new dealerships.',
                        'Important: Changes to the master template affect new dealerships but not existing ones.',
                        'Each dealership maintains its own checklist instance based on the master template at the time of creation.',
                        'This means:',
                        '  • New dealerships get the latest version of the master template',
                        '  • Existing dealerships keep their current checklist structure',
                        '  • You can update individual dealership checklists manually if needed',
                        'Best Practices:',
                        '  • Work through tasks systematically, category by category',
                        '  • Complete tasks in order when dependencies exist',
                        '  • Add notes or additional information when completing tasks for future reference',
                        '  • Review the checklist regularly to ensure nothing is missed',
                        '  • Mark tasks complete only when fully finished',
                        '  • Use the search function to quickly find specific tasks',
                        '  • Review completed tasks periodically to ensure accuracy',
                        'Troubleshooting:',
                        '  • Checklist not appearing: Verify the dealership is selected and you have Setup role permissions',
                        '  • Cannot mark task complete: Check if required fields are filled in',
                        '  • Task field not accepting input: Verify the field type and format requirements',
                        '  • Date picker not working: Check your browser compatibility and try refreshing the page',
                        '  • Dropdown has no options: Contact administrator to configure dropdown options',
                        '  • Changes not saving: Check your internet connection and try again',
                        '  • Missing tasks: Verify the master checklist template has been pushed to this dealership'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'dealership-onboarding-process',
                    title: 'Dealership Onboarding Process',
                    content: 'Complete two-step onboarding process for creating and pushing messages to VoiceBox',
                    instructions: [
                        '## Overview',
                        'The dealership onboarding process has been enhanced with a two-step approach that allows you to create messages locally before pushing them to VoiceBox. This eliminates errors when VoiceBox client databases don\'t exist yet.',
                        '## Two-Step Onboarding Process',
                        '### Step 1: Create Messages Locally',
                        'The first step creates all messages in the local database without requiring a VoiceBox connection:',
                        '1. Navigate to **Admin** → **Dealership Onboarding**',
                        '2. Select the dealership group you want to onboard',
                        '3. Review the onboarding preview to see what will be created',
                        '4. Click **"Create Messages Locally"** button',
                        '5. The system will:',
                        '   • Create messages for all brands at each location',
                        '   • Populate UnLayer JSON and HTML content from brand templates',
                        '   • Replace tags with location-specific data',
                        '   • Store messages with `pending_voicebox_push = true`',
                        '   • Display results showing how many messages were created',
                        '**Benefits:**',
                        '  • No VoiceBox connection required',
                        '  • Can create messages immediately after importing data',
                        '  • Messages are stored locally and ready to push when VoiceBox is ready',
                        '  • No sync errors during initial setup',
                        '### Step 2: Push to VoiceBox',
                        'The second step pushes locally created messages to VoiceBox (requires client database):',
                        '1. Ensure VoiceBox client database has been created (see VoiceBox Client Creation documentation)',
                        '2. Configure VoiceBox mapping in **Admin** → **VoiceBox Sync**',
                        '3. Return to **Admin** → **Dealership Onboarding**',
                        '4. Click **"Push to VoiceBox"** button',
                        '5. Confirm the push operation',
                        '6. The system will:',
                        '   • Validate that the VoiceBox client database exists',
                        '   • Create folders in VoiceBox for each location',
                        '   • Push all messages with UnLayer JSON and HTML content',
                        '   • Update message status to show they\'ve been pushed',
                        '   • Display results showing success/failure for each message',
                        '**Requirements:**',
                        '  • VoiceBox client database must exist',
                        '  • VoiceBox mapping must be configured',
                        '  • Messages must have been created locally first',
                        '## Onboarding Preview',
                        'Before creating messages, you can preview what will be created:',
                        '1. The preview shows:',
                        '   • Number of locations',
                        '   • Number of brands per location',
                        '   • Total messages that will be created',
                        '   • Brand templates that will be used',
                        '2. If VoiceBox mapping is missing, you\'ll see a warning (not an error)',
                        '3. You can still proceed with local message creation even without mapping',
                        '## VoiceBox Mapping Status',
                        'The onboarding page displays VoiceBox mapping status:',
                        '  • **✓ Mapping Found** (Green) - Ready to push to VoiceBox',
                        '  • **⚠ Mapping Not Found** (Yellow) - Can create locally, but need mapping to push',
                        'When mapping is not found, clear instructions are provided on how to set it up.',
                        '## Message Creation Results',
                        'After creating messages locally, you\'ll see:',
                        '  • Total messages created',
                        '  • Messages per location',
                        '  • Messages per brand',
                        '  • Any errors or warnings',
                        '  • Status showing messages are pending VoiceBox push',
                        '## Push to VoiceBox Results',
                        'After pushing to VoiceBox, you\'ll see:',
                        '  • Successfully pushed messages count',
                        '  • Failed messages (if any) with error details',
                        '  • VoiceBox folder IDs created',
                        '  • Final status of the push operation',
                        '## Best Practices',
                        '  • **Complete Step 1 first**: Always create messages locally before attempting to push',
                        '  • **Create VoiceBox client**: Set up the VoiceBox client database before pushing (see documentation)',
                        '  • **Configure mapping**: Ensure VoiceBox mapping is set up before pushing',
                        '  • **Review preview**: Check the preview to ensure correct messages will be created',
                        '  • **Test with one location**: Consider testing with a single location first',
                        '  • **Monitor results**: Review creation and push results for any issues',
                        '## Troubleshooting',
                        '**Cannot create messages locally:**',
                        '  • Verify brand templates exist for all brands',
                        '  • Check that locations and brands are properly configured',
                        '  • Ensure you have appropriate permissions',
                        '**Cannot push to VoiceBox:**',
                        '  • Verify VoiceBox client database exists (see VoiceBox Client Creation docs)',
                        '  • Check VoiceBox mapping is configured correctly',
                        '  • Test VoiceBox connection in Admin → VoiceBox Sync',
                        '  • Ensure messages were created locally first',
                        '**Messages not appearing in VoiceBox:**',
                        '  • Check push results for errors',
                        '  • Verify VoiceBox folder structure',
                        '  • Review VoiceBox sync logs',
                        '**UnLayer content missing:**',
                        '  • Ensure brand templates have UnLayer JSON configured',
                        '  • Check that tags are properly populated',
                        '  • Verify HTML content is generated'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'contact-plan',
                    title: 'Workflows',
                    content: 'Build automated communications using VoiceBox lists and message templates',
                    instructions: [
                        'Understanding Workflows:',
                        'Workflows define who to contact (VoiceBox lists + filters), when to send, and which message template to use.',
                        'They replace the old contact plan page and are the single source of truth for automated messaging.',
                        'Accessing Workflows:',
                        'Navigate to Admin → Workflows (or Setup → Workflows).',
                        'Select a client/group to sync triggers, lists, and message templates from VoiceBox.',
                        'Targeting Locations:',
                        'Use the location selector to send to one, many, or all locations within the selected group.',
                        'Building a Workflow:',
                        '1. Add a List Selector node to choose one or more VoiceBox lists.',
                        '2. Add Filter nodes to define who is included (visual, SQL, or natural language).',
                        '3. Add Delay nodes to schedule the send timing.',
                        '4. Add Message nodes to choose a template from the message suite.',
                        'Message Templates:',
                        'Message templates are generated in VoiceBox from your master templates and then synced into the catalog.',
                        'If you see no templates, generate the message suite and sync messages.',
                        'Templates:',
                        'Use Workflow Templates to quickly build brand-aligned workflows for new customers.',
                        'Best Practices:',
                        '  • Keep filters list-specific and simple where possible',
                        '  • Reuse templates to ensure consistent customer communication',
                        '  • Test workflows in a small group before enabling for all locations',
                        'Troubleshooting:',
                        '  • No lists found: Verify VoiceBox mapping and list availability',
                        '  • No messages found: Generate the message suite, then sync messages',
                        '  • Targeting wrong locations: Check the location selector before saving'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'signoff',
                    title: 'Signoff Templates',
                    content: 'Creating and managing signoff document templates for formalising dealership agreements',
                    instructions: [
                        'Understanding Signoff Templates:',
                        'Signoff templates are used to create formal signoff documents for dealerships.',
                        'These documents formalise agreements, confirmations, and approvals during the onboarding process.',
                        'Signoff documents help track who has approved what and when, providing an audit trail.',
                        'Accessing Signoff Templates:',
                        'Navigate to Setup → [Select Dealership] → Signoff.',
                        'First, select the dealership group from the dropdown.',
                        'Then click on "Signoff" in the navigation.',
                        'You will see the signoff template interface with Sales and Service tabs.',
                        'Signoff Template Structure:',
                        'Signoff templates are separated into two main types:',
                        '  • Sales Signoff - Documents related to sales operations and agreements',
                        '  • Service Signoff - Documents related to service operations and agreements',
                        'Each type can have multiple message templates configured, allowing for different signoff scenarios.',
                        'Configuring Signoff Fields:',
                        'Signoff templates include standard fields and allow you to add custom fields.',
                        'Standard fields typically include:',
                        '  • Email address - For the person signing off',
                        '  • Name/Job Title - Name and position of the signatory',
                        '  • Signed Off checkbox - Confirmation that the signoff is complete',
                        '  • Date - Date of signoff',
                        'Adding Custom Fields:',
                        '1. Click "Add Field" in the signoff template.',
                        '2. Select the field type from the dropdown:',
                        '   • Text - Single-line text input',
                        '   • Email - Email address validation',
                        '   • Checkbox - Yes/no or true/false',
                        '   • Date - Date picker',
                        '   • Dropdown - Select from predefined options',
                        '   • Textarea - Multi-line text input',
                        '   • Number - Numeric input',
                        '3. Configure field properties:',
                        '   • Field name - Internal identifier',
                        '   • Field label - Display name',
                        '   • Required/Optional - Whether the field must be filled',
                        '   • Default value - Pre-filled value (if applicable)',
                        '   • Validation rules - Format requirements (if applicable)',
                        '4. Click "Save" to add the field.',
                        'Editing Field Configurations:',
                        '1. Find the field in the signoff template.',
                        '2. Click the edit icon next to the field.',
                        '3. Modify the field properties as needed.',
                        '4. Click "Save" to apply changes.',
                        'Removing Fields:',
                        '1. Find the field you want to remove.',
                        '2. Click the delete icon next to the field.',
                        '3. Confirm the deletion when prompted.',
                        '4. The field will be removed from the template.',
                        'Note: Removing fields may affect existing signoff documents.',
                        'Field Requirements:',
                        'Fields can be marked as required or optional.',
                        'Required fields must be filled before the signoff can be completed.',
                        'Optional fields can be left blank.',
                        'Mark fields as required for critical information that must be captured.',
                        'Selecting Message Templates:',
                        'Each signoff type can include multiple message templates.',
                        'Message templates define the content and structure of the signoff document.',
                        'To select message templates:',
                        '1. In the signoff template configuration, find the "Message Templates" section.',
                        '2. Select from available message templates (configured in Admin → Settings → Message Names).',
                        '3. You can select multiple message templates for a single signoff type.',
                        '4. The selected templates will be included in the signoff document.',
                        'Message templates are configured by administrators in Admin → Settings → Message Names.',
                        'Template Application:',
                        'The signoff template is applied to all dealerships but can be customised per dealership.',
                        'This allows for:',
                        '  • Standard signoff structure across all dealerships',
                        '  • Dealership-specific customisations when needed',
                        '  • Consistency in signoff processes',
                        '  • Flexibility for unique requirements',
                        'Using Signoff Documents:',
                        'Signoff documents are used to:',
                        '  • Formalise agreements and confirmations with dealerships',
                        '  • Track approvals and authorisations',
                        '  • Maintain an audit trail of who approved what and when',
                        '  • Ensure compliance with onboarding requirements',
                        '  • Document important decisions and agreements',
                        'Best Practices:',
                        '  • Keep signoff templates clear and concise',
                        '  • Use required fields only for critical information',
                        '  • Provide clear labels and instructions for each field',
                        '  • Test signoff templates before deploying to all dealerships',
                        '  • Document the purpose of each signoff type',
                        '  • Review and update templates regularly',
                        '  • Ensure message templates are properly configured',
                        '  • Coordinate with dealerships on signoff requirements',
                        'Troubleshooting:',
                        '  • Signoff template not appearing: Verify dealership is selected and you have Setup role permissions',
                        '  • Cannot add field: Check that field type is supported and configuration is correct',
                        '  • Field not saving: Verify all required field properties are filled',
                        '  • Message templates not available: Contact administrator to configure in Admin → Settings',
                        '  • Signoff document not generating: Verify all required fields are configured and message templates are selected',
                        '  • Cannot edit field: Check your permissions and verify the field is not locked',
                        '  • Template changes not applying: Verify changes are saved and dealership has the latest template version'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'links',
                    title: 'Useful Links',
                    content: 'Managing useful links to provide quick access to important resources for dealerships',
                    instructions: [
                        'Understanding Useful Links:',
                        'Useful links provide quick access to important resources, documentation, and external tools for dealerships.',
                        'They help centralise access to training materials, policies, procedures, and other essential resources.',
                        'Links are displayed in the dealership dashboard for easy access during the onboarding process.',
                        'Accessing Useful Links:',
                        'Navigate to Setup → [Select Dealership] → Links.',
                        'First, select the dealership group from the dropdown.',
                        'Then click on "Links" in the navigation.',
                        'You will see the links management interface with Sales and Service categories.',
                        'Link Organisation:',
                        'Links are organised into two main categories:',
                        '  • Sales Links - Resources related to sales operations, training, and procedures',
                        '  • Service Links - Resources related to service operations, training, and procedures',
                        'This categorisation helps dealerships quickly find relevant resources.',
                        'Adding a New Link:',
                        '1. Click on the appropriate category tab (Sales or Service).',
                        '2. Click "Add Link" button in that category.',
                        '3. Fill in the link details:',
                        '   • Link Key - Internal identifier (used by the system, not displayed to users)',
                        '   • Link Label - Display name (what users will see)',
                        '   • URL - Full web address (e.g., https://example.com/training)',
                        '   • Required/Optional - Whether dealerships must complete/access this link',
                        '4. Click "Save" to add the link.',
                        'Link Key Guidelines:',
                        'Link keys are internal identifiers used by the system.',
                        'Best practices:',
                        '  • Use lowercase letters and underscores (e.g., "training_video_1")',
                        '  • Keep keys descriptive but concise',
                        '  • Avoid spaces and special characters',
                        '  • Use consistent naming conventions',
                        'Link Label Guidelines:',
                        'Link labels are what users see, so make them clear and descriptive.',
                        'Examples:',
                        '  • "Sales Training Video Series"',
                        '  • "Service Department Procedures Manual"',
                        '  • "Dealership Policy Documentation"',
                        '  • "Warranty Information Portal"',
                        'URL Requirements:',
                        'Enter the full URL including the protocol (http:// or https://).',
                        'Examples:',
                        '  • https://example.com/training',
                        '  • http://internal.company.com/policies',
                        '  • https://docs.example.com/onboarding-guide',
                        'Ensure URLs are accessible and up to date.',
                        'Required vs Optional Links:',
                        'Links can be marked as required or optional.',
                        'Required links:',
                        '  • Must be accessed or completed by dealerships',
                        '  • May be tracked for compliance purposes',
                        '  • Appear prominently in the dashboard',
                        'Optional links:',
                        '  • Available but not mandatory',
                        '  • Provide additional resources',
                        '  • Can be accessed as needed',
                        'Reordering Links:',
                        'Links can be reordered to control their display order.',
                        'To reorder:',
                        '1. Click and hold on a link item.',
                        '2. Drag it to the desired position.',
                        '3. Release to drop it in the new position.',
                        '4. The new order is saved automatically.',
                        'Editing Links:',
                        '1. Find the link in the list.',
                        '2. Click the edit icon next to the link.',
                        '3. Modify any of the fields (key, label, URL, required status).',
                        '4. Click "Save" to apply changes.',
                        'Deleting Links:',
                        '1. Find the link you want to remove.',
                        '2. Click the delete icon next to the link.',
                        '3. Confirm the deletion when prompted.',
                        '4. The link will be removed from the dealership\'s link list.',
                        'Link Template Management:',
                        'The link template is managed in Admin → Links and used as a starting point for all dealerships.',
                        'Administrators can:',
                        '  • Configure standard links that apply to all dealerships',
                        '  • Push template updates to selected dealerships',
                        '  • Maintain consistency across dealerships',
                        'Each dealership can add their own specific links in addition to the template links.',
                        'This allows for:',
                        '  • Standard resources available to all',
                        '  • Dealership-specific resources when needed',
                        '  • Customisation for unique requirements',
                        'Link Display:',
                        'Links appear in the dealership dashboard for easy access.',
                        'They are typically displayed as:',
                        '  • Clickable buttons or cards',
                        '  • Organised by category',
                        '  • With clear labels and descriptions',
                        '  • With indicators for required vs optional links',
                        'Best Practices:',
                        '  • Use clear, descriptive link labels',
                        '  • Keep URLs up to date and test them regularly',
                        '  • Organise links logically by category',
                        '  • Mark critical resources as required',
                        '  • Provide a mix of required and optional links',
                        '  • Test links before adding them to ensure they work',
                        '  • Document the purpose of each link',
                        '  • Review and update links periodically',
                        '  • Remove broken or outdated links promptly',
                        '  • Coordinate with dealerships on link requirements',
                        'Troubleshooting:',
                        '  • Links not appearing: Verify dealership is selected and you have Setup role permissions',
                        '  • Link not working: Check that the URL is correct and accessible',
                        '  • Cannot add link: Verify all required fields are filled and URL format is correct',
                        '  • Link not saving: Check your internet connection and try again',
                        '  • Cannot reorder links: Try refreshing the page or check browser compatibility',
                        '  • Template links not showing: Verify template has been pushed to the dealership',
                        '  • Required link not tracked: Contact administrator to verify tracking configuration'
                    ],
                    image: null,
                    video: null
                }
            ]
        },
        {
            id: 'events',
            title: 'Event Management',
            icon: Calendar,
            description: 'Managing events and appointments',
            subsections: [
                {
                    id: 'events-overview',
                    title: 'Events Overview',
                    content: 'Understanding the events system for managing customer-facing events and appointments',
                    instructions: [
                        'Understanding the Events System:',
                        'The Events system allows you to create and manage customer-facing events with booking capabilities.',
                        'Events can be used for appointment booking, test drives, consultations, demonstrations, and other customer interactions.',
                        'Each event has its own public-facing booking page where customers can view availability and book appointments.',
                        'Accessing Events:',
                        'Navigate to Event Management → Events to view all events in the system.',
                        'You will see a list of all events with their name, status, associated locations, and key details.',
                        'Events can be filtered, searched, and sorted to help you find specific events quickly.',
                        'Event Configuration Options:',
                        'Events can be extensively configured with:',
                        '  • Custom booking pages with branding and messaging',
                        '  • Availability schedules (days, times, time zones)',
                        '  • Appointment duration and buffer times',
                        '  • Branding (colours, logos, banners)',
                        '  • Custom domains or URLs',
                        '  • Sales team member assignments',
                        '  • Vehicle model and accessory options',
                        'Event Associations:',
                        'Each event can be linked to:',
                        '  • An OEM (Original Equipment Manufacturer) - For brand-specific events',
                        '  • A dealership group - For group-wide events',
                        '  • Specific locations - For location-specific events',
                        'These associations help organise events and determine which customers can access them.',
                        'Event Status:',
                        'Events can be set as:',
                        '  • Active/Inactive - Controls whether the event is available for booking',
                        '    - Active events appear in lists and can accept bookings',
                        '    - Inactive events are hidden but not deleted',
                        '  • Public/Private - Controls access to the event',
                        '    - Public events can be accessed by customers via URL',
                        '    - Private events are only accessible to authorised users',
                        'Public Event Access:',
                        'Public events can be accessed by customers via:',
                        '  • A unique URL generated by the system',
                        '  • A custom domain (if configured)',
                        'Customers can visit the event page to view details and book appointments.',
                        'Appointment Booking:',
                        'Events support appointment booking with:',
                        '  • Time slot selection',
                        '  • Availability management',
                        '  • Automatic conflict detection',
                        '  • Customer information collection',
                        '  • Vehicle and accessory selection',
                        'Visitor Tracking and Analytics:',
                        'Each event includes comprehensive tracking and analytics:',
                        '  • Visitor statistics (total visits, unique visitors)',
                        '  • Appointment conversion rates',
                        '  • Booking trends and patterns',
                        '  • UTM parameter tracking for marketing campaigns',
                        '  • Device and location information',
                        '  • VoiceBox integration data (if configured)',
                        'Best Practices:',
                        '  • Use clear, descriptive event names',
                        '  • Configure availability accurately to avoid double bookings',
                        '  • Set appropriate buffer times between appointments',
                        '  • Test the public booking page before making events public',
                        '  • Monitor analytics to optimise event performance',
                        '  • Keep event information up to date',
                        '  • Use custom branding to match your organisation\'s style',
                        'Troubleshooting:',
                        '  • Events not appearing: Check event status (must be active) and your role permissions',
                        '  • Cannot access event page: Verify event is set to public and URL is correct',
                        '  • Booking not working: Check availability settings and appointment configuration',
                        '  • Analytics not showing: Verify visitor tracking is enabled and event has received traffic'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'create-event',
                    title: 'Creating Events',
                    content: 'Step-by-step guide to creating and configuring customer-facing events',
                    instructions: [
                        '## Overview',
                        'Creating events allows you to set up customer-facing booking pages for appointments, test drives, consultations, and more. This guide walks you through the complete event creation process.',
                        '## Accessing Event Creation',
                        '1. Navigate to **Event Management** → **Events** to view all events',
                        '2. Click **"Create Event"** or **"Add Event"** button to start creating a new event',
                        '3. You will see the event creation form with multiple sections to configure',
                        '## Basic Event Information',
                        '### Event Name (Required)',
                        'Enter a clear, descriptive name for your event:',
                        'Example:',
                        '```',
                        'Good: "BMW Test Drive Event - March 2024"',
                        'Good: "Mercedes Consultation Day - Spring 2024"',
                        'Avoid: "Event 1" or "Test"',
                        '```',
                        '**Note**: This name appears in event lists and may be visible to customers on the booking page.',
                        '### Event Description (Optional)',
                        'Provide details about the event purpose and what customers can expect:',
                        'Example:',
                        '```',
                        'Description: "Join us for an exclusive test drive event featuring the latest BMW models. ',
                        'Experience our premium vehicles and speak with our expert sales team about financing options."',
                        '```',
                        '### Event Type',
                        'Select the event type from the dropdown:',
                        '  • **Appointment booking**: General appointment scheduling',
                        '  • **Test drive**: Vehicle test drive appointments',
                        '  • **Consultation**: Sales or service consultations',
                        '  • **Demonstration**: Product demonstrations',
                        '  • **Other**: Custom event types',
                        '## Event Associations',
                        '### Associated OEM (Optional)',
                        'Choose the associated OEM from the dropdown:',
                        '  • Links the event to a specific manufacturer',
                        '  • Helps filter and organise events by brand',
                        '  • Example: Select `"BMW"` for BMW-specific events',
                        '### Dealership Group (Optional)',
                        'Select the dealership group from the dropdown:',
                        '  • Associates the event with a specific dealership group',
                        '  • Locations will filter based on the selected group',
                        '  • Example: Select `"ABC Motors Group"` to show only locations in that group',
                        '### Dealership Locations',
                        'Select specific dealership locations from the multi-select:',
                        '  • Locations filter automatically based on the selected group',
                        '  • You can select multiple locations if the event spans multiple sites',
                        '  • Each location can have its own availability schedule',
                        'Example:',
                        '```',
                        'Selected Group: "ABC Motors Group"',
                        'Available Locations:',
                        '  • Downtown Location',
                        '  • North Branch',
                        '  • South Branch',
                        'Select: Downtown Location, North Branch',
                        '```',
                        '## Appointment Configuration',
                        'Configure timing settings for appointments:',
                        '### Appointment Duration',
                        'Set how long each appointment slot lasts (default: 30 minutes):',
                        '  • Common durations: **30**, **45**, **60** minutes',
                        '  • This determines the length of each booking slot',
                        'Example:',
                        '```',
                        'Duration: 45 minutes',
                        'Result: Each appointment is 45 minutes long',
                        '```',
                        '### Gap Duration',
                        'Set time buffer between consecutive appointments (default: 15 minutes):',
                        '  • Allows for preparation and cleanup time',
                        '  • Prevents back-to-back appointments',
                        'Example:',
                        '```',
                        'Gap: 15 minutes',
                        'Appointment 1: 09:00 - 09:45',
                        'Gap: 09:45 - 10:00',
                        'Appointment 2: 10:00 - 10:45',
                        '```',
                        '### Buffer Times',
                        'Configure additional time before and after appointments:',
                        '  • **Before appointments**: Additional time before each appointment starts (useful for preparation)',
                        '  • **After appointments**: Additional time after each appointment ends (useful for cleanup)',
                        '## Availability Schedule',
                        '### Start and End Times',
                        'Set when appointments are available:',
                        '  • **Start time**: First available appointment time (e.g., `09:00`)',
                        '  • **End time**: Last available appointment time (e.g., `17:00`)',
                        '  • Use **24-hour format** (HH:MM)',
                        'Example:',
                        '```',
                        'Start Time: 09:00',
                        'End Time: 17:00',
                        'Result: Appointments available from 9 AM to 5 PM',
                        '```',
                        '### Days of the Week',
                        'Select days when appointments are available:',
                        '  • Checkboxes for Monday through Sunday',
                        '  • Uncheck days when the event is not available',
                        'Example:',
                        '```',
                        'Available Days: Monday, Tuesday, Wednesday, Thursday, Friday',
                        'Unavailable: Saturday, Sunday',
                        '```',
                        '### Timezone Settings',
                        'Configure timezone settings:',
                        '  • Defaults to system timezone',
                        '  • **Important** for accurate scheduling across time zones',
                        '  • Ensures appointments are scheduled correctly for the event location',
                        '**Note**: Always set the timezone to match the event location to avoid scheduling conflicts.',
                        '## Custom Domain Configuration',
                        '### Custom Hostname (Optional)',
                        'Set a custom hostname for your event page:',
                        'Example:',
                        '```',
                        'Custom Hostname: bmw-events.example.com',
                        'Result: Event accessible at https://bmw-events.example.com',
                        '```',
                        '**Requirements**:',
                        '  • Requires DNS configuration on your domain',
                        '  • Custom hostnames must be unique across all events',
                        '  • SSL certificate must be configured for HTTPS',
                        '**Note**: If not using a custom domain, the system generates a unique URL automatically (e.g., `events.example.com/event-12345`).',
                        '## Branding Configuration',
                        'Customise the visual appearance of your event page:',
                        '### Primary Colour',
                        'Configure the main brand colour:',
                        '  • Used for buttons, headers, and accents',
                        '  • Select using colour picker or enter hex code',
                        'Example:',
                        '```',
                        'Primary Colour: #0066CC (Blue)',
                        'Used for: Main buttons, page header, primary actions',
                        '```',
                        '### Secondary Colour',
                        'Configure a complementary colour:',
                        '  • Used for highlights and secondary elements',
                        '  • Select using colour picker or enter hex code',
                        'Example:',
                        '```',
                        'Secondary Colour: #FF6600 (Orange)',
                        'Used for: Highlights, secondary buttons, accents',
                        '```',
                        '## Event Status Settings',
                        '### Active Status',
                        'Set whether the event is active:',
                        '  • **Active**: Event appears in lists and can accept bookings',
                        '  • **Inactive**: Event is hidden but not deleted (preserves data)',
                        '### Public Status',
                        'Set whether the event is public:',
                        '  • **Public**: Event is accessible via URL by customers',
                        '  • **Private**: Event is only accessible to authorised users',
                        '## Saving the Event',
                        '1. **Review all configuration settings** before saving',
                        '2. Click **"Save"** to create the event',
                        '3. The event will be created and you can now configure additional details',
                        '**Important**: Once saved, you can edit most settings, but some changes may affect existing bookings.',
                        '## Post-Creation Configuration',
                        'After creating the event, you can configure additional details:',
                        '  • **Add sales team members**: Users who can be assigned to appointments',
                        '  • **Add vehicle models**: Vehicles available for the event',
                        '  • **Add accessories**: Optional add-ons customers can select',
                        '  • **Configure banner**: Custom banner for the event page',
                        '  • **Set up visitor tracking**: Enable analytics and tracking',
                        '## Best Practices',
                        '### Event Setup',
                        '  • **Use descriptive names**: Clearly identify the event purpose',
                        '  • **Set realistic availability**: Base times on actual capacity and staff availability',
                        '  • **Configure buffer times**: Prevent scheduling conflicts and allow for preparation',
                        '  • **Test booking process**: Test the booking flow before making the event public',
                        '  • **Use consistent branding**: Maintain brand consistency across all events',
                        '  • **Set timezone correctly**: Match the event location timezone',
                        '  • **Review settings**: Double-check all settings before saving',
                        '### Maintenance',
                        '  • Regularly review and update event availability',
                        '  • Monitor booking patterns and adjust schedules as needed',
                        '  • Keep event information up to date',
                        '  • Archive or deactivate events that are no longer needed',
                        '## Troubleshooting',
                        '### Common Issues',
                        '**Problem**: Cannot create event',
                        '  • **Solution**: Verify you have **Event Admin** or **Event Manager** role permissions',
                        '  • **Solution**: Check that you have access to the Event Management section',
                        '**Problem**: Locations not appearing',
                        '  • **Solution**: Check that locations are **active** and associated with the selected group',
                        '  • **Solution**: Verify the dealership group is correctly selected',
                        '**Problem**: Time slots not working',
                        '  • **Solution**: Verify availability times are set correctly (start < end)',
                        '  • **Solution**: Check that timezone is accurate for the event location',
                        '  • **Solution**: Ensure at least one day of the week is selected',
                        '**Problem**: Custom domain not working',
                        '  • **Solution**: Check DNS configuration and domain setup',
                        '  • **Solution**: Verify SSL certificate is properly configured',
                        '  • **Solution**: Ensure custom hostname is unique',
                        '**Problem**: Event not appearing',
                        '  • **Solution**: Verify event is set to **active** status',
                        '  • **Solution**: Check event filters and search criteria',
                        '**Problem**: Cannot access booking page',
                        '  • **Solution**: Check that event is set to **public** status',
                        '  • **Solution**: Verify the event URL is correct',
                        '  • **Solution**: Check that the event is active'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'banner-config',
                    title: 'Banner Configuration',
                    content: 'Setting up custom banners for event booking pages',
                    instructions: [
                        'Understanding Event Banners:',
                        'Banners appear prominently above the booking form on the public event page.',
                        'They provide an opportunity to display important information, promotions, or branding.',
                        'Banners can include text, images, or both, with customisable styling.',
                        'Accessing Banner Configuration:',
                        'Navigate to Event Management → Events.',
                        'Select the event you want to configure.',
                        'Click on "Banner Configuration" or find it in the event settings.',
                        'You will see the banner configuration interface with preview options.',
                        'Enabling the Banner:',
                        '1. Toggle "Banner Active" to enable or disable the banner.',
                        '2. When enabled, the banner will appear on the public event page.',
                        '3. When disabled, no banner will be displayed.',
                        'Configuring Banner Text:',
                        '1. Enter banner text in the text field.',
                        '2. This text will be displayed prominently on the banner.',
                        '3. Use clear, concise messaging that captures attention.',
                        '4. Examples: "Limited Time Offer", "Book Your Test Drive Today", "Special Event - March 2024".',
                        'Adding Banner Images:',
                        '1. Optionally, enter a banner image URL.',
                        '2. For local images, place them in the public folder and use paths like:',
                        '   • "/banner.jpg"',
                        '   • "/images/banner.jpg"',
                        '   • "/images/events/bmw-banner.png"',
                        '3. For external images, use full URLs:',
                        '   • "https://example.com/images/banner.jpg"',
                        '4. Recommended banner dimensions: 1200px × 300px for optimal display.',
                        '5. Supported formats: JPG, PNG, GIF, WebP.',
                        'Colour Configuration:',
                        '1. Configure background colour using the colour picker:',
                        '   • Click the colour picker to select a colour',
                        '   • Or enter a hex code directly (e.g., #FF5733)',
                        '   • This is the background colour behind the text',
                        '2. Configure text colour using the colour picker:',
                        '   • Select a colour that contrasts well with the background',
                        '   • Ensure text is readable against the background',
                        '   • Consider accessibility guidelines for colour contrast',
                        'Font Size Configuration:',
                        '1. Adjust font size using the slider or number input.',
                        '2. Range: 12-72 pixels.',
                        '3. Recommended sizes:',
                        '   • Small banners: 24-32px',
                        '   • Medium banners: 36-48px',
                        '   • Large banners: 54-72px',
                        '4. Choose a size that is readable but not overwhelming.',
                        'Banner Preview:',
                        '1. Use the preview section to see how the banner will appear.',
                        '2. The preview updates in real-time as you make changes.',
                        '3. Test different combinations of text, images, and colours.',
                        '4. Ensure the banner looks good on both desktop and mobile devices.',
                        'Banner Display Logic:',
                        '• If both text and image are provided, the text will overlay on the image.',
                        '• Ensure text colour contrasts well with the image for readability.',
                        '• Consider using semi-transparent backgrounds or text shadows for better visibility.',
                        'Saving Banner Configuration:',
                        '1. Review your banner configuration in the preview.',
                        '2. Ensure all settings are correct.',
                        '3. Click "Save" to apply the banner to the public event page.',
                        '4. The banner will appear immediately on the event booking page.',
                        'Best Practices:',
                        '  • Keep banner text concise and impactful',
                        '  • Use high-quality images with appropriate dimensions',
                        '  • Ensure text is readable with good colour contrast',
                        '  • Test banner appearance on different screen sizes',
                        '  • Use banners to highlight important information or promotions',
                        '  • Keep banner styling consistent with your brand',
                        '  • Update banners regularly to keep content fresh',
                        '  • Consider mobile users when designing banners',
                        'Troubleshooting:',
                        '  • Banner not appearing: Verify "Banner Active" is toggled on and changes are saved',
                        '  • Image not loading: Check image URL is correct and image is accessible',
                        '  • Text not visible: Adjust text colour or background for better contrast',
                        '  • Banner too large/small: Adjust font size and image dimensions',
                        '  • Preview not updating: Refresh the page or check browser compatibility',
                        '  • Changes not saving: Verify you have Event Admin or Event Manager permissions'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'custom-domains-portal',
                    title: 'Custom Domains and Event Portal',
                    content: 'Step-by-step guide for event admins: custom domains, DNS, SSL, and multi-event portal',
                    instructions: [
                        '## Event Admin Step-by-Step Guide',
                        'This guide covers creating events, adding custom domains, DNS setup, and using a single domain for multiple events (portal).',
                        '## Step 1: Create an Event',
                        '1. Go to **Event Management** → **Events**.',
                        '2. Click **Add Event**.',
                        '3. Enter **Event Name** (required), description, event type, OEM/Group if needed.',
                        '4. Set **Appointment Duration** and **Gap Duration**.',
                        '5. Optionally set a **Custom Domain** now or later (see Step 2).',
                        '6. For a **portal** (multiple events on one domain): either type the same domain for each event, or use **Or select existing portal domain** to pick a domain already in use.',
                        '7. Click **Create**.',
                        '## Step 2: Add a Custom Domain',
                        '**Option A – When creating an event**',
                        '  • In the Add Event form, fill **Custom Domain (optional)** or choose **Or select existing portal domain** to add this event to an existing domain.',
                        '**Option B – On an existing event**',
                        '  • Go to **Event Management** → **Events** → click the event → open the **Custom Domain** section.',
                        '  • Enter the full hostname (e.g. `bookings.yourdomain.com`).',
                        '  • Or select **Or select existing portal domain** to use a domain that already has other events.',
                        '  • Save the event.',
                        '## Step 3: Domain Management Page',
                        '1. Go to **Event Management** → **Domain Management**.',
                        '2. You will see all events that have a custom domain.',
                        '3. For each domain you will see: Event name, domain, SSL status, and actions.',
                        '4. Click **Expand** (chevron) on a row to see DNS instructions and copy them for your customer.',
                        '## Step 4: DNS Setup (Customer / Domain Owner)',
                        'Your customer (who owns the domain) must add DNS records. Share the instructions from Domain Management (expand the row → **Copy instructions to send to customer**).',
                        '**Required records:**',
                        '  • **Routing CNAME**: Points the domain to the booking service (Name: subdomain or @, Value: the CNAME target shown in Domain Management).',
                        '  • **SSL validation CNAME** (if shown): Required for HTTPS. Name and Value are shown in the expanded DNS section after you run **Setup Domain**.',
                        '**Important:**',
                        '  • DNS can take up to 24–48 hours to propagate.',
                        '  • Both records are needed for HTTPS when using AWS certificates.',
                        '  • GoDaddy: For the SSL validation **Name** field, enter only the part *before* the domain (e.g. `_abc123.bookings`); do not include `.yourdomain.com`.',
                        '## Step 5: Request SSL Certificate (CloudFront / ACM)',
                        '1. In **Domain Management**, find the event and click **Setup Domain** (globe icon) to request an AWS ACM certificate and add the domain to routing.',
                        '2. Expand the row and copy the **SSL validation CNAME** record; your customer must add it in their DNS.',
                        '3. Wait for validation (often 5–30 minutes). Status will show **Pending Validation** until then.',
                        '4. When the certificate shows **Valid**, click **Apply to CloudFront** (shield icon) to enable HTTPS for that domain.',
                        '## Step 6: Sync After Changes',
                        'If you add or remove domains, or change which events use a domain:',
                        '  • In **Domain Management**, click **Sync CloudFront KVS** so the portal and single-event URLs work correctly.',
                        '## Single Event vs Portal (Multiple Events on One Domain)',
                        '**Single-event domain**',
                        '  • One event uses the domain (e.g. `bookings.dealer.com`).',
                        '  • Customers visiting that URL go straight to that event’s booking page.',
                        '**Portal domain (multi-event)**',
                        '  • Several events share the same domain (e.g. `bookings.onlinecardealership.co.uk`).',
                        '  • Customers visiting the root URL see an **event picker** (list of events).',
                        '  • Each event has its own link: `https://yourdomain.com/F4408622`, `https://yourdomain.com/AD926237`, etc.',
                        '  • To set up: give each event the *same* custom hostname, or use **Or select existing portal domain** when creating/editing the event.',
                        '## Event URLs Summary',
                        '  • **Portal root**: `https://yourdomain.com/` → event picker (if multiple events on that domain).',
                        '  • **Single event on domain**: `https://yourdomain.com/` → that event’s booking page.',
                        '  • **Portal event by key**: `https://yourdomain.com/F4408622` → that event’s booking page (no login required).',
                        '  • **Default URL**: `https://yourapp.com/events/public/F4408622` → same event, without custom domain.',
                        '## Troubleshooting',
                        '**Custom domain shows “Unauthorized” or login page**',
                        '  • Ensure you are using the *custom* domain URL (e.g. `https://bookings.yourdomain.com/F4408622`), not the main app URL. Event key links on the custom domain are public and do not require login.',
                        '**Both event links show the same event**',
                        '  • This was a previous bug; it is fixed. Ensure the app is up to date and that each event has a unique short code/event key.',
                        '**SSL stuck on “Pending Validation”**',
                        '  • Confirm the SSL validation CNAME record was added in DNS exactly as shown (Name and Value). For GoDaddy, use only the part before the domain in the Name field.',
                        '**Event picker not showing / wrong events**',
                        '  • Click **Sync CloudFront KVS** in Domain Management after changing domains or events.',
                        '**No events with custom domains**',
                        '  • Add a custom hostname in Event Configuration (Events → select event → Custom Domain section), then open Domain Management to see it and complete DNS/SSL.'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'appointments',
                    title: 'Appointments',
                    content: 'Managing customer appointments booked through event pages',
                    instructions: [
                        'Understanding Appointments:',
                        'Appointments are bookings made by customers through public event pages.',
                        'They represent scheduled interactions such as test drives, consultations, or demonstrations.',
                        'Each appointment includes customer information, selected time slot, and any chosen options.',
                        'Accessing Appointments:',
                        'Navigate to Event Management → Appointments to view all appointments in the system.',
                        'You will see a list of all appointments with key details including customer name, event, date, time, and status.',
                        'Appointments can be filtered, searched, and sorted to help you manage them effectively.',
                        'Viewing Appointment Details:',
                        'Click on an appointment to view its full details:',
                        '  • Customer information (name, email, phone)',
                        '  • Event name and location',
                        '  • Date and time',
                        '  • Status (pending, confirmed, cancelled, completed)',
                        '  • Selected vehicle models (if applicable)',
                        '  • Selected accessories (if applicable)',
                        '  • Assigned sales team member',
                        '  • Special notes or requirements',
                        '  • Booking source and tracking information',
                        'Filtering Appointments:',
                        'Use filters to find specific appointments:',
                        '  • By event - Show appointments for a specific event',
                        '  • By date range - Filter appointments within a time period',
                        '  • By status - Show only pending, confirmed, cancelled, or completed appointments',
                        '  • By location - Filter by dealership location',
                        '  • By sales team member - Show appointments assigned to specific staff',
                        'Managing Appointment Status:',
                        'Appointments can have different statuses:',
                        '  • Pending - Newly booked, awaiting confirmation',
                        '  • Confirmed - Appointment has been confirmed',
                        '  • Cancelled - Appointment has been cancelled',
                        '  • Completed - Appointment has taken place',
                        'To change status:',
                        '1. Find the appointment in the list.',
                        '2. Click on it to view details.',
                        '3. Use the status dropdown or action buttons.',
                        '4. Select the new status.',
                        '5. Save the changes.',
                        'Confirming Appointments:',
                        '1. Review the appointment details.',
                        '2. Verify customer information is correct.',
                        '3. Check that the time slot is still available.',
                        '4. Assign a sales team member if not already assigned.',
                        '5. Change status to "Confirmed".',
                        '6. Optionally, send a confirmation email to the customer.',
                        'Cancelling Appointments:',
                        '1. Find the appointment you want to cancel.',
                        '2. Click to view details.',
                        '3. Change status to "Cancelled".',
                        '4. Add a cancellation reason if required.',
                        '5. Optionally, notify the customer.',
                        '6. The time slot will become available for other bookings.',
                        'Rescheduling Appointments:',
                        '1. Find the appointment to reschedule.',
                        '2. Click to view details.',
                        '3. Click "Reschedule" or edit the date/time.',
                        '4. Select a new available time slot.',
                        '5. Verify the new time works for all parties.',
                        '6. Save the changes.',
                        '7. Optionally, notify the customer of the change.',
                        'Assigning Sales Team Members:',
                        '1. Open the appointment details.',
                        '2. Find the "Assigned To" or "Sales Team Member" field.',
                        '3. Select a team member from the dropdown.',
                        '4. The team member will be notified (if notifications are enabled).',
                        '5. Save the changes.',
                        'Viewing Appointment History:',
                        'The system tracks all changes to appointments:',
                        '  • Status changes',
                        '  • Time modifications',
                        '  • Assignment changes',
                        '  • Notes and updates',
                        'View the history to see a complete audit trail of appointment changes.',
                        'Exporting Appointment Data:',
                        '1. Use filters to select the appointments you want to export.',
                        '2. Click "Export" or "Download".',
                        '3. Choose export format (CSV, Excel, etc.).',
                        '4. The file will be generated and downloaded.',
                        'Exported data can be used for:',
                        '  • Reporting and analysis',
                        '  • Integration with other systems',
                        '  • Backup and archival',
                        'Best Practices:',
                        '  • Confirm appointments promptly after booking',
                        '  • Assign sales team members early',
                        '  • Keep customer information up to date',
                        '  • Document any special requirements or notes',
                        '  • Follow up on cancelled appointments to understand reasons',
                        '  • Regularly review and clean up old appointments',
                        '  • Export data regularly for backup purposes',
                        'Troubleshooting:',
                        '  • Appointments not appearing: Check filters and verify you have Event Admin or Event Manager permissions',
                        '  • Cannot change status: Verify your role permissions and appointment state',
                        '  • Time slot conflicts: Check availability settings and existing appointments',
                        '  • Customer not receiving notifications: Verify email address and notification settings',
                        '  • Export not working: Check your permissions and try a smaller date range',
                        '  • Cannot assign team member: Verify team members are added to the event'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'reports',
                    title: 'Event Reports',
                    content: 'Viewing comprehensive event analytics and performance reports',
                    instructions: [
                        'Understanding Event Reports:',
                        'Event reports provide detailed analytics on event performance, visitor behaviour, and booking metrics.',
                        'They help you understand how effective your events are and identify areas for improvement.',
                        'Reports include visitor statistics, conversion rates, booking trends, and marketing campaign performance.',
                        'Accessing Event Reports:',
                        'Navigate to Event Management → View Reports to access event analytics.',
                        'You will see a list of all events in the system.',
                        'Select an event from the list to view its detailed reports and analytics dashboard.',
                        'Key Metrics Overview:',
                        'Event reports show several key metrics:',
                        '  • Total Visits - Total number of page views for the event',
                        '  • Unique Visitors - Number of distinct visitors (based on visitor ID)',
                        '  • Appointment Bookings - Total number of appointments booked',
                        '  • Conversion Rate - Percentage of visitors who booked appointments',
                        '  • Average Time on Page - How long visitors spend on the event page',
                        '  • Bounce Rate - Percentage of visitors who leave without booking',
                        'Visitor Statistics:',
                        'View detailed visitor information including:',
                        '  • Visitor ID - Unique identifier for each visitor',
                        '  • Session ID - Individual visit sessions',
                        '  • IP Address - Visitor\'s IP address (for location tracking)',
                        '  • Location - Geographic location (city, country)',
                        '  • Device Type - Desktop, mobile, or tablet',
                        '  • Browser - Web browser used',
                        '  • Operating System - OS of the device',
                        '  • Referrer - Where the visitor came from',
                        'Appointment Conversion Metrics:',
                        'Track how many visitors convert to appointments:',
                        '  • Conversion Rate = (Appointments Booked / Total Visits) × 100',
                        '  • Booking trends over time',
                        '  • Peak booking times and days',
                        '  • Most popular time slots',
                        '  • Cancellation rates',
                        'Filtering Reports:',
                        'Filter reports by date range to analyse specific time periods:',
                        '1. Select a start date.',
                        '2. Select an end date.',
                        '3. Click "Apply" or "Update" to filter the data.',
                        '4. All metrics will update to show only data within the selected range.',
                        'This is useful for:',
                        '  • Analysing specific campaigns',
                        '  • Comparing performance across time periods',
                        '  • Identifying trends and patterns',
                        'UTM Parameter Tracking:',
                        'Track UTM parameters to measure marketing campaign effectiveness:',
                        '  • Campaign source (utm_source) - Where traffic came from',
                        '  • Campaign medium (utm_medium) - Marketing medium (email, social, etc.)',
                        '  • Campaign name (utm_campaign) - Specific campaign name',
                        '  • Campaign term (utm_term) - Keywords or terms',
                        '  • Campaign content (utm_content) - Specific content variant',
                        'Use UTM parameters to:',
                        '  • Identify which marketing channels drive the most traffic',
                        '  • Measure campaign ROI',
                        '  • Optimise marketing spend',
                        '  • Understand customer acquisition sources',
                        'VoiceBox Integration Data:',
                        'If VoiceBox integration is configured, reports include:',
                        '  • Email campaign performance',
                        '  • Click-through rates from VoiceBox emails',
                        '  • Appointment creation from email campaigns',
                        '  • Campaign-to-visitor mapping',
                        'This helps measure the effectiveness of email marketing campaigns.',
                        'Exporting Report Data:',
                        '1. Configure your report filters and date range.',
                        '2. Click "Export" or "Download".',
                        '3. Choose export format (CSV, Excel, PDF, etc.).',
                        '4. The report will be generated and downloaded.',
                        'Exported data can be used for:',
                        '  • Further analysis in spreadsheet applications',
                        '  • Creating custom visualisations',
                        '  • Sharing with stakeholders',
                        '  • Archival and record-keeping',
                        'Best Practices:',
                        '  • Review reports regularly to track performance',
                        '  • Compare metrics across different time periods',
                        '  • Use UTM parameters consistently in marketing campaigns',
                        '  • Monitor conversion rates and identify improvement opportunities',
                        '  • Track which marketing channels perform best',
                        '  • Use data to optimise event configuration and availability',
                        '  • Share key metrics with relevant stakeholders',
                        'Troubleshooting:',
                        '  • Reports not loading: Check your internet connection and refresh the page',
                        '  • No data showing: Verify the event has received traffic and date range is correct',
                        '  • Metrics seem incorrect: Check that visitor tracking is enabled and working',
                        '  • Cannot export: Verify you have appropriate permissions and try a smaller date range',
                        '  • UTM parameters not tracking: Verify UTM parameters are correctly formatted in campaign URLs',
                        '  • VoiceBox data missing: Check VoiceBox integration is configured and active'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'calendar-sync',
                    title: 'Calendar Integration',
                    content: 'Syncing appointments with external calendar systems',
                    instructions: [
                        'Understanding Calendar Integration:',
                        'Calendar integration allows appointments from ClientBox events to sync with external calendar systems.',
                        'This helps prevent double-booking, keeps schedules synchronised, and ensures appointments appear in your preferred calendar application.',
                        'Supported calendar systems include Google Calendar, Microsoft Outlook, Apple Calendar, and other CalDAV-compatible systems.',
                        'Accessing Calendar Sync:',
                        'Navigate to Event Management → Calendar Sync to configure calendar integrations.',
                        'You will see a list of configured calendar integrations and options to add new ones.',
                        'Connecting a Calendar Account:',
                        '1. Click "Add Calendar" or "Connect Calendar".',
                        '2. Select your calendar provider (Google, Outlook, etc.).',
                        '3. You will be redirected to authorise access to your calendar.',
                        '4. Sign in to your calendar account.',
                        '5. Grant permissions for ClientBox to access your calendar.',
                        '6. You will be redirected back to ClientBox.',
                        '7. The calendar connection will be established.',
                        'Calendar Sync Functionality:',
                        'Once connected, appointments will automatically sync:',
                        '  • New appointments created in ClientBox appear in your calendar',
                        '  • Appointment updates (time changes, cancellations) sync to your calendar',
                        '  • Calendar events can be viewed in both systems',
                        '  • Sync happens in real-time or near real-time',
                        'Configuring Sync Settings:',
                        'Configure which calendars to sync:',
                        '1. Select the calendar integration.',
                        '2. Choose which calendar(s) to sync with.',
                        '3. Set sync direction (one-way or two-way).',
                        '4. Configure sync frequency (real-time, every 5 minutes, etc.).',
                        '5. Set which event types to sync.',
                        '6. Save your settings.',
                        'Sync Direction Options:',
                        '  • One-way (ClientBox → Calendar) - Appointments sync to your calendar only',
                        '  • Two-way (ClientBox ↔ Calendar) - Changes in either system sync to the other',
                        'Two-way sync requires careful configuration to avoid conflicts.',
                        'Viewing Sync Status:',
                        'Monitor the status of your calendar integrations:',
                        '  • Connection status (connected, disconnected, error)',
                        '  • Last sync time',
                        '  • Number of appointments synced',
                        '  • Any error messages or warnings',
                        'Troubleshooting Connection Issues:',
                        'If you experience sync problems:',
                        '1. Check connection status in the Calendar Sync page.',
                        '2. Verify your calendar account credentials are correct.',
                        '3. Re-authorise access if the connection has expired.',
                        '4. Check that calendar permissions are granted.',
                        '5. Verify your calendar provider is accessible.',
                        '6. Check for error messages in the sync status.',
                        'Common Issues:',
                        '  • Sync not working: Re-authorise access or check permissions',
                        '  • Duplicate appointments: Check sync direction settings',
                        '  • Missing appointments: Verify sync is enabled and working',
                        '  • Permission errors: Re-grant calendar access permissions',
                        'Disconnecting Calendar Integration:',
                        'To disconnect a calendar:',
                        '1. Find the calendar integration in the list.',
                        '2. Click "Disconnect" or "Remove".',
                        '3. Confirm the disconnection.',
                        '4. The calendar will be disconnected and sync will stop.',
                        'Note: Disconnecting does not delete appointments, only stops future syncing.',
                        'Best Practices:',
                        '  • Use one-way sync unless you need two-way synchronisation',
                        '  • Regularly check sync status to ensure it\'s working',
                        '  • Use dedicated calendar accounts for business appointments if possible',
                        '  • Keep calendar permissions up to date',
                        '  • Test sync with a test appointment before relying on it',
                        '  • Monitor for sync conflicts or errors',
                        '  • Document which calendars are connected for team reference',
                        'Troubleshooting:',
                        '  • Cannot connect calendar: Verify calendar provider is supported and check internet connection',
                        '  • Authorisation failed: Check credentials and try re-authorising',
                        '  • Appointments not syncing: Verify sync is enabled and check sync status',
                        '  • Sync errors: Review error messages and check calendar permissions',
                        '  • Duplicate appointments: Review sync direction settings and calendar configuration',
                        '  • Cannot disconnect: Verify you have appropriate permissions'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'custom-domains-ssl',
                    title: 'Custom Domains & SSL Certificates',
                    content: 'Configuring custom domains and managing SSL certificates for events',
                    instructions: [
                        '## Overview',
                        'Custom domains allow you to create branded URLs for your events (e.g., `bookings.yourdealership.com`). The system supports automatic SSL certificate management using Let\'s Encrypt, making it easy to secure your custom domains with HTTPS.',
                        '## Custom Domain Configuration',
                        '### Setting Up a Custom Hostname',
                        '1. Navigate to **Event Management** → **Events** and select your event',
                        '2. Scroll to the **Custom Hostname / Domain** section',
                        '3. Enter your custom domain (e.g., `bookings.onlinecardealership.co.uk`)',
                        '4. The domain must be unique across all events',
                        '5. Click **Save** to save the configuration',
                        '### DNS Configuration',
                        'Before your custom domain will work, you must configure DNS:',
                        '**Step 1: Create CNAME Record**',
                        'In your DNS provider, create a CNAME record:',
                        'Example:',
                        '```',
                        'Type: CNAME',
                        'Name: bookings (or @ for root domain)',
                        'Value: your-server-domain.com',
                        'TTL: 3600 (or default)',
                        '```',
                        '**Step 2: Wait for DNS Propagation**',
                        'DNS changes can take 24-48 hours to propagate. You can verify DNS is working by:',
                        '```',
                        'nslookup bookings.yourdealership.com',
                        '```',
                        '**Common DNS Providers:**',
                        '  • **Cloudflare**: DNS → Records → Add CNAME',
                        '  • **GoDaddy**: DNS Management → Add → CNAME',
                        '  • **Namecheap**: Advanced DNS → Add New Record → CNAME',
                        '  • **AWS Route 53**: Hosted Zones → Create Record → CNAME',
                        '## SSL Certificate Management',
                        '### Automatic Certificate Generation',
                        'The system can automatically generate Let\'s Encrypt SSL certificates for your custom domains:',
                        '**Manual Generation:**',
                        '1. Configure a custom hostname for your event',
                        '2. Scroll to the **SSL Certificate (Let\'s Encrypt)** section',
                        '3. Click **"Generate Let\'s Encrypt Certificate"**',
                        '4. Wait 1-2 minutes for certificate generation',
                        '5. The status will update automatically when ready',
                        '**Automatic Generation:**',
                        '1. Configure a custom hostname for your event',
                        '2. Check **"Automatically generate certificate when saving event"**',
                        '3. Save the event',
                        '4. If DNS is configured correctly, the certificate will generate automatically',
                        '### Certificate Status',
                        'The certificate status is automatically checked when:',
                        '  • The event configuration page loads',
                        '  • A custom hostname is configured',
                        '  • Certificate generation is in progress (polls every 10 seconds)',
                        'You can also manually check status by clicking **"Check Certificate Status"**.',
                        '**Certificate Status Indicators:**',
                        '  • **✓ Certificate Installed** (Green) - Certificate is valid and active',
                        '  • **⚠️ No Certificate Found** (Yellow) - Certificate needs to be generated',
                        '  • **Expired** (Red) - Certificate has expired and needs renewal',
                        '### Certificate Renewal',
                        'Let\'s Encrypt certificates expire after 90 days. To renew:',
                        '1. Navigate to the event configuration page',
                        '2. Scroll to the SSL Certificate section',
                        '3. Click **"Renew Certificate"**',
                        '4. Wait for renewal to complete',
                        '**Note**: Certificates can also auto-renew via certbot cron job if configured on the server.',
                        '## Requirements',
                        '### Server Requirements',
                        '1. **Certbot Installation**: Certbot must be installed on the server',
                        '```',
                        'sudo apt-get install certbot python3-certbot-nginx',
                        '```',
                        '2. **Port 80 Access**: Port 80 must be accessible from the internet for Let\'s Encrypt validation',
                        '3. **DNS Configuration**: DNS CNAME record must be configured and propagated',
                        '4. **Nginx Access**: System needs permission to reload nginx',
                        '### DNS Requirements',
                        'Before generating a certificate:',
                        '  • DNS CNAME record must be configured',
                        '  • DNS must be propagated (can take up to 48 hours)',
                        '  • Domain must resolve to your server',
                        '## How It Works',
                        '### Custom Domain Routing',
                        'The system uses a sophisticated nginx-based routing system to automatically route custom domains to the correct events:',
                        '',
                        '**Request Flow:**',
                        '1. **DNS Resolution**: Customer\'s browser resolves the custom domain (e.g., `bookings.dealership.com`) to your server IP via CNAME record',
                        '2. **Nginx Receives Request**: Nginx receives the HTTP/HTTPS request with the custom hostname in the Host header',
                        '3. **Host Header Preservation**: Nginx preserves the original Host header when proxying to the application containers',
                        '4. **React App Detection**: The React application detects the custom hostname via `window.location.hostname`',
                        '5. **API Lookup**: The app calls `/api/events/by-hostname` which queries the database for an event matching the hostname',
                        '6. **Event Display**: If found, the public event page is displayed for that specific event',
                        '',
                        '**Automatic Nginx Configuration:**',
                        'The system automatically updates nginx configuration when:',
                        '  • An event is created with a `custom_hostname`',
                        '  • An event\'s `custom_hostname` is updated or changed',
                        '  • An event with a `custom_hostname` is deleted',
                        '',
                        'This ensures nginx routing is always up-to-date without manual intervention.',
                        '',
                        '**Manual Nginx Config Updates:**',
                        'You can manually trigger nginx configuration updates via the API:',
                        '  • Endpoint: `POST /api/events/nginx-config/update`',
                        '  • Requires: `event_admin` or `event_manager` role',
                        '  • Use case: After bulk event updates or if automatic updates fail',
                        '### Certificate Generation Process',
                        '1. **Validation**: System checks if certbot is installed',
                        '2. **DNS Check**: Verifies hostname format',
                        '3. **Certbot Execution**: Runs certbot with nginx plugin (preferred) or standalone mode',
                        '4. **Nginx Configuration**: Certbot automatically updates nginx configuration',
                        '5. **Nginx Reload**: System attempts to reload nginx automatically',
                        '6. **Status Update**: Certificate status is updated in the UI',
                        '## Troubleshooting',
                        '### Certificate Generation Fails',
                        '**DNS Not Configured:**',
                        '  • Ensure CNAME record is set up',
                        '  • Wait for DNS propagation (can take up to 48 hours)',
                        '  • Verify DNS resolution: `nslookup your-domain.com`',
                        '**Port 80 Not Accessible:**',
                        '  • Ensure port 80 is open in firewall',
                        '  • Check if another service is using port 80',
                        '  • Verify server is accessible from the internet',
                        '**Certbot Not Installed:**',
                        '  • Install certbot: `sudo apt-get install certbot python3-certbot-nginx`',
                        '  • Verify installation: `which certbot`',
                        '**Nginx Configuration Issues:**',
                        '  • Check nginx configuration: `nginx -t`',
                        '  • Ensure nginx is running',
                        '  • Check nginx error logs',
                        '### Custom Domain Not Working',
                        '**Event Not Found:**',
                        '  • Verify event has `custom_hostname` set correctly in database',
                        '  • Check that event is active and public',
                        '  • Verify hostname matches exactly (case-sensitive)',
                        '  • Ensure nginx configuration has been updated (check nginx logs)',
                        '',
                        '**Nginx Routing Issues:**',
                        '  • **Host Header Not Preserved**: Verify nginx config includes `proxy_set_header Host $host;`',
                        '  • **Nginx Config Not Updated**: Manually trigger nginx config update via API endpoint',
                        '  • **Nginx Not Reloaded**: Check if nginx was reloaded after config changes',
                        '  • **Check Nginx Logs**: Review `/var/log/nginx/error.log` for routing errors',
                        '  • **Test Host Header**: Use curl to test: `curl -H "Host: your-domain.com" http://your-server/`',
                        '',
                        '**DNS Issues:**',
                        '  • Verify CNAME record is correctly configured',
                        '  • Check DNS propagation: `nslookup your-domain.com`',
                        '  • Ensure CNAME points to your server\'s domain (not IP address)',
                        '  • Wait for DNS propagation (can take 24-48 hours)',
                        '**Certificate Error:**',
                        '  • Check certificate status in event configuration',
                        '  • Verify certificate is valid and not expired',
                        '  • Ensure certificate covers the exact domain name',
                        '**HTTPS Redirect Issues:**',
                        '  • Verify nginx is configured for HTTPS',
                        '  • Check that SSL certificate paths are correct',
                        '  • Ensure port 443 is accessible',
                        '### Certificate Status Not Updating',
                        '  • Wait a few minutes for ACM validation (usually 5–30 minutes after DNS is added)',
                        '  • In Domain Management, use **Check certificate** (refresh icon) to re-fetch status',
                        '  • Ensure the SSL validation CNAME record was added exactly as shown (Name and Value)',
                        '  • For AWS ACM: certificate is managed in AWS; no local certificate files',
                        '## Best Practices',
                        '### Domain Setup',
                        '  • **DNS First**: Always configure DNS before generating certificates',
                        '  • **Wait for Propagation**: Allow 24-48 hours for DNS propagation',
                        '  • **Use Descriptive Names**: Choose clear, memorable domain names',
                        '  • **Keep Domains Unique**: Ensure each event has a unique custom hostname',
                        '### Certificate Management',
                        '  • **Monitor Expiry**: Check certificate status in Domain Management regularly',
                        '  • **ACM**: AWS ACM certificates auto-renew; no manual renewal needed',
                        '  • **Test First**: Add DNS validation record and wait for ACM to validate before applying to CloudFront',
                        '  • **One cert per domain**: Multiple events on the same domain share one certificate',
                        '### Security',
                        '  • **Always Use HTTPS**: Never use HTTP for custom domains',
                        '  • **Keep Certificates Updated**: Renew certificates before they expire',
                        '  • **Monitor Certificate Status**: Regularly check certificate validity',
                        '## Nginx Configuration Management',
                        '### Automatic Configuration Updates',
                        'The system automatically manages nginx configuration:',
                        '  • **On Event Creation**: When an event is created with a custom hostname, nginx config is updated automatically',
                        '  • **On Event Update**: When a custom hostname is added, changed, or removed, nginx config updates automatically',
                        '  • **On Event Deletion**: When an event with a custom hostname is deleted, nginx config is updated to remove the routing',
                        '',
                        '### Manual Configuration Updates',
                        'To manually update nginx configuration:',
                        '1. Ensure you have `event_admin` or `event_manager` role',
                        '2. Call the API endpoint: `POST /api/events/nginx-config/update`',
                        '3. The system will regenerate nginx config based on all active events with custom hostnames',
                        '',
                        '### Configuration File Location',
                        '  • **Development**: `client/nginx.conf` (nginx runs in client container)',
                        '  • **Production**: `nginx/nginx.conf` (separate nginx service)',
                        '  • **Generated Config**: If automatic updates fail, config is saved to `nginx/nginx.conf.generated`',
                        '',
                        '### Nginx Server Blocks',
                        'The system uses two approaches for routing:',
                        '  • **Catch-All Server Block**: Accepts requests for any hostname and preserves the Host header',
                        '  • **Specific Server Blocks**: (Optional) Generates specific server blocks for each custom hostname for better logging and SSL management',
                        '',
                        '## Docker / CloudFront',
                        'When using **AWS CloudFront** (recommended):',
                        '  • SSL certificates are **AWS ACM** (no certbot or local certificate files)',
                        '  • Domain Management shows one SSL status per domain; multiple events can share a domain',
                        '  • Use **Setup domain** to request an ACM certificate, then **Apply to CloudFront** after validation',
                        'If running **nginx only** (no CloudFront):',
                        '  • Nginx config volume must be mounted; use **Update Nginx config** in Domain Management',
                        '  • Nginx reload may require container restart if volume is read-only',
                        '## Limitations',
                        '  • **Single domain per certificate**: Each ACM certificate is for one domain (no wildcards)',
                        '  • **DNS validation**: ACM requires the validation CNAME record to be added by the domain owner',
                        '  • **CloudFront**: Custom domains must be applied to CloudFront after ACM validates'
                    ],
                    image: null,
                    video: null
                }
            ]
        },
        {
            id: 'joblist',
            title: 'Job List & Invoicing',
            icon: Briefcase,
            description: 'Managing jobs and invoices',
            subsections: [
                {
                    id: 'jobs-overview',
                    title: 'Jobs Overview',
                    content: 'Understanding the job list system for managing work orders and projects',
                    instructions: [
                        'Understanding the Job List System:',
                        'The Job List system helps manage work orders, projects, and tasks throughout their lifecycle.',
                        'It provides a centralised location to track work, assign resources, monitor progress, and manage billing.',
                        'Jobs can represent various types of work including service orders, projects, installations, repairs, and more.',
                        'Accessing Jobs:',
                        'Navigate to Job List → Jobs to view all jobs in the system.',
                        'You will see a list of all jobs with key information including job title, client, status, and dates.',
                        'Jobs can be filtered, searched, and sorted to help you find and manage specific jobs.',
                        'Job Information:',
                        'Each job contains comprehensive details:',
                        '  • Job title and description',
                        '  • Client information',
                        '  • Status (New, In Progress, Completed, etc.)',
                        '  • Priority level',
                        '  • Assigned team members',
                        '  • Department or category',
                        '  • Start and end dates',
                        '  • Estimated and actual costs',
                        '  • Related invoices and documents',
                        'Filtering Jobs:',
                        'Filter jobs to find specific records:',
                        '  • By status - Show only jobs with specific status',
                        '  • By department - Filter by department or category',
                        '  • By client - Show jobs for a specific client',
                        '  • By date range - Filter jobs within a time period',
                        '  • By assigned team member - Show jobs assigned to specific staff',
                        '  • By priority - Filter by priority level',
                        'Job Relationships:',
                        'Jobs can be linked to:',
                        '  • Clients - The customer or organisation the job is for',
                        '  • Invoices - Billing documents related to the job',
                        '  • Other jobs - Related or dependent jobs',
                        '  • Documents - Files, notes, and attachments',
                        'These relationships help maintain a complete view of each job and its context.',
                        'Job Lifecycle:',
                        'Jobs progress through various stages:',
                        '  • New - Job has been created but not started',
                        '  • In Progress - Work is actively being performed',
                        '  • On Hold - Work is temporarily paused',
                        '  • Completed - Work has been finished',
                        '  • Cancelled - Job has been cancelled',
                        'Track progress and status throughout the work lifecycle to ensure timely completion.',
                        'Custom Fields:',
                        'Jobs support custom fields and additional information as needed.',
                        'This allows you to:',
                        '  • Add job-specific data fields',
                        '  • Track custom metrics',
                        '  • Store additional context',
                        '  • Adapt the system to your specific needs',
                        'Best Practices:',
                        '  • Use clear, descriptive job titles',
                        '  • Keep job status up to date',
                        '  • Assign jobs to appropriate team members',
                        '  • Link jobs to clients and invoices',
                        '  • Document progress and updates regularly',
                        '  • Use filters to organise and find jobs efficiently',
                        '  • Set realistic dates and priorities',
                        'Troubleshooting:',
                        '  • Jobs not appearing: Check filters and verify you have Job List role permissions',
                        '  • Cannot create job: Verify your role permissions and required fields are filled',
                        '  • Status not updating: Check your permissions and job state',
                        '  • Cannot link to client: Verify client exists and is active',
                        '  • Filters not working: Clear filters and try again, or refresh the page'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'create-job',
                    title: 'Creating Jobs',
                    content: 'Step-by-step guide to creating and managing jobs in the system',
                    instructions: [
                        'Understanding Job Creation:',
                        'Jobs represent work orders, projects, or tasks that need to be completed.',
                        'Creating jobs helps you track work, assign resources, monitor progress, and manage billing.',
                        'Accessing Job Creation:',
                        'Navigate to Job List → Jobs to view all jobs.',
                        'Click "Create Job" or "Add Job" button to start creating a new job.',
                        'You will see the job creation form with fields to fill in.',
                        'Entering Job Details:',
                        '1. Enter job title (required):',
                        '   • Use a clear, descriptive title (e.g., "Website Redesign - ABC Motors")',
                        '   • Make it specific enough to identify the job easily',
                        '2. Enter job description:',
                        '   • Provide details about the work to be performed',
                        '   • Include scope, requirements, and objectives',
                        '   • Add any special instructions or notes',
                        '3. Select or enter client information:',
                        '   • Choose from existing clients in the dropdown',
                        '   • Or create a new client if needed',
                        '   • Client information links the job to customer records',
                        'Selecting Department:',
                        '1. Select the department responsible for the job from the dropdown.',
                        '2. Departments help organise jobs and assign them to the right teams.',
                        '3. Examples: Sales, Service, IT, Marketing, Operations.',
                        'Setting Job Status:',
                        '1. Set the initial job status:',
                        '   • New - Job has been created but not started',
                        '   • In Progress - Work has begun',
                        '   • On Hold - Work is temporarily paused',
                        '   • Completed - Work is finished',
                        '   • Cancelled - Job has been cancelled',
                        '2. Status can be updated as work progresses.',
                        'Assigning Team Members:',
                        '1. If applicable, assign the job to team members:',
                        '   • Select team members from the dropdown',
                        '   • You can assign multiple team members',
                        '   • Assigned members will be notified (if notifications are enabled)',
                        '2. Team member assignments help track who is responsible for the work.',
                        'Adding Additional Information:',
                        '1. Add any relevant notes or additional information:',
                        '   • Use the notes field for important details',
                        '   • Document special requirements',
                        '   • Add reminders or follow-up items',
                        '2. Attach files or documents if needed.',
                        '3. Set priority level if applicable.',
                        'Linking Related Records:',
                        'Link the job to related records:',
                        '  • Invoices - Link to billing documents',
                        '  • Clients - Associate with customer records',
                        '  • Other jobs - Link to related or dependent jobs',
                        '  • Documents - Attach relevant files',
                        'These links help maintain a complete view of the job and its context.',
                        'Saving the Job:',
                        '1. Review all entered information.',
                        '2. Verify required fields are filled.',
                        '3. Click "Save" to add the job to the system.',
                        '4. The job will appear in the jobs list.',
                        'Updating Jobs:',
                        'As work progresses, update job information:',
                        '  • Change status to reflect current state',
                        '  • Update description with progress notes',
                        '  • Adjust dates and deadlines',
                        '  • Add or remove team member assignments',
                        '  • Link to new invoices or documents',
                        'Job Management Options:',
                        'Jobs can be:',
                        '  • Edited - Modify any job details',
                        '  • Duplicated - Create a copy for similar work',
                        '  • Archived - Move to archive for historical reference',
                        '  • Deleted - Remove from the system (use with caution)',
                        'Best Practices:',
                        '  • Use clear, descriptive job titles',
                        '  • Provide detailed descriptions for context',
                        '  • Assign jobs to appropriate departments and team members',
                        '  • Set realistic dates and priorities',
                        '  • Keep job status current',
                        '  • Document progress regularly',
                        '  • Link jobs to related records for complete tracking',
                        'Troubleshooting:',
                        '  • Cannot create job: Verify you have Job List role permissions',
                        '  • Client not in dropdown: Create the client first in Job List → Clients',
                        '  • Cannot assign team member: Verify team members exist and are active',
                        '  • Job not saving: Check that all required fields are filled',
                        '  • Cannot link to invoice: Verify invoice exists and is accessible'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'invoices',
                    title: 'Invoices',
                    content: 'Creating and managing invoices for billing and payment tracking',
                    instructions: [
                        'Understanding Invoices:',
                        'Invoices are billing documents used to request payment for work performed or products delivered.',
                        'They track what was provided, the cost, payment terms, and payment status.',
                        'Invoices can be linked to jobs, clients, and other related records.',
                        'Accessing Invoices:',
                        'Navigate to Job List → Invoices to view all invoices in the system.',
                        'You will see a list of all invoices with key information including invoice number, client, amount, status, and due date.',
                        'Invoices can be filtered, searched, and sorted to help you manage them effectively.',
                        'Creating a New Invoice:',
                        '1. Click "Create Invoice" button to generate a new invoice.',
                        '2. Fill in the basic invoice information:',
                        '   • Invoice number (can be auto-generated or custom)',
                        '   • Invoice date',
                        '   • Due date',
                        '   • Client information',
                        '3. Link the invoice to related records:',
                        '   • Link to a job if the invoice is for specific work',
                        '   • Link to a client for direct billing',
                        '   • Multiple links can be established',
                        'Adding Line Items:',
                        '1. Add line items to detail what is being billed:',
                        '   • Description - What the line item represents',
                        '   • Quantity - Number of units',
                        '   • Unit price - Price per unit',
                        '   • Total - Automatically calculated (quantity × unit price)',
                        '2. Click "Add Line Item" to add more items.',
                        '3. Edit or remove line items as needed.',
                        'Calculating Totals:',
                        'Totals are calculated automatically:',
                        '  • Subtotal - Sum of all line items',
                        '  • Tax/VAT - Calculated based on tax rate (if applicable)',
                        '  • Discount - Any discounts applied',
                        '  • Total - Final amount due',
                        'You can also enter totals manually if needed.',
                        'Setting Invoice Status:',
                        'Set the invoice status to track its lifecycle:',
                        '  • Draft - Invoice is being prepared',
                        '  • Sent - Invoice has been sent to the client',
                        '  • Paid - Payment has been received',
                        '  • Overdue - Payment is past due date',
                        '  • Cancelled - Invoice has been cancelled',
                        'Update status as the invoice progresses through its lifecycle.',
                        'Payment Terms and Due Dates:',
                        '1. Add payment terms:',
                        '   • Net 30, Net 60, etc.',
                        '   • Due on receipt',
                        '   • Custom terms',
                        '2. Set the due date:',
                        '   • Based on invoice date and payment terms',
                        '   • Or set a specific date',
                        '3. Payment terms help clients understand when payment is expected.',
                        'Invoice Numbers:',
                        'Invoice numbers can be:',
                        '  • Auto-generated - System creates sequential numbers',
                        '  • Custom - Enter your own invoice number',
                        'Use consistent numbering for easy tracking and reference.',
                        'Exporting Invoices:',
                        'Export invoices as PDFs for sending to clients:',
                        '1. Find the invoice you want to export.',
                        '2. Click "Export" or "Download PDF".',
                        '3. The PDF will be generated with invoice details.',
                        '4. Save or send the PDF to the client.',
                        'PDFs include:',
                        '  • Invoice number and dates',
                        '  • Client information',
                        '  • Line items and totals',
                        '  • Payment terms and instructions',
                        'Tracking Payments:',
                        'Track invoice payments and update status:',
                        '1. When payment is received, update the invoice status to "Paid".',
                        '2. Record payment details:',
                        '   • Payment date',
                        '   • Payment method',
                        '   • Payment amount',
                        '   • Reference number',
                        '3. For partial payments, track remaining balance.',
                        '4. Update status accordingly (Partially Paid, Paid, etc.).',
                        'Viewing Invoice History:',
                        'View invoice history and related transactions:',
                        '  • Status changes over time',
                        '  • Payment records',
                        '  • Related jobs and clients',
                        '  • Notes and communications',
                        'This provides a complete audit trail of the invoice.',
                        'Best Practices:',
                        '  • Use clear, descriptive line item descriptions',
                        '  • Keep invoice numbers consistent and sequential',
                        '  • Set realistic due dates based on payment terms',
                        '  • Update invoice status promptly',
                        '  • Track payments accurately',
                        '  • Export and send invoices promptly after creation',
                        '  • Follow up on overdue invoices',
                        '  • Maintain complete invoice history',
                        'Troubleshooting:',
                        '  • Cannot create invoice: Verify you have Job List role permissions',
                        '  • Totals not calculating: Check line item quantities and prices are entered correctly',
                        '  • Cannot link to job: Verify the job exists and is accessible',
                        '  • PDF not generating: Check your browser settings and try a different browser',
                        '  • Status not updating: Verify your permissions and invoice state',
                        '  • Payment not recording: Check payment fields are filled correctly'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'clients',
                    title: 'Clients',
                    content: 'Managing client information and customer records',
                    instructions: [
                        'Understanding Client Management:',
                        'Clients represent customers, organisations, or entities that you work with.',
                        'Client records centralise contact information, history, and relationships with jobs and invoices.',
                        'Maintaining accurate client information helps with communication, billing, and relationship management.',
                        'Accessing Clients:',
                        'Navigate to Job List → Clients to view all clients in the system.',
                        'You will see a list of all clients with key information including name, contact details, and related records.',
                        'Clients can be filtered, searched, and sorted to help you find specific records quickly.',
                        'Creating a New Client:',
                        '1. Click "Add Client" button to create a new client record.',
                        '2. Enter client details:',
                        '   • Name (required) - Individual or company name',
                        '   • Contact information:',
                        '     - Email address',
                        '     - Phone number(s)',
                        '     - Mobile number',
                        '   • Address:',
                        '     - Street address',
                        '     - City, county, postcode',
                        '     - Country',
                        '3. Add additional information:',
                        '   • Company details (if applicable)',
                        '   • Job title or position',
                        '   • Notes and special instructions',
                        '   • Preferred contact method',
                        '4. Click "Save" to create the client record.',
                        'Linking Clients to Jobs and Invoices:',
                        'Link clients to related records:',
                        '  • Jobs - Associate client with work orders',
                        '  • Invoices - Link to billing documents',
                        '  • Other clients - Link related contacts or companies',
                        'These links help maintain a complete view of the client relationship.',
                        'Viewing Client History:',
                        'View comprehensive client history:',
                        '  • All related jobs and their status',
                        '  • Invoice history and payment records',
                        '  • Communication history',
                        '  • Notes and interactions',
                        '  • Timeline of activities',
                        'This provides a complete picture of your relationship with the client.',
                        'Updating Client Information:',
                        'Update client information as needed:',
                        '1. Find the client in the list.',
                        '2. Click to view or edit client details.',
                        '3. Modify any information fields.',
                        '4. Save the changes.',
                        'Keep client information current for accurate communication and billing.',
                        'Archiving and Deactivating Clients:',
                        'Clients can be archived or deactivated if no longer active:',
                        '  • Archived - Moved to archive, hidden from active lists',
                        '  • Deactivated - Marked as inactive, but record is preserved',
                        'This helps:',
                        '  • Keep active client lists clean',
                        '  • Preserve historical data',
                        '  • Maintain audit trails',
                        'Searching and Filtering Clients:',
                        'Use search and filter options to find clients quickly:',
                        '  • Search by name, email, or company',
                        '  • Filter by status (active, inactive, archived)',
                        '  • Filter by related records (has jobs, has invoices)',
                        '  • Sort by name, date created, or other criteria',
                        'Best Practices:',
                        '  • Use consistent naming conventions for clients',
                        '  • Keep contact information up to date',
                        '  • Add notes for important client preferences or requirements',
                        '  • Link clients to all related jobs and invoices',
                        '  • Regularly review and update client information',
                        '  • Archive inactive clients rather than deleting them',
                        '  • Use search and filters to quickly find clients',
                        'Troubleshooting:',
                        '  • Cannot create client: Verify you have Job List role permissions',
                        '  • Client not appearing: Check filters and search terms',
                        '  • Cannot link to job: Verify the job exists and client is active',
                        '  • Duplicate clients: Search for existing clients before creating new ones',
                        '  • Information not saving: Check required fields are filled and try again'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'pipeline',
                    title: 'Pipeline',
                    content: 'Tracking sales opportunities and potential work through the sales pipeline',
                    instructions: [
                        'Understanding the Pipeline:',
                        'The Pipeline helps track sales opportunities and potential work from initial contact through to conversion.',
                        'It provides a structured way to manage leads, qualify prospects, and track progress toward closing deals.',
                        'The pipeline visualises your sales process and helps identify where opportunities are in the sales cycle.',
                        'Accessing the Pipeline:',
                        'Navigate to Job List → Pipeline to view all opportunities.',
                        'You will see a list or board view of opportunities organised by stage.',
                        'Opportunities can be filtered, searched, and sorted to help you focus on priorities.',
                        'Creating New Opportunities:',
                        '1. Click "Create Opportunity" or "Add Opportunity" button.',
                        '2. Enter opportunity details:',
                        '   • Opportunity name or title',
                        '   • Potential client or company name',
                        '   • Description of the opportunity',
                        '   • Estimated value or potential revenue',
                        '   • Expected close date',
                        '3. Set initial stage (typically "Lead" for new opportunities).',
                        '4. Assign to a team member if applicable.',
                        '5. Add any relevant notes or context.',
                        '6. Click "Save" to create the opportunity.',
                        'Tracking Opportunity Stages:',
                        'Track opportunities through various stages:',
                        '  • Lead - Initial contact or inquiry',
                        '  • Qualified - Opportunity has been qualified',
                        '  • Proposal - Proposal has been sent',
                        '  • Negotiation - Terms are being negotiated',
                        '  • Won - Opportunity converted to a sale',
                        '  • Lost - Opportunity did not convert',
                        'Update the stage as the opportunity progresses through your sales process.',
                        'Assigning Opportunities:',
                        'Assign opportunities to team members:',
                        '1. Find the opportunity in the pipeline.',
                        '2. Click to view or edit opportunity details.',
                        '3. Select a team member from the "Assigned To" dropdown.',
                        '4. The team member will be responsible for moving the opportunity forward.',
                        '5. Save the changes.',
                        'Adding Notes and Updates:',
                        'Add notes and update opportunity status as it progresses:',
                        '1. Open the opportunity details.',
                        '2. Add notes in the notes field:',
                        '   • Meeting summaries',
                        '   • Client feedback',
                        '   • Next steps',
                        '   • Important information',
                        '3. Update status or stage as appropriate.',
                        '4. Save your updates.',
                        'Regular updates help track progress and maintain context.',
                        'Converting Opportunities:',
                        'Convert opportunities to jobs or clients when ready:',
                        '1. When an opportunity is won or ready to proceed:',
                        '   • Click "Convert to Job" to create a job from the opportunity',
                        '   • Click "Convert to Client" to create a client record',
                        '   • Or do both if needed',
                        '2. The system will pre-fill information from the opportunity.',
                        '3. Review and complete the conversion.',
                        '4. The opportunity can be marked as "Won" and linked to the new records.',
                        'Viewing Pipeline Metrics:',
                        'View pipeline metrics and conversion rates:',
                        '  • Total opportunities by stage',
                        '  • Conversion rates between stages',
                        '  • Average time in each stage',
                        '  • Win/loss ratios',
                        '  • Total pipeline value',
                        '  • Forecasted revenue',
                        'These metrics help you understand pipeline health and identify bottlenecks.',
                        'Filtering and Sorting Opportunities:',
                        'Filter and sort opportunities to focus on priorities:',
                        '  • By stage - View opportunities in specific stages',
                        '  • By assigned team member - See individual pipelines',
                        '  • By value - Focus on high-value opportunities',
                        '  • By expected close date - Prioritise by timeline',
                        '  • By status - Filter active, won, or lost opportunities',
                        'Use filters to create focused views for different purposes.',
                        'Best Practices:',
                        '  • Create opportunities promptly when leads come in',
                        '  • Keep opportunity stages current',
                        '  • Add regular notes to track progress',
                        '  • Assign opportunities to appropriate team members',
                        '  • Set realistic expected close dates',
                        '  • Review pipeline regularly to identify stuck opportunities',
                        '  • Convert won opportunities to jobs/clients promptly',
                        '  • Analyse pipeline metrics to improve processes',
                        'Troubleshooting:',
                        '  • Cannot create opportunity: Verify you have Job List role permissions',
                        '  • Opportunities not appearing: Check filters and verify opportunities exist',
                        '  • Cannot change stage: Verify your permissions and opportunity state',
                        '  • Conversion not working: Verify required information is filled and try again',
                        '  • Metrics not updating: Check that opportunities are properly staged and saved'
                    ],
                    image: null,
                    video: null
                }
            ]
        },
        {
            id: 'data',
            title: 'Data Management',
            icon: Database,
            description: 'Importing and exporting data',
            subsections: [
                {
                    id: 'data-import',
                    title: 'Data Import',
                    content: 'Importing data from external sources into ClientBox',
                    instructions: [
                        'Understanding Data Import:',
                        'Data import allows you to bulk import data from external sources into ClientBox.',
                        'This is useful for initial system setup, migrating data from other systems, or bulk updates.',
                        'The system supports importing various data types including users, groups, locations, events, and more.',
                        'Accessing Data Import:',
                        'Navigate to Admin → Data Imports to access import functionality.',
                        'You will see the data import interface with options to select import type and upload files.',
                        'Selecting Import Type:',
                        '1. Select the type of data you want to import from the dropdown:',
                        '   • Master Checklist - Onboarding checklist items',
                        '   • Contact Messages - Contact plan messages',
                        '   • Dealer Locations - Dealership locations (consolidated import)',
                        '   • Addresses - Address records',
                        '   • Admin Links - Useful links',
                        '   • Dropdown Options - System dropdown values',
                        '   • And more...',
                        '2. Each import type has specific requirements and field mappings.',
                        'Preparing Your Data File:',
                        '1. Prepare your data file in the required format:',
                        '   • CSV (Comma-Separated Values) - .csv files',
                        '   • Excel - .xlsx or .xls files',
                        '   • JSON - .json files (for some import types)',
                        '2. Ensure your data matches the required structure:',
                        '   • Required fields must be present',
                        '   • Data types must be correct (text, numbers, dates)',
                        '   • Format dates consistently (e.g., YYYY-MM-DD)',
                        '   • Remove any formatting or special characters that might cause issues',
                        '3. Review the data import guide for specific field requirements.',
                        'Uploading the File:',
                        '1. Click "Choose File" or "Browse" to select your data file.',
                        '2. Select the file from your computer.',
                        '3. The file will be uploaded and processed.',
                        '4. The system will automatically detect data types for each column.',
                        'Mapping File Columns:',
                        '1. Review the automatically detected data types.',
                        '2. Adjust data type detection if needed.',
                        '3. Map your file columns to system fields:',
                        '   • Required fields must be mapped',
                        '   • Optional fields can be mapped or skipped',
                        '   • Use the dropdown to select the corresponding system field',
                        '4. Verify that all required fields are mapped correctly.',
                        'Reviewing Import Preview:',
                        '1. Review the import preview table to verify data mapping.',
                        '2. Check that data looks correct:',
                        '   • Values are in the right format',
                        '   • Required fields have values',
                        '   • Data types are correct',
                        '3. Look for any warnings or errors in the preview.',
                        '4. Make adjustments to mapping if needed.',
                        'Executing the Import:',
                        '1. Once you\'re satisfied with the preview, click "Import Data".',
                        '2. The system will process the import.',
                        '3. You will see progress indicators during the import.',
                        '4. The import may take some time for large files.',
                        'Reviewing Import Results:',
                        'After the import completes, review the results:',
                        '  • Number of records successfully imported',
                        '  • Number of records that failed',
                        '  • Details of any errors or warnings',
                        '  • List of records that were skipped',
                        'Handle any errors by:',
                        '  • Correcting the data in your source file',
                        '  • Fixing mapping issues',
                        '  • Re-importing failed records',
                        'Consolidated Dealer Locations Import:',
                        'For dealer locations import, the system automatically handles:',
                        '  • Creating or updating groups',
                        '  • Creating or updating locations',
                        '  • Creating or updating addresses',
                        '  • Associating brands with locations',
                        'Existing records with matching names will be updated rather than duplicated.',
                        'Best Practices:',
                        '  • Always keep backup copies of original files before importing',
                        '  • Test imports with small sample files first',
                        '  • Verify data format and structure before importing',
                        '  • Review import preview carefully before executing',
                        '  • Document your import process for future reference',
                        '  • Import during low-usage periods for large imports',
                        '  • Verify imported data in the relevant sections after import',
                        'Troubleshooting:',
                        '  • Import failing: Check file format, required fields, and data types',
                        '  • Mapping errors: Verify column names and system field requirements',
                        '  • Data validation errors: Review error messages and fix data format issues',
                        '  • Import taking too long: Large files may take time; be patient or split into smaller files',
                        '  • Duplicate records: Check for existing records and use update mode if available',
                        '  • Cannot access import: Verify you have Admin role permissions'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'data-export',
                    title: 'Data Export',
                    content: 'Exporting system data for reporting, analysis, and backup purposes',
                    instructions: [
                        'Understanding Data Export:',
                        'Data export allows you to extract data from ClientBox for reporting, analysis, backup, or integration with other systems.',
                        'You can export specific data types with custom filters and field selections.',
                        'Accessing Data Export:',
                        'Navigate to Admin → Data Exports to export system data.',
                        'You will see the export interface with options to select data type, format, and filters.',
                        'Selecting Data Type:',
                        '1. Select the type of data you want to export from the dropdown:',
                        '   • Users - User accounts and information',
                        '   • Groups - Dealership groups',
                        '   • Locations - Dealership locations',
                        '   • Events - Event records',
                        '   • Appointments - Appointment bookings',
                        '   • Jobs - Job records',
                        '   • Invoices - Invoice records',
                        '   • Clients - Client records',
                        '   • And more...',
                        '2. Each data type has specific fields available for export.',
                        'Choosing Export Format:',
                        'Choose the export format that best suits your needs:',
                        '  • CSV (Comma-Separated Values) - Universal format, works with Excel and most applications',
                        '  • Excel (.xlsx) - Native Excel format with formatting support',
                        '  • JSON - Structured data format for integrations',
                        '  • PDF - Formatted reports (for some data types)',
                        'Apply Filters:',
                        'Apply filters to export specific subsets of data:',
                        '1. Use filter options to narrow down the data:',
                        '   • Date range filters',
                        '   • Status filters',
                        '   • Location or group filters',
                        '   • Custom field filters',
                        '2. Filters help you export only the data you need.',
                        '3. This reduces file size and processing time.',
                        'Selecting Fields:',
                        'Select which fields to include in the export:',
                        '1. Review available fields for the selected data type.',
                        '2. Check or uncheck fields to include or exclude them.',
                        '3. Include only fields you need to keep exports focused.',
                        '4. Required fields are typically included by default.',
                        'Generating the Export:',
                        '1. Review your selections (data type, format, filters, fields).',
                        '2. Click "Export" to generate the export file.',
                        '3. The system will process the export.',
                        '4. Large exports may take some time to process.',
                        '5. You will see a progress indicator.',
                        'Downloading the File:',
                        '1. Once the export is complete, a download link will appear.',
                        '2. Click the download link to save the file to your computer.',
                        '3. The file will be saved in your browser\'s default download location.',
                        '4. You can rename or move the file as needed.',
                        'Using Exported Data:',
                        'Exported data can be used for:',
                        '  • Reporting - Create custom reports in Excel or other tools',
                        '  • Analysis - Analyse data in spreadsheet applications',
                        '  • Backup - Keep copies of important data',
                        '  • Integration - Import into other systems',
                        '  • Archival - Long-term data storage',
                        '  • Compliance - Meet data retention requirements',
                        'Scheduling Regular Exports:',
                        'Schedule regular exports if needed for reporting workflows:',
                        '  • Set up automated exports for routine reporting',
                        '  • Export data on a schedule (daily, weekly, monthly)',
                        '  • Send exports automatically to specified recipients',
                        '  • Maintain regular data backups',
                        'Note: Scheduling may require additional configuration or workflow setup.',
                        'Best Practices:',
                        '  • Export only the data you need using filters',
                        '  • Select appropriate fields to keep files manageable',
                        '  • Use CSV for maximum compatibility',
                        '  • Use Excel format if you need formatting',
                        '  • Schedule regular exports for important data',
                        '  • Verify exported data after download',
                        '  • Store exports securely, especially if they contain sensitive information',
                        '  • Document your export processes for future reference',
                        'Troubleshooting:',
                        '  • Export not generating: Check your permissions and try a smaller date range',
                        '  • Export taking too long: Use filters to reduce data volume or try exporting during off-peak hours',
                        '  • File not downloading: Check browser settings and download permissions',
                        '  • Missing fields: Verify field selections and check if fields are available for the data type',
                        '  • Data format issues: Verify export format is compatible with your intended use',
                        '  • Cannot access export: Verify you have Admin role permissions'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'database-config',
                    title: 'Database Configuration',
                    content: 'Configuring external database connections for integrations and data operations',
                    instructions: [
                        'Understanding Database Configuration:',
                        'Database configuration allows ClientBox to connect to external databases for data imports, exports, and integrations.',
                        'This enables integration with other systems, data synchronisation, and advanced data operations.',
                        'Accessing Database Configuration:',
                        'Navigate to Admin → Database Config to manage database connections.',
                        'You will see a list of configured database connections and options to add new ones.',
                        'Adding a New Database Configuration:',
                        '1. Click "Add Configuration" or "New Connection" button.',
                        '2. Fill in the connection details:',
                        '   • Configuration name - Descriptive name for this connection',
                        '   • Database type - Select from supported types',
                        '   • Host - Database server address or IP',
                        '   • Port - Database port number',
                        '   • Database name - Name of the database',
                        '   • Username - Database user account',
                        '   • Password - Database password (encrypted when saved)',
                        '3. Additional settings (depending on database type):',
                        '   • Server instance (SQL Server)',
                        '   • SSL/TLS settings',
                        '   • Connection timeout',
                        '   • Trust server certificate (SQL Server)',
                        '4. Click "Test Connection" to verify the connection works.',
                        '5. If the test is successful, click "Save" to store the configuration.',
                        'Supported Database Types:',
                        'Different database types are supported:',
                        '  • PostgreSQL - Open-source relational database',
                        '  • SQL Server - Microsoft SQL Server',
                        '  • MySQL - Popular open-source database',
                        '  • Oracle - Enterprise database system',
                        '  • Other - Additional types may be supported',
                        'Testing Connections:',
                        'Always test connections before saving:',
                        '1. Fill in all connection details.',
                        '2. Click "Test Connection" button.',
                        '3. The system will attempt to connect to the database.',
                        '4. You will see a success or error message.',
                        '5. If successful, proceed to save the configuration.',
                        '6. If it fails, check your connection details and try again.',
                        'Connection Security:',
                        'Connection details are encrypted for security:',
                        '  • Passwords are never displayed in plain text',
                        '  • Credentials are stored securely',
                        '  • Connections use encrypted protocols when available',
                        '  • Access is restricted to administrators only',
                        'Using Database Configurations:',
                        'Database configurations are used for:',
                        '  • Data imports - Importing data from external databases',
                        '  • Data exports - Exporting data to external databases',
                        '  • Integrations - Connecting with other systems',
                        '  • VoiceBox integration - Connecting to VoiceBox database',
                        '  • Data synchronisation - Keeping data in sync between systems',
                        'Managing Configurations:',
                        'Update or remove configurations as needed:',
                        '1. Find the configuration in the list.',
                        '2. Click edit to modify connection details.',
                        '3. Click delete to remove a configuration (confirm when prompted).',
                        '4. Test connections after making changes.',
                        'Best Practices:',
                        '  • Use descriptive names for configurations',
                        '  • Always test connections before saving',
                        '  • Keep connection details secure and up to date',
                        '  • Use dedicated database accounts with limited permissions',
                        '  • Document what each configuration is used for',
                        '  • Remove unused configurations',
                        '  • Regularly review and update connection settings',
                        '  • Use strong passwords for database accounts',
                        'Troubleshooting:',
                        '  • Connection test failing: Verify host, port, and database name are correct',
                        '  • Authentication errors: Check username and password are correct',
                        '  • Cannot connect: Verify database server is accessible and firewall allows connections',
                        '  • SSL errors: Check SSL/TLS settings and certificate configuration',
                        '  • Timeout errors: Increase connection timeout or check network connectivity',
                        '  • Cannot save configuration: Verify you have Admin permissions and all required fields are filled',
                        '  • Configuration not appearing: Refresh the page or check if it was saved successfully'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'table-browser',
                    title: 'Table Browser',
                    content: 'Browsing database table structures and data to understand schemas',
                    instructions: [
                        'Understanding the Table Browser:',
                        'The Table Browser allows you to view database table structures and sample data from external databases.',
                        'It helps you understand database schemas, identify fields, and plan data imports or integrations.',
                        'This is a read-only tool that does not modify any data.',
                        'Accessing the Table Browser:',
                        'Navigate to Admin → Table Browser to access this feature.',
                        'You will see the table browser interface with options to select a database and browse tables.',
                        'Selecting a Database:',
                        '1. Select a database configuration from the dropdown.',
                        '2. The database configuration must be set up in Admin → Database Config first.',
                        '3. The system will connect to the selected database.',
                        '4. You will see a list of available tables in that database.',
                        'Viewing Available Tables:',
                        '1. After selecting a database, view the list of available tables.',
                        '2. Tables are typically organised by schema or database.',
                        '3. Click on a table name to view its structure and data.',
                        '4. Use search or filter to find specific tables.',
                        'Browsing Table Structures:',
                        'When you select a table, view its structure:',
                        '  • Column names - Field names in the table',
                        '  • Data types - Type of data stored (text, number, date, etc.)',
                        '  • Constraints - Primary keys, foreign keys, unique constraints',
                        '  • Nullable - Whether columns allow null values',
                        '  • Default values - Default values for columns',
                        '  • Column descriptions - Additional information about columns',
                        'This information helps you understand the table structure.',
                        'Viewing Sample Data:',
                        'View sample data from tables:',
                        '1. After selecting a table, sample data will be displayed.',
                        '2. Data is limited to a reasonable number of rows for performance.',
                        '3. You can see actual data values to understand the content.',
                        '4. Use this to verify data format and identify field mappings.',
                        'Use Cases:',
                        'The table browser is useful for:',
                        '  • Planning data imports - Understanding source data structure',
                        '  • Understanding data structures - Learning about external databases',
                        '  • Field mapping - Identifying which fields to map during imports',
                        '  • Data validation - Verifying data format and content',
                        '  • Integration planning - Understanding how to connect systems',
                        'Safety and Security:',
                        'Important considerations:',
                        '  • This is a read-only tool - no data is modified',
                        '  • Be cautious when browsing production databases',
                        '  • Only administrators should use the table browser',
                        '  • Large tables may take time to load',
                        '  • Sample data is limited for performance',
                        '  • Connection credentials must be properly configured',
                        'Best Practices:',
                        '  • Use the table browser to plan imports before executing them',
                        '  • Document table structures for future reference',
                        '  • Verify field names and data types before mapping',
                        '  • Be mindful of performance when browsing large databases',
                        '  • Use appropriate database configurations for each environment',
                        '  • Keep database connection details secure',
                        'Troubleshooting:',
                        '  • Cannot connect to database: Verify database configuration is correct and test connection',
                        '  • Tables not appearing: Check database permissions and verify connection is working',
                        '  • Table structure not loading: Check database accessibility and try refreshing',
                        '  • Sample data not showing: Verify table has data and check row limit settings',
                        '  • Cannot access table browser: Verify you have Admin role permissions',
                        '  • Slow performance: Large databases may be slow; be patient or use filters if available'
                    ],
                    image: null,
                    video: null
                }
            ]
        },
        {
            id: 'workflows',
            title: 'Workflows & Automation',
            icon: Wand2,
            description: 'Creating automated workflows',
            subsections: [
                {
                    id: 'workflow-overview',
                    title: 'Workflows Overview',
                    content: 'Understanding automated workflows for streamlining business processes',
                    instructions: [
                        'Understanding Workflows:',
                        'Workflows automate business processes and communications, reducing manual work and ensuring consistency.',
                        'They can trigger actions based on events, schedules, or conditions, making your operations more efficient.',
                        'Workflows help streamline repetitive tasks, automate communications, and ensure processes are followed consistently.',
                        'Accessing Workflows:',
                        'Navigate to Admin → Workflows to view all workflows in the system.',
                        'You will see a list of all workflows with their name, type, status, and target locations.',
                        'Workflows can be filtered, searched, and sorted to help you manage them effectively.',
                        'Workflow Types:',
                        'Common workflow types include:',
                        '  • Email Automation - Automated email sending based on triggers',
                        '  • Data Processing - Automated data manipulation and updates',
                        '  • Notification Workflows - Sending alerts and notifications',
                        '  • Integration Workflows - Connecting with external systems',
                        '  • Reporting Workflows - Automated report generation and distribution',
                        'Workflow Triggers:',
                        'Workflows can be triggered by several types of trigger nodes:',
                        '  • **Event Triggers** - Trigger on specific VoiceBox events (field changes, status updates, etc.)',
                        '  • **Manual Start Triggers** - Execute workflows on-demand by users or administrators',
                        '  • **Schedule Triggers** - Time-based triggers using cron expressions (daily, weekly, monthly, custom)',
                        '  • **Webhook Triggers** - HTTP webhook endpoints for Lambda integration and external systems',
                        '  • **Condition-Based** - When certain criteria are met (via Filter or Condition nodes)',
                        '### Trigger Node Types Explained',
                        '#### Event Triggers',
                        'Event triggers respond to specific actions in VoiceBox:',
                        '  • Field changes in contact lists',
                        '  • Status updates',
                        '  • Date-based events',
                        '  • Business events',
                        'These are synced from VoiceBox and appear in the trigger catalog.',
                        '#### Manual Start Triggers',
                        'Manual triggers allow on-demand workflow execution:',
                        '  • Click "Execute Workflow" to run immediately',
                        '  • No automatic triggering',
                        '  • Useful for one-off campaigns or testing',
                        '  • Can optionally start from a specific VoiceBox list',
                        '#### Schedule Triggers',
                        'Schedule triggers use cron expressions for automatic execution:',
                        '  • Format: `minute hour day month weekday`',
                        '  • Example: `0 9 * * *` = Daily at 9:00 AM',
                        '  • Example: `0 0 1 * *` = First day of every month at midnight',
                        '  • Can include human-readable descriptions',
                        '  • Can optionally start from a specific VoiceBox list',
                        '**Note:** Schedule triggers store the cron expression but require an external scheduler (cron job or task scheduler) to actually execute them.',
                        '#### Webhook Triggers',
                        'Webhook triggers provide HTTP endpoints for external systems:',
                        '  • Each webhook has a unique token',
                        '  • Webhook URL format: `/api/workflows/webhook/{token}`',
                        '  • Can receive parameters in the request body',
                        '  • Parameters can be used in workflow SQL (e.g., `{{paramName}}`)',
                        '  • Designed for VoiceBox Lambda function integration',
                        '  • Public endpoint (no authentication required)',
                        '**Webhook Configuration:**',
                        '1. Add a Webhook Trigger node to your workflow',
                        '2. Enter a unique webhook token (e.g., `my-workflow-token`)',
                        '3. The system generates the full webhook URL',
                        '4. Copy the URL for use in Lambda functions or external systems',
                        '5. Parameters can be passed in the request body:',
                        '```',
                        '{',
                        '  "parameters": {',
                        '    "fieldName": "value",',
                        '    "anotherField": "anotherValue"',
                        '  }',
                        '}',
                        '```',
                        '**Security Note:** Webhook endpoints are currently public. Consider adding authentication for production use.',
                        'Workflow Targets:',
                        'Workflows can target:',
                        '  • All Locations - Applies to all dealerships',
                        '  • Specific Groups - Targets selected dealership groups',
                        '  • Specific Locations - Targets individual locations',
                        'This allows you to customise workflows for different dealerships or groups.',
                        'Workflow Status:',
                        'Each workflow can be:',
                        '  • Active - Workflow is running and will execute when triggered',
                        '  • Inactive - Workflow is paused and will not execute',
                        '  • Draft - Workflow is being created or edited',
                        'Activate workflows when ready, and deactivate them when they\'re no longer needed.',
                        'Workflow Execution:',
                        'View workflow execution history and results:',
                        '  • Execution logs showing when workflows ran',
                        '  • Success and failure counts',
                        '  • Error messages and warnings',
                        '  • Performance metrics',
                        'This helps you monitor workflow performance and troubleshoot issues.',
                        'Workflow Templates:',
                        'Templates can be used to create workflows quickly:',
                        '  • Pre-built workflow structures',
                        '  • Proven workflow patterns',
                        '  • Customisable templates',
                        '  • Reusable across different contexts',
                        'Use templates to speed up workflow creation and ensure best practices.',
                        'Best Practices:',
                        '  • Start with simple workflows and gradually add complexity',
                        '  • Test workflows thoroughly before activating',
                        '  • Document workflow purpose and configuration',
                        '  • Monitor workflow execution regularly',
                        '  • Use templates for common workflow patterns',
                        '  • Keep workflows active only when needed',
                        '  • Review and update workflows as processes evolve',
                        'Troubleshooting:',
                        '  • Workflows not executing: Check workflow status is active and triggers are configured',
                        '  • Execution errors: Review execution logs and error messages',
                        '  • Cannot create workflow: Verify you have Admin or appropriate permissions',
                        '  • Workflow not targeting correct locations: Review target settings',
                        '  • Performance issues: Check workflow complexity and execution frequency'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'create-workflow',
                    title: 'Creating Workflows',
                    content: 'Building custom automated workflows to streamline business processes',
                    instructions: [
                        '## Overview',
                        'Creating workflows allows you to automate business processes, communications, and data operations. Workflows can be simple or complex, depending on your needs, and help streamline repetitive tasks while ensuring consistency.',
                        '## Accessing Workflow Builder',
                        '1. Navigate to **Admin** → **Workflow Builder**',
                        '2. You will see the workflow builder interface with tools to design and configure workflows',
                        '3. The interface provides a visual canvas for building workflow logic',
                        '## Starting a New Workflow',
                        '### Step 1: Create New Workflow',
                        '1. Click the **"Create Workflow"** button to start building',
                        '2. You will see a blank workflow canvas or form',
                        '3. The workflow builder will open with default settings',
                        '### Step 2: Enter Basic Information',
                        '#### Workflow Name',
                        'Enter a clear, descriptive name for your workflow:',
                        'Example:',
                        '```',
                        'Good: "Welcome Email for New Dealerships"',
                        'Good: "Monthly Sales Report Automation"',
                        'Avoid: "Workflow 1" or "Test"',
                        '```',
                        '#### Workflow Description',
                        'Explain what the workflow does and document its purpose:',
                        'Example:',
                        '```',
                        'Description: "Sends a welcome email to new dealerships when they are onboarded. ',
                        'Includes dealership name, contact information, and next steps."',
                        '```',
                        '## Selecting Workflow Type',
                        'Select the appropriate workflow type from the dropdown. The type determines available options and actions:',
                        '### Available Types',
                        '  • **Email**: Automated email sending with personalisation',
                        '  • **Data Processing**: Data manipulation and updates',
                        '  • **Notification**: Alerts and notifications',
                        '  • **Integration**: Connecting with external systems',
                        '  • **Reporting**: Automated report generation and distribution',
                        '**Note**: Once selected, the workflow type determines which actions and options are available in the builder.',
                        '## Defining Workflow Triggers',
                        'Define workflow triggers (events that start the workflow execution) by adding trigger nodes:',
                        '### Adding Trigger Nodes',
                        '1. Click **"Add Node"** in the workflow builder',
                        '2. Select from available trigger types:',
                        '   • **Trigger (Event)** - VoiceBox event-based triggers',
                        '   • **Manual Start** - On-demand execution',
                        '   • **Schedule** - Cron-based scheduling',
                        '   • **Webhook** - HTTP endpoint for Lambda/external systems',
                        '3. Configure the trigger node settings',
                        '4. Connect the trigger to other workflow nodes',
                        '### Trigger Types',
                        '#### 1. Event-Based Triggers',
                        'Triggers on specific VoiceBox events (synced from VoiceBox):',
                        '  • Field changes in contact lists',
                        '  • Status updates',
                        '  • Date-based events',
                        '  • Business events',
                        '**Configuration:**',
                        '1. Add a "Trigger (Event)" node',
                        '2. Select a trigger from the catalog (synced from VoiceBox)',
                        '3. Optionally filter by list if lists are selected upstream',
                        '4. The trigger will fire when the event occurs in VoiceBox',
                        'Example:',
                        '```',
                        'Trigger: "Status Change - New Lead"',
                        'When: Contact status changes to "New Lead" in VoiceBox',
                        'Action: Start welcome email workflow',
                        '```',
                        '#### 2. Schedule-Based Triggers',
                        'Runs on a schedule using cron expressions:',
                        '  • Daily at 9:00 AM: `0 9 * * *`',
                        '  • Weekly on Mondays: `0 9 * * 1`',
                        '  • Monthly on the 1st: `0 0 1 * *`',
                        '  • Custom schedules supported',
                        '**Configuration:**',
                        '1. Add a "Schedule" trigger node',
                        '2. Enter cron expression (format: `minute hour day month weekday`)',
                        '3. Add a human-readable description (e.g., "Daily at 9:00 AM")',
                        '4. Optionally select a starting list',
                        '**Note:** Requires external scheduler (cron job or task scheduler) to execute.',
                        'Example:',
                        '```',
                        'Cron: "0 9 * * *"',
                        'Description: "Daily at 9:00 AM"',
                        'Action: Generate and send daily reports',
                        '```',
                        '#### 3. Manual Start Triggers',
                        'Triggered on demand by users:',
                        '  • User clicks "Execute Workflow" button',
                        '  • Admin initiates workflow execution',
                        '  • No automatic triggering',
                        '**Configuration:**',
                        '1. Add a "Manual Start" trigger node',
                        '2. Optionally add a description',
                        '3. Optionally select a starting list',
                        '4. Workflow can be executed manually from the workflows list',
                        '**Use Cases:**',
                        '  • One-off campaigns',
                        '  • Testing workflows',
                        '  • On-demand reporting',
                        '#### 4. Webhook Triggers',
                        'HTTP webhook endpoints for Lambda and external systems:',
                        '  • Unique token-based URLs',
                        '  • Parameter support from request body',
                        '  • Designed for VoiceBox Lambda integration',
                        '**Configuration:**',
                        '1. Add a "Webhook" trigger node',
                        '2. Enter a unique webhook token (e.g., `my-workflow-token`)',
                        '3. System generates full URL: `/api/workflows/webhook/{token}`',
                        '4. Copy URL for use in Lambda functions',
                        '5. Parameters can be passed in request body and used in SQL',
                        '**Example Request:**',
                        '```',
                        'POST /api/workflows/webhook/my-workflow-token',
                        'Content-Type: application/json',
                        '',
                        '{',
                        '  "parameters": {',
                        '    "campaignId": "12345",',
                        '    "listId": "409"',
                        '  }',
                        '}',
                        '```',
                        '**Using Parameters in SQL:**',
                        'Parameters can be referenced in workflow SQL using `{{paramName}}` or `:paramName`:',
                        '```',
                        'WHERE CampaignId = {{campaignId}}',
                        '```',
                        '## Adding Workflow Steps',
                        'Add workflow steps and actions to define what the workflow does:',
                        '### Step-by-Step Process',
                        '1. **Click "Add Step"** or drag actions onto the canvas',
                        '2. **Configure each step** with:',
                        '   • Action type (send email, update data, etc.)',
                        '   • Action parameters and settings',
                        '   • Conditions for step execution',
                        '3. **Arrange steps** in the desired order',
                        '4. **Connect steps** to define workflow flow',
                        '### Common Workflow Actions',
                        '  • **Send Email**: Send personalised emails using message templates',
                        '  • **Update Data**: Modify records in the database',
                        '  • **Generate Report**: Create and distribute reports',
                        '  • **Send Notification**: Trigger alerts and notifications',
                        '  • **Wait/Delay**: Add time delays between steps',
                        '  • **Conditional Branch**: Create IF/THEN logic paths',
                        '## Configuring Conditions and Logic',
                        'Configure conditions and logic for workflow execution:',
                        '### Adding Conditions',
                        'Add conditions to control workflow flow:',
                        '  • **IF/THEN logic**: Execute steps based on conditions',
                        '  • **Branching**: Create multiple paths based on conditions',
                        '  • **Loops**: Iterate over collections of data',
                        'Example:',
                        '```',
                        'IF dealership.status = "Active" THEN',
                        '  Send welcome email',
                        'ELSE',
                        '  Send activation reminder',
                        'END IF',
                        '```',
                        '### Setting Up Filters',
                        'Set up filters to determine which records the workflow processes:',
                        '  • Filter by specific criteria (location, status, date range)',
                        '  • Narrow down target records',
                        '  • Improve workflow efficiency',
                        '### Error Handling',
                        'Configure error handling and fallback actions:',
                        '  • What to do if a step fails',
                        '  • Retry logic for failed operations',
                        '  • Notification of errors to administrators',
                        '## Using Variable Mappings',
                        'Use variable mappings to personalise workflows:',
                        '1. Click the **"Variables"** button (purple button with map icon) in the workflow builder',
                        '2. Configure mappings to connect data sources to message tags',
                        '3. This enables personalised communications and data',
                        '**Note**: See the **Variable Mappings** section for detailed instructions on creating and managing mappings.',
                        '## Testing Workflows',
                        'Test the workflow before activating it to ensure it works correctly:',
                        '### Testing Process',
                        '1. Use the **"Test"** or **"Preview"** function in the workflow builder',
                        '2. Run the workflow with sample data',
                        '3. Verify that steps execute correctly',
                        '4. Check for errors or issues',
                        '5. Review output and results',
                        '6. Make adjustments as needed',
                        '### What to Test',
                        '  • **Trigger conditions**: Ensure triggers fire correctly',
                        '  • **Step execution**: Verify each step runs as expected',
                        '  • **Data flow**: Check that data moves correctly between steps',
                        '  • **Error handling**: Test what happens when errors occur',
                        '  • **Personalisation**: Verify variable mappings work correctly',
                        '## Saving and Activating',
                        'Save and activate the workflow when ready:',
                        '### Save Options',
                        '1. Click **"Save"** to save the workflow',
                        '2. Choose to save as **draft** or **activate immediately**',
                        '3. If activating, the workflow will start running based on its triggers',
                        '4. If saving as draft, you can activate it later from the workflows list',
                        '**Important**: Always test workflows thoroughly before activating them in production.',
                        '## Monitoring Workflow Execution',
                        'Monitor workflow execution and adjust as needed:',
                        '### Monitoring Tools',
                        '  • **Execution logs**: View detailed logs of workflow runs',
                        '  • **Success and failure rates**: Track workflow performance',
                        '  • **Error messages**: Review and resolve errors',
                        '  • **Performance metrics**: Monitor execution time and resource usage',
                        '  • **Adjust configuration**: Update workflow based on results',
                        '## Using Templates',
                        'Use templates to speed up workflow creation:',
                        '1. Start from a template instead of a blank workflow.',
                        '2. Templates provide proven structures.',
                        '3. Customise the template to match your needs.',
                        '4. This saves time and ensures best practices.',
                        'Best Practices:',
                        '  • Start with simple workflows and add complexity gradually',
                        '  • Test thoroughly before activating',
                        '  • Document workflow purpose and configuration',
                        '  • Use descriptive names and descriptions',
                        '  • Monitor workflow execution regularly',
                        '  • Use templates for common patterns',
                        '  • Keep workflows active only when needed',
                        '  • Review and update workflows as processes evolve',
                        'Troubleshooting:',
                        '  • Cannot create workflow: Verify you have Admin or appropriate permissions',
                        '  • Workflow not executing: Check triggers are configured and workflow is active',
                        '  • Steps not working: Verify step configuration and test individual steps',
                        '  • Errors in execution: Review execution logs and error messages',
                        '  • Performance issues: Check workflow complexity and execution frequency'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'templates',
                    title: 'Workflow Templates',
                    content: 'Using and creating workflow templates to speed up workflow development',
                    instructions: [
                        'Understanding Workflow Templates:',
                        'Workflow templates provide pre-built workflows that can be customised for your specific needs.',
                        'They save time by providing proven workflow structures and best practices.',
                        'Templates can be used as starting points or adapted for different scenarios.',
                        'Accessing Templates:',
                        'Navigate to Admin → Workflow Templates to view available templates.',
                        'You will see a list of templates organised by category or type.',
                        'Browsing Templates:',
                        'Browse templates to find what you need:',
                        '  • Browse by category - Organised by workflow type or purpose',
                        '  • Search for specific types - Use search to find relevant templates',
                        '  • View template details - See description, category, and use cases',
                        '  • Preview template structure - Understand what the template includes',
                        'Using a Template:',
                        'Use a template as a starting point for creating new workflows:',
                        '1. Find a template that matches your needs.',
                        '2. Click "Use Template" or "Create from Template".',
                        '3. The workflow builder will open with the template pre-loaded.',
                        '4. The template structure will be ready for customisation.',
                        'Customising Templates:',
                        'Customise template settings to match your requirements:',
                        '1. Modify workflow name and description.',
                        '2. Adjust triggers to match your events or schedule.',
                        '3. Customise workflow steps and actions.',
                        '4. Update conditions and logic as needed.',
                        '5. Configure variable mappings for your data.',
                        '6. Test the customised workflow.',
                        '7. Save and activate when ready.',
                        'Benefits of Templates:',
                        'Templates save time by:',
                        '  • Providing proven workflow structures',
                        '  • Including best practices',
                        '  • Reducing setup time',
                        '  • Ensuring consistency',
                        '  • Offering examples to learn from',
                        'Creating Your Own Templates:',
                        'Create your own templates from existing workflows:',
                        '1. Open an existing workflow in the workflow builder.',
                        '2. Customise it as needed.',
                        '3. When saving, choose "Save as Template".',
                        '4. Enter template details:',
                        '   • Template name',
                        '   • Description',
                        '   • Category',
                        '   • Use case or instructions',
                        '5. Save the template.',
                        '6. It will be available for use by others.',
                        'Sharing Templates:',
                        'Share templates across the organisation:',
                        '  • Templates are available to all users with appropriate permissions',
                        '  • Document templates clearly for others to understand',
                        '  • Use consistent naming conventions',
                        '  • Organise templates by category',
                        '  • Keep templates up to date',
                        'Updating Templates:',
                        'Update templates as business processes evolve:',
                        '1. Find the template you want to update.',
                        '2. Edit the template structure or settings.',
                        '3. Save the updated template.',
                        '4. Note: Updates may affect workflows created from the template (depending on system configuration).',
                        '5. Consider versioning for major template changes.',
                        'Best Practices:',
                        '  • Use templates for common workflow patterns',
                        '  • Customise templates to match your specific needs',
                        '  • Document custom templates clearly',
                        '  • Share useful templates with your team',
                        '  • Keep templates current with process changes',
                        '  • Review and update templates regularly',
                        '  • Test templates before sharing',
                        'Troubleshooting:',
                        '  • Templates not appearing: Verify you have appropriate permissions',
                        '  • Cannot use template: Check template is available and accessible',
                        '  • Template not working: Review template structure and test it',
                        '  • Cannot create template: Verify you have Admin permissions',
                        '  • Template missing options: Check if template needs updating or customisation'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'voicebox-client-creation',
                    title: 'VoiceBox Client Creation',
                    content: 'Creating VoiceBox client databases before syncing and onboarding',
                    instructions: [
                        '## Overview',
                        'Before you can sync data or push messages to VoiceBox, a client database must be created in VoiceBox. This is a prerequisite for all VoiceBox operations.',
                        '## Prerequisites',
                        'Before creating a VoiceBox client database, ensure:',
                        '  • You have access to the VoiceBox SQL Server database',
                        '  • You have appropriate database permissions (CREATE DATABASE, CREATE TABLE, etc.)',
                        '  • The client/group exists in ClientBox',
                        '  • You know the client name that will be used in VoiceBox',
                        '## Step-by-Step Process',
                        '### Step 1: Create the Database',
                        '1. Connect to the VoiceBox SQL Server instance',
                        '2. Create a new database for the client:',
                        '```',
                        'CREATE DATABASE [VoiceBox_ClientName]',
                        'GO',
                        '```',
                        'Replace `ClientName` with the actual client name (e.g., `VoiceBox_ABCDealerships`).',
                        '### Step 2: Run Schema Scripts',
                        '1. Run the standard VoiceBox schema scripts to create tables',
                        '2. Ensure all required tables are created (MessageFolders, Messages, Contacts, Lists, etc.)',
                        '3. Verify the schema is properly initialized',
                        '### Step 3: Create Client Record',
                        '1. Insert a client record in the main VoiceBox database:',
                        '```',
                        'INSERT INTO dbo.Clients (ClientName, DatabaseName, IsActive, CreatedDate)',
                        'VALUES (\'Client Display Name\', \'VoiceBox_ClientName\', 1, GETDATE())',
                        '```',
                        '2. Note the ClientID that is generated',
                        '### Step 4: Configure Mapping in ClientBox',
                        '1. Navigate to **Admin** → **VoiceBox Sync**',
                        '2. Create or update the client mapping',
                        '3. Enter the VoiceBox client name and database name',
                        '4. Test the connection to verify it works',
                        '## Integration Points',
                        'Once the client database is created:',
                        '  • **Onboarding**: Can push messages to VoiceBox',
                        '  • **Workflows**: Can execute workflows against VoiceBox data',
                        '  • **Sync**: Can sync triggers, lists, and messages',
                        '  • **Reports**: Can track email campaign performance',
                        '## Common Issues',
                        '**Database doesn\'t exist error:**',
                        '  • Verify database name is correct',
                        '  • Check database was created successfully',
                        '  • Ensure schema scripts were run',
                        '**Connection fails:**',
                        '  • Verify SQL Server connection string',
                        '  • Check database permissions',
                        '  • Ensure firewall allows connections',
                        '**Client not found:**',
                        '  • Verify client record exists in main VoiceBox database',
                        '  • Check ClientID matches',
                        '  • Ensure mapping is configured correctly',
                        '## Best Practices',
                        '  • Use consistent naming conventions for databases',
                        '  • Document database names and client mappings',
                        '  • Test connections immediately after creation',
                        '  • Keep schema scripts up to date',
                        '  • Monitor database size and performance',
                        'For detailed instructions, see `docs/VOICEBOX_CLIENT_CREATION.md`'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'voicebox',
                    title: 'VoiceBox Integration',
                    content: 'Integrating ClientBox with VoiceBox email marketing platform',
                    instructions: [
                        'Understanding VoiceBox Integration:',
                        'VoiceBox integration connects ClientBox with the VoiceBox email marketing platform.',
                        'This enables synchronisation of client data, tracking of email campaigns, and linking marketing efforts to event visitor data.',
                        'The integration helps measure marketing campaign effectiveness and provides a complete view of customer interactions.',
                        '## Important: Client Database Creation',
                        '**Before syncing or pushing data to VoiceBox, the client database must be created in VoiceBox.**',
                        'The system now validates that client databases exist before attempting operations, preventing errors during onboarding.',
                        'See the **VoiceBox Client Creation** documentation for step-by-step instructions.',
                        '## Accessing VoiceBox Integration',
                        'Navigate to **Admin** → **VoiceBox Sync** to configure VoiceBox integration.',
                        'You will see the VoiceBox integration interface with options for database connection and client syncing.',
                        '## Setting Up Database Connection',
                        'Set up VoiceBox database connection details:',
                        '1. Ensure you have a database configuration pointing to your VoiceBox database.',
                        '2. The VoiceBox database must be SQL Server.',
                        '3. Configure the connection in **Admin** → **Database Config** if not already done.',
                        '4. Test the connection to ensure it works.',
                        '5. The connection will be used for syncing data.',
                        '## Configuring Client Mapping',
                        'Configure client mapping between systems:',
                        '1. Set up mapping rules to match VoiceBox clients with ClientBox groups.',
                        '2. Define match criteria (name, email, etc.).',
                        '3. Set confidence thresholds for automatic matching.',
                        '4. Review and approve matches.',
                        '5. Manually match unmatched records if needed.',
                        '## Client Database Validation',
                        'The system now checks if VoiceBox client databases exist before operations:',
                        '  • **Connection Testing**: Validates client database exists when testing connections',
                        '  • **Onboarding**: Checks before pushing messages to VoiceBox',
                        '  • **Workflow Execution**: Validates before executing workflows',
                        '  • **Sync Operations**: Provides friendly errors if client doesn\'t exist',
                        '**If client database doesn\'t exist, you\'ll see:**',
                        '  • Clear error message explaining the issue',
                        '  • Instructions on how to create the client database',
                        '  • Link to documentation',
                        '  • Guidance on next steps',
                        '## Syncing Client Data',
                        'Sync client data from VoiceBox to the system:',
                        '1. Click **"Sync Groups and Clients"** to start synchronisation.',
                        '2. The system will:',
                        '   • Check if client database exists first',
                        '   • Retrieve groups and clients from VoiceBox',
                        '   • Show friendly error if client database doesn\'t exist',
                        '3. Review sync results showing matched and unmatched records.',
                        '4. For unmatched records, manually match them to existing groups.',
                        '5. Use match confidence scores to help identify correct matches.',
                        '6. Save mappings once configured.',
                        '**Note:** If you see an error that the client database doesn\'t exist, follow the instructions to create it first (see VoiceBox Client Creation documentation).',
                        'Tracking Email Campaigns:',
                        'Track email campaigns and engagement metrics:',
                        '  • Campaign performance data',
                        '  • Email open rates',
                        '  • Click-through rates',
                        '  • Bounce rates',
                        '  • Unsubscribe rates',
                        '  • Campaign-to-visitor mapping',
                        'Linking to Event Visitor Tracking:',
                        'Link VoiceBox campaigns to event visitor tracking:',
                        '1. When visitors arrive at event pages from VoiceBox emails, UTM parameters are captured.',
                        '2. The system links visitors to their VoiceBox campaign source.',
                        '3. This enables tracking of which email campaigns drive event visits.',
                        '4. View campaign performance in event reports.',
                        'Viewing Campaign Metrics:',
                        'View click rates and appointment creation from VoiceBox mailings:',
                        '  • Which campaigns drive the most traffic',
                        '  • Conversion rates from email to appointments',
                        '  • Campaign ROI and effectiveness',
                        '  • Best-performing email content',
                        '  • Optimal send times and frequencies',
                        'Measuring Campaign Effectiveness:',
                        'Integration helps measure marketing campaign effectiveness:',
                        '  • Track complete customer journey from email to appointment',
                        '  • Measure ROI of email marketing campaigns',
                        '  • Identify most effective campaigns and content',
                        '  • Optimise email marketing strategy',
                        '  • Understand customer engagement patterns',
                        'Maintaining Integration:',
                        'Keep connection details secure and up to date:',
                        '  • Regularly review and update database connection settings',
                        '  • Keep credentials secure and rotate passwords periodically',
                        '  • Monitor sync status and resolve any issues',
                        '  • Review and update client mappings as needed',
                        '  • Test integration regularly to ensure it\'s working',
                        'Best Practices:',
                        '  • Set up database connection correctly before syncing',
                        '  • Review match confidence scores carefully',
                        '  • Manually verify important client matches',
                        '  • Monitor sync results regularly',
                        '  • Use UTM parameters consistently in email campaigns',
                        '  • Review campaign metrics to optimise marketing',
                        '  • Keep integration documentation up to date',
                        'Troubleshooting:',
                        '  • Cannot connect to VoiceBox: Verify database configuration and test connection',
                        '  • Sync not working: Check database connection and permissions',
                        '  • Clients not matching: Review match rules and confidence thresholds',
                        '  • Campaign data not appearing: Verify UTM parameters are correctly formatted',
                        '  • Integration errors: Review error logs and check database connectivity',
                        '  • Cannot access VoiceBox sync: Verify you have Admin permissions'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'variable-mappings',
                    title: 'Variable Mappings',
                    content: 'Configuring personalization tags for message templates',
                    instructions: [
                        '## Overview',
                        'Variable Mappings allow you to connect data from your Onboarding App (or other sources) to personalization tags in VoiceBox message templates. This enables you to send personalized messages like **"Hello John, welcome to ABC Motors!"** instead of generic messages.',
                        '### Understanding Variable Mappings',
                        'Variable Mappings create a bridge between:',
                        '  • **VoiceBox Message Tags**: Personalization placeholders in your email/SMS templates (e.g., `%%DealershipName%%`, `%%FirstName%%`)',
                        '  • **Data Sources**: Where the actual values come from (Onboarding App database, VoiceBox contacts, or static text)',
                        '### How It Works',
                        '1. Your message template contains tags: `Hello %%FirstName%%, welcome to %%DealershipName%%!`',
                        '2. You create mappings:',
                        '   • `%%FirstName%%` → Gets value from VoiceBox contact table',
                        '   • `%%DealershipName%%` → Gets value from Onboarding App `groups.name`',
                        '3. When the workflow runs, the SQL generator creates personalized data',
                        '4. VoiceBox replaces the tags with actual values when sending',
                        '## Accessing the Feature',
                        '### Step 1: Open Workflow Builder',
                        '1. Navigate to **Admin** → **Workflow Builder**',
                        '2. Either:',
                        '   • Open an existing workflow, OR',
                        '   • Create a new workflow',
                        '### Step 2: Access Variable Mappings',
                        '1. In the workflow builder, look for the **"Variables"** button in the toolbar',
                        '2. Click the **Variables** button (purple button with a map icon)',
                        '3. The Variable Mappings modal will open',
                        '## Creating Your First Mapping',
                        '### Step 1: Select a Message Template (Optional but Recommended)',
                        '1. In the Variable Mappings modal, you\'ll see a dropdown: **"Select Message Template (to see available tags)"**',
                        '2. Select a message template from your VoiceBox message catalog',
                        '3. The system will display all available tags in that template (e.g., `%%FirstName%%`, `%%LastName%%`, `%%DealershipName%%`)',
                        '4. This helps you see which tags you need to map',
                        '**Note**: You can create mappings without selecting a message, but selecting one helps you see what tags are available.',
                        '### Step 2: Add a New Mapping',
                        '1. Click the **"Add Mapping"** button (top right of the mappings section)',
                        '2. A new mapping row will appear with three fields',
                        '### Step 3: Configure the Mapping',
                        'Fill in the three required fields:',
                        '#### Field 1: Onboarding App Field',
                        '  • **Purpose**: Select which field from your Onboarding App database to use',
                        '  • **How to use**:',
                        '    - Click the dropdown',
                        '    - Select from available fields like:',
                        '      • `groups.name` → Dealership/Group Name',
                        '      • `dealer_locations.name` → Location Name',
                        '      • `users.first_name` → User First Name',
                        '      • `users.last_name` → User Last Name',
                        '      • `users.email` → User Email',
                        '      • And more...',
                        '#### Field 2: VoiceBox Tag Name',
                        '  • **Purpose**: The tag name that appears in your message template (without the `%%` markers)',
                        '  • **How to use**:',
                        '    - Type the tag name in **CamelCase** format',
                        '    - Examples:',
                        '      • `DealershipName` (for `%%DealershipName%%`)',
                        '      • `LocationName` (for `%%LocationName%%`)',
                        '      • `FirstName` (for `%%FirstName%%`)',
                        '      • `CustomerEmail` (for `%%CustomerEmail%%`)',
                        '    - **Important**: This must match exactly what\'s in your VoiceBox message template',
                        '#### Field 3: Data Source',
                        '  • **Purpose**: Where the value comes from',
                        '  • **Options**:',
                        '    - **Onboarding App**: Value comes from the Onboarding App database field you selected',
                        '    - **VoiceBox Contact**: Value comes from the VoiceBox contact table (e.g., `c.FirstName`, `c.Email`)',
                        '    - **Static**: A fixed text value (you\'ll enter it in "Default Value")',
                        '    - **Computed**: Calculated value (future feature)',
                        '### Step 4: Add Default Value (If Using Static)',
                        'If you selected **"Static"** as the data source:',
                        '1. A "Default Value" field will appear',
                        '2. Enter the fixed text you want to use',
                        '3. Example: "Welcome to our dealership!"',
                        '### Step 5: Save the Mapping',
                        '1. Click **"Save Mappings"** at the bottom of the modal',
                        '2. The mappings are now saved to your workflow',
                        '## Data Source Types Explained',
                        '### 1. Onboarding App',
                        '**Use when**: You want to use data from your Onboarding App database (groups, locations, users, etc.)',
                        'Example:',
                        '```',
                        'Onboarding Field: groups.name',
                        'VoiceBox Tag: DealershipName',
                        'Result: %%DealershipName%% in messages will be replaced with the group\'s name',
                        '```',
                        '**Available Fields**:',
                        '  • `groups.name` - Dealership/Group Name',
                        '  • `dealer_locations.name` - Location Name',
                        '  • `dealer_locations.address_line_1` - Location Address',
                        '  • `users.first_name` - User First Name',
                        '  • `users.last_name` - User Last Name',
                        '  • `users.email` - User Email',
                        '  • And more (see dropdown for full list)',
                        '### 2. VoiceBox Contact',
                        '**Use when**: You want to use data directly from the VoiceBox contact record',
                        'Example:',
                        '```',
                        'Onboarding Field: FirstName (just the field name, not a path)',
                        'VoiceBox Tag: FirstName',
                        'Result: %%FirstName%% in messages will be replaced with c.FirstName from VoiceBox',
                        '```',
                        '**Common Fields**:',
                        '  • `FirstName` → `c.FirstName`',
                        '  • `LastName` → `c.LastName`',
                        '  • `Email` → `c.Email`',
                        '  • `MobilePhone` → `c.MobilePhone`',
                        '**Note**: Default fields (FirstName, LastName, Email) are automatically included, so you may not need to map these separately.',
                        '### 3. Static',
                        '**Use when**: You want to use a fixed text value that doesn\'t change',
                        'Example:',
                        '```',
                        'VoiceBox Tag: WelcomeMessage',
                        'Data Source: Static',
                        'Default Value: "Welcome to our dealership!"',
                        'Result: %%WelcomeMessage%% in messages will always be replaced with "Welcome to our dealership!"',
                        '```',
                        '**Use Cases**:',
                        '  • Company slogans',
                        '  • Fixed promotional messages',
                        '  • Standard disclaimers',
                        '  • Brand names',
                        '### 4. Computed',
                        '**Status**: Future feature - not yet implemented',
                        '**Planned Use**: For calculated values (e.g., "Days since purchase", "Total orders", etc.)',
                        '## Step-by-Step Example',
                        '### Scenario: Welcome Email with Dealership and Location Names',
                        '**Goal**: Send a personalized email that says:',
                        '> "Hello John, welcome to ABC Motors at our Downtown location!"',
                        '### Step 1: Check Your Message Template',
                        '1. In VoiceBox, your message template contains:',
                        '```',
                        'Hello %%FirstName%%, welcome to %%DealershipName%% at our %%LocationName%% location!',
                        '```',
                        '2. Note the tags: `FirstName`, `DealershipName`, `LocationName`',
                        '### Step 2: Open Variable Mappings',
                        '1. In Workflow Builder, click **Variables**',
                        '2. Select your message template from the dropdown',
                        '3. You should see the tags: `FirstName`, `DealershipName`, `LocationName`',
                        '### Step 3: Create Mappings',
                        '#### Mapping 1: FirstName (Already Handled)',
                        '  • **Note**: `FirstName` is automatically included by default, so you may not need to map this',
                        '  • If you want to override it:',
                        '    - **Onboarding Field**: Leave empty or use `users.first_name`',
                        '    - **VoiceBox Tag**: `FirstName`',
                        '    - **Data Source**: `voicebox_contact`',
                        '#### Mapping 2: DealershipName',
                        '1. Click **Add Mapping**',
                        '2. **Onboarding Field**: Select `groups.name`',
                        '3. **VoiceBox Tag**: Type `DealershipName`',
                        '4. **Data Source**: Select `onboarding_app`',
                        '5. Click **Save Mappings**',
                        '#### Mapping 3: LocationName',
                        '1. Click **Add Mapping**',
                        '2. **Onboarding Field**: Select `dealer_locations.name`',
                        '3. **VoiceBox Tag**: Type `LocationName`',
                        '4. **Data Source**: Select `onboarding_app`',
                        '5. Click **Save Mappings**',
                        '### Step 4: Verify Your Mappings',
                        'Your mappings list should show:',
                        '  • ✅ `groups.name` → `DealershipName` (onboarding_app)',
                        '  • ✅ `dealer_locations.name` → `LocationName` (onboarding_app)',
                        '  • ✅ `FirstName` (automatic - from VoiceBox contact)',
                        '### Step 5: Test Your Workflow',
                        '1. Save your workflow',
                        '2. Generate SQL (the system will use your mappings)',
                        '3. Execute the workflow',
                        '4. Check the generated messages - they should be personalized!',
                        '## Best Practices',
                        '### 1. Always Select a Message Template First',
                        '  • This shows you which tags are actually used in your messages',
                        '  • Prevents creating unnecessary mappings',
                        '  • Helps ensure tag names match exactly',
                        '### 2. Use Descriptive Tag Names',
                        '**Good**:',
                        '  • `DealershipName`',
                        '  • `LocationAddress`',
                        '  • `CustomerFirstName`',
                        '**Avoid**:',
                        '  • `Tag1`, `Tag2`',
                        '  • `Dealership` (unclear)',
                        '  • `name` (too generic)',
                        '### 3. Match Tag Names Exactly',
                        '  • Tag names are **case-sensitive**',
                        '  • `DealershipName` ≠ `dealershipname` ≠ `Dealership_Name`',
                        '  • Check your VoiceBox message template for exact spelling',
                        '### 4. Use Appropriate Data Sources',
                        '  • **Onboarding App**: For dealership, location, user data',
                        '  • **VoiceBox Contact**: For contact-specific data (name, email, phone)',
                        '  • **Static**: For fixed text that never changes',
                        '### 5. Test with Sample Data',
                        '  • After creating mappings, generate SQL and review it',
                        '  • Check that the personalization JSON includes your mapped fields',
                        '  • Test with a small group first before sending to all contacts',
                        '### 6. Document Your Mappings',
                        '  • Keep a list of which tags you\'re using and why',
                        '  • This helps when updating workflows later',
                        '  • Makes it easier for other team members to understand',
                        '## Troubleshooting',
                        '### Problem: Tags Not Replacing in Messages',
                        '**Possible Causes**:',
                        '1. **Tag name mismatch**: The tag name in your mapping doesn\'t exactly match the tag in your message template',
                        '   • **Solution**: Check spelling and case sensitivity',
                        '   • Example: `%%DealershipName%%` requires tag name `DealershipName` (not `dealershipname`)',
                        '2. **Mapping not saved**: You created the mapping but didn\'t click "Save Mappings"',
                        '   • **Solution**: Make sure to click "Save Mappings" before closing the modal',
                        '3. **Data source issue**: The field you selected doesn\'t have data',
                        '   • **Solution**: Verify the data exists in your database',
                        '### Problem: Can\'t See Available Tags',
                        '**Possible Causes**:',
                        '1. **No message selected**: You haven\'t selected a message template',
                        '   • **Solution**: Select a message from the dropdown',
                        '2. **Message has no tags**: The selected message doesn\'t use personalization tags',
                        '   • **Solution**: This is normal - some messages don\'t need personalization',
                        '3. **Tags not synced**: The message tags haven\'t been extracted yet',
                        '   • **Solution**: Sync messages from VoiceBox (this happens automatically when you select a client)',
                        '### Problem: Onboarding App Fields Show as "NULL" or "TODO"',
                        '**Status**: This is expected behavior currently',
                        '  • Onboarding App field mappings are stored but not yet fully implemented in SQL generation',
                        '  • The structure is in place for future implementation',
                        '  • VoiceBox Contact and Static mappings work fully',
                        '**Workaround**:',
                        '  • Use VoiceBox Contact fields where possible',
                        '  • Use Static values for fixed text',
                        '  • Onboarding App mappings will be fully functional in a future update',
                        '### Problem: Too Many Mappings to Manage',
                        '**Solution**:',
                        '  • Group related mappings together (e.g., all dealership-related tags)',
                        '  • Use consistent naming conventions',
                        '  • Remove unused mappings regularly',
                        '  • Consider using workflow templates to reuse common mappings',
                        '## Quick Reference',
                        '### Default Mappings (Always Included)',
                        'These are automatically available in all workflows:',
                        '  • `FirstName` → `c.FirstName`',
                        '  • `LastName` → `c.LastName`',
                        '  • `Email` → `c.Email`',
                        '### Common Mapping Patterns',
                        '**Dealership Information**:',
                        '```',
                        'groups.name → DealershipName (onboarding_app)',
                        'groups.phone → DealershipPhone (onboarding_app)',
                        '```',
                        '**Location Information**:',
                        '```',
                        'dealer_locations.name → LocationName (onboarding_app)',
                        'dealer_locations.address_line_1 → LocationAddress (onboarding_app)',
                        '```',
                        '**User Information**:',
                        '```',
                        'users.first_name → UserFirstName (onboarding_app)',
                        'users.email → UserEmail (onboarding_app)',
                        '```',
                        '**Static Values**:',
                        '```',
                        '"Welcome!" → WelcomeMessage (static)',
                        '"ABC Motors" → CompanyName (static)',
                        '```'
                    ],
                    image: null,
                    video: null
                }
            ]
        },
        {
            id: 'reports',
            title: 'Reports & Analytics',
            icon: BarChart3,
            description: 'Viewing reports and analytics',
            subsections: [
                {
                    id: 'event-reports',
                    title: 'Event Reports',
                    content: 'Viewing comprehensive event analytics and performance reports',
                    instructions: [
                        'Understanding Event Reports:',
                        'Event reports provide detailed analytics on event performance, visitor behaviour, and booking metrics.',
                        'They help you understand how effective your events are and identify areas for improvement.',
                        'Reports include visitor statistics, conversion rates, booking trends, and marketing campaign performance.',
                        'Accessing Event Reports:',
                        'Navigate to Event Management → View Reports to access event analytics.',
                        'You will see a list of all events in the system.',
                        'Select an event from the list to view its detailed reports and analytics dashboard.',
                        'Key Metrics Overview:',
                        'Event reports show several key metrics:',
                        '  • Total Visits - Total number of page views for the event',
                        '  • Unique Visitors - Number of distinct visitors (based on visitor ID)',
                        '  • Appointment Bookings - Total number of appointments booked',
                        '  • Conversion Rate - Percentage of visitors who booked appointments',
                        '  • Average Time on Page - How long visitors spend on the event page',
                        '  • Bounce Rate - Percentage of visitors who leave without booking',
                        'Visitor Statistics:',
                        'View detailed visitor information including:',
                        '  • Visitor ID - Unique identifier for each visitor',
                        '  • Session ID - Individual visit sessions',
                        '  • IP Address - Visitor\'s IP address (for location tracking)',
                        '  • Location - Geographic location (city, country)',
                        '  • Device Type - Desktop, mobile, or tablet',
                        '  • Browser - Web browser used',
                        '  • Operating System - OS of the device',
                        '  • Referrer - Where the visitor came from',
                        'Appointment Conversion Metrics:',
                        'Track how many visitors convert to appointments:',
                        '  • Conversion Rate = (Appointments Booked / Total Visits) × 100',
                        '  • Booking trends over time',
                        '  • Peak booking times and days',
                        '  • Most popular time slots',
                        '  • Cancellation rates',
                        'Filtering Reports:',
                        'Filter reports by date range to analyse specific time periods:',
                        '1. Select a start date.',
                        '2. Select an end date.',
                        '3. Click "Apply" or "Update" to filter the data.',
                        '4. All metrics will update to show only data within the selected range.',
                        'This is useful for:',
                        '  • Analysing specific campaigns',
                        '  • Comparing performance across time periods',
                        '  • Identifying trends and patterns',
                        'UTM Parameter Analysis:',
                        'Analyse UTM parameters to measure marketing campaign effectiveness:',
                        '  • Campaign source (utm_source) - Where traffic came from',
                        '  • Campaign medium (utm_medium) - Marketing medium (email, social, etc.)',
                        '  • Campaign name (utm_campaign) - Specific campaign name',
                        '  • Campaign term (utm_term) - Keywords or terms',
                        '  • Campaign content (utm_content) - Specific content variant',
                        'Use UTM parameters to:',
                        '  • Identify which marketing channels drive the most traffic',
                        '  • Measure campaign ROI',
                        '  • Optimise marketing spend',
                        '  • Understand customer acquisition sources',
                        'Exporting Report Data:',
                        'Export report data for further analysis or reporting:',
                        '1. Configure your report filters and date range.',
                        '2. Click "Export" or "Download".',
                        '3. Choose export format (CSV, Excel, PDF, etc.).',
                        '4. The report will be generated and downloaded.',
                        'Exported data can be used for:',
                        '  • Further analysis in spreadsheet applications',
                        '  • Creating custom visualisations',
                        '  • Sharing with stakeholders',
                        '  • Archival and record-keeping',
                        'Optimising Based on Reports:',
                        'Use reports to optimise event configuration and marketing strategies:',
                        '  • Identify high-performing events and replicate success factors',
                        '  • Improve low-performing events',
                        '  • Optimise availability based on booking patterns',
                        '  • Adjust marketing spend based on channel performance',
                        '  • Improve conversion rates through data-driven changes',
                        'Best Practices:',
                        '  • Review reports regularly to track performance',
                        '  • Compare metrics across different time periods',
                        '  • Use UTM parameters consistently in marketing campaigns',
                        '  • Monitor conversion rates and identify improvement opportunities',
                        '  • Track which marketing channels perform best',
                        '  • Use data to optimise event configuration and availability',
                        '  • Share key metrics with relevant stakeholders',
                        'Troubleshooting:',
                        '  • Reports not loading: Check your internet connection and refresh the page',
                        '  • No data showing: Verify the event has received traffic and date range is correct',
                        '  • Metrics seem incorrect: Check that visitor tracking is enabled and working',
                        '  • Cannot export: Verify you have appropriate permissions and try a smaller date range',
                        '  • UTM parameters not tracking: Verify UTM parameters are correctly formatted in campaign URLs'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'visitor-tracking',
                    title: 'Visitor Tracking',
                    content: 'Understanding visitor data and tracking event page visitors',
                    instructions: [
                        'Understanding Visitor Tracking:',
                        'Visitor tracking captures detailed information about event page visitors.',
                        'This data helps you understand who is visiting your events, how they found you, and what they do on your pages.',
                        'Tracking is automatic and happens in the background when visitors access event pages.',
                        'Visitor Identification:',
                        'Each visitor is assigned a unique visitor ID that persists across sessions:',
                        '  • Visitor ID is stored in cookies or local storage',
                        '  • It persists across multiple visits',
                        '  • Allows tracking of returning visitors',
                        '  • Helps identify unique vs returning visitors',
                        'Session Tracking:',
                        'Session IDs track individual visits to event pages:',
                        '  • Each visit creates a new session',
                        '  • Sessions track activity during a single visit',
                        '  • Session data includes pages viewed, time spent, and actions taken',
                        '  • Multiple sessions can belong to the same visitor',
                        'Visitor Data Collected:',
                        'Visitor data includes comprehensive information:',
                        '  • IP address - For location and network identification',
                        '  • Location - Geographic location (city, country)',
                        '  • Device type - Desktop, mobile, or tablet',
                        '  • Browser information - Browser type and version',
                        '  • Operating system - OS of the device',
                        '  • Screen resolution - Display capabilities',
                        '  • Language preferences - Browser language settings',
                        '  • Referrer - Where the visitor came from',
                        'UTM Parameter Tracking:',
                        'UTM parameters are captured to track marketing campaign sources:',
                        '  • utm_source - Campaign source (e.g., google, email)',
                        '  • utm_medium - Marketing medium (e.g., cpc, email)',
                        '  • utm_campaign - Campaign name',
                        '  • utm_term - Keywords or terms',
                        '  • utm_content - Content variant',
                        'These parameters help identify which marketing efforts drive traffic.',
                        'VoiceBox Integration:',
                        'VoiceBox integration parameters link visitors to email campaigns:',
                        '  • Campaign IDs from VoiceBox emails',
                        '  • List IDs and segment information',
                        '  • Email send information',
                        '  • Click-through tracking',
                        'This enables measurement of email marketing effectiveness.',
                        'Understanding Audience Demographics:',
                        'Visitor data helps understand audience demographics and behaviour:',
                        '  • Geographic distribution of visitors',
                        '  • Device and browser preferences',
                        '  • Traffic sources and referrers',
                        '  • Visit patterns and timing',
                        '  • Engagement levels and behaviour',
                        'Tracking Conversions:',
                        'Track which visitors convert to appointments:',
                        '  • Link visitor IDs to appointment bookings',
                        '  • Identify conversion paths',
                        '  • Understand what leads to conversions',
                        '  • Measure conversion rates by source',
                        'Using Visitor Data:',
                        'Use visitor data to improve event pages and marketing strategies:',
                        '  • Optimise pages based on device usage',
                        '  • Improve content for your audience',
                        '  • Focus marketing efforts on best-performing channels',
                        '  • Personalise experiences based on visitor data',
                        '  • A/B test different page variations',
                        '  • Improve conversion rates',
                        'Privacy Considerations:',
                        'Important privacy considerations:',
                        '  • Visitor data is collected automatically',
                        '  • Ensure compliance with privacy regulations',
                        '  • Consider visitor privacy preferences',
                        '  • Use data responsibly and ethically',
                        'Best Practices:',
                        '  • Review visitor data regularly to understand your audience',
                        '  • Use UTM parameters consistently in marketing campaigns',
                        '  • Track conversions to measure effectiveness',
                        '  • Analyse visitor behaviour to improve pages',
                        '  • Respect visitor privacy and comply with regulations',
                        '  • Use data to inform marketing and content decisions',
                        'Troubleshooting:',
                        '  • Visitor data not appearing: Verify tracking is enabled and event has received traffic',
                        '  • Visitor IDs not persisting: Check cookie settings and browser compatibility',
                        '  • UTM parameters not captured: Verify parameters are correctly formatted in URLs',
                        '  • Location data inaccurate: IP-based location may have limitations',
                        '  • Conversion tracking not working: Verify appointments are properly linked to visitors'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'conversion',
                    title: 'Conversion Metrics',
                    content: 'Tracking conversions and measuring appointment booking effectiveness',
                    instructions: [
                        'Understanding Conversion Metrics:',
                        'Conversion metrics measure how many visitors become appointments.',
                        'They are key indicators of event page effectiveness and marketing performance.',
                        'Tracking conversions helps you understand what works and identify improvement opportunities.',
                        'Viewing Conversion Rates:',
                        'View conversion rates in event reports:',
                        '1. Navigate to Event Management → View Reports.',
                        '2. Select an event to view its analytics.',
                        '3. Find the conversion metrics section.',
                        '4. View overall conversion rate and related metrics.',
                        'Calculating Conversion Rate:',
                        'Conversion rate is calculated as:',
                        '  Conversion Rate = (Appointments Booked / Total Visits) × 100',
                        'Example:',
                        '  • 1000 total visits',
                        '  • 50 appointments booked',
                        '  • Conversion rate = (50 / 1000) × 100 = 5%',
                        'Higher conversion rates indicate more effective event pages and marketing.',
                        'Tracking Conversion Trends:',
                        'Track conversion trends over time to measure improvements:',
                        '  • Compare conversion rates across different time periods',
                        '  • Identify trends (improving, declining, stable)',
                        '  • Measure impact of changes to event pages',
                        '  • Track seasonal or cyclical patterns',
                        '  • Set benchmarks and goals',
                        'Comparing Conversion Rates:',
                        'Compare conversion rates across different events or campaigns:',
                        '  • Which events have the highest conversion rates',
                        '  • What makes high-converting events successful',
                        '  • Identify best practices from top performers',
                        '  • Apply learnings to other events',
                        '  • Benchmark against industry standards',
                        'Identifying Top Conversion Sources:',
                        'Identify which marketing sources drive the most conversions:',
                        '  • Compare conversion rates by traffic source',
                        '  • Identify best-performing marketing channels',
                        '  • Focus marketing spend on high-converting sources',
                        '  • Optimise low-performing channels',
                        '  • Measure ROI by source',
                        'Analysing Visitor Behaviour:',
                        'Analyse visitor behaviour to understand what leads to conversions:',
                        '  • What pages or content convert best',
                        '  • Which time slots are most popular',
                        '  • Device types that convert better',
                        '  • Geographic locations with higher conversion',
                        '  • Visitor paths that lead to bookings',
                        '  • Drop-off points in the booking process',
                        'Optimising Based on Data:',
                        'Use conversion data to optimise event pages and booking processes:',
                        '  • Improve pages with low conversion rates',
                        '  • Simplify booking processes',
                        '  • Highlight popular time slots',
                        '  • Address common drop-off points',
                        '  • Test different page layouts and content',
                        '  • Improve mobile experience if mobile conversion is low',
                        'Setting Conversion Goals:',
                        'Set conversion goals and track progress towards them:',
                        '1. Define realistic conversion goals based on:',
                        '   • Historical performance',
                        '   • Industry benchmarks',
                        '   • Business objectives',
                        '2. Track progress regularly.',
                        '3. Adjust strategies if goals aren\'t being met.',
                        '4. Celebrate when goals are achieved.',
                        '5. Set new goals as performance improves.',
                        'Key Conversion Metrics:',
                        'Important conversion metrics to track:',
                        '  • Overall conversion rate',
                        '  • Conversion rate by source',
                        '  • Conversion rate by device',
                        '  • Conversion rate by location',
                        '  • Conversion rate by time of day',
                        '  • Conversion rate by day of week',
                        '  • Average time to conversion',
                        'Best Practices:',
                        '  • Monitor conversion rates regularly',
                        '  • Set realistic but challenging goals',
                        '  • Test changes and measure impact',
                        '  • Focus on high-impact improvements',
                        '  • Learn from high-converting events',
                        '  • Address conversion barriers',
                        '  • Continuously optimise based on data',
                        'Troubleshooting:',
                        '  • Conversion rates seem low: Review booking process for barriers, check for technical issues',
                        '  • Rates not updating: Verify appointments are being tracked correctly',
                        '  • Cannot compare events: Ensure events have similar structures and time periods',
                        '  • Data seems incorrect: Verify visitor tracking and appointment linking are working',
                        '  • Goals not achievable: Review goals and adjust based on realistic expectations'
                    ],
                    image: null,
                    video: null
                }
            ]
        },
        {
            id: 'admin',
            title: 'Administration',
            icon: Shield,
            description: 'Administrative functions',
            subsections: [
                {
                    id: 'admin-overview',
                    title: 'Admin Overview',
                    content: 'Understanding the administrative dashboard and system-wide management functions',
                    instructions: [
                        'Understanding the Admin Section:',
                        'The Admin section provides system-wide configuration and management capabilities.',
                        'It is the central hub for administrators to configure, manage, and monitor the ClientBox system.',
                        'Only users with Admin role can access these functions, ensuring system security and integrity.',
                        'Accessing Admin Functions:',
                        'Navigate to Admin in the main navigation to access administrative functions.',
                        'You will see the admin dashboard with an overview of system status and key metrics.',
                        'The admin section includes multiple sub-sections for different administrative tasks.',
                        'Admin Dashboard:',
                        'The admin dashboard provides:',
                        '  • System status overview',
                        '  • Key metrics and statistics',
                        '  • Recent activity and changes',
                        '  • Quick access to common administrative tasks',
                        '  • Alerts and notifications',
                        'Use the dashboard to get a quick view of system health and activity.',
                        'Key Admin Functions:',
                        'Admin functions include:',
                        '  • User Management - Creating and managing user accounts',
                        '  • Role & Permission Management - Configuring roles and access control',
                        '  • System Settings - Global configuration options',
                        '  • Data Management - Importing and exporting data',
                        '  • Template Management - Managing master templates',
                        '  • Workflow Management - Creating and managing automated workflows',
                        '  • Database Configuration - Setting up external database connections',
                        '  • Audit Logs - Viewing system activity and changes',
                        'System Configuration:',
                        'Use admin functions to configure the system for your organisation:',
                        '  • Set up initial data (OEMs, groups, locations)',
                        '  • Configure system-wide settings',
                        '  • Customise templates and workflows',
                        '  • Set up integrations with external systems',
                        '  • Configure security and access controls',
                        'Regular Administrative Tasks:',
                        'Common administrative tasks include:',
                        '  • Managing users - Creating accounts, updating roles, deactivating users',
                        '  • Managing roles and permissions - Configuring access control',
                        '  • Updating system settings - Keeping configuration current',
                        '  • Managing templates - Updating master templates',
                        '  • Monitoring system usage - Reviewing activity and performance',
                        '  • Data maintenance - Importing, exporting, and cleaning data',
                        '  • Troubleshooting - Resolving issues and errors',
                        'System Monitoring:',
                        'Monitor system usage and performance through admin tools:',
                        '  • User activity and login history',
                        '  • System performance metrics',
                        '  • Error logs and warnings',
                        '  • Data usage and storage',
                        '  • Integration status',
                        'Regular monitoring helps identify issues early and ensure optimal performance.',
                        'Keeping Settings Current:',
                        'Keep system settings up to date as your organisation evolves:',
                        '  • Review settings periodically',
                        '  • Update configurations when processes change',
                        '  • Add new options and values as needed',
                        '  • Remove outdated configurations',
                        '  • Document changes for reference',
                        'Best Practices:',
                        '  • Regularly review and update system settings',
                        '  • Monitor system activity and performance',
                        '  • Keep user accounts and roles current',
                        '  • Document administrative changes',
                        '  • Test changes in a safe environment when possible',
                        '  • Backup data before major changes',
                        '  • Coordinate with team members on system changes',
                        '  • Review audit logs regularly for security',
                        'Troubleshooting:',
                        '  • Cannot access Admin section: Verify you have Admin role permissions',
                        '  • Settings not saving: Check your permissions and verify all required fields are filled',
                        '  • Dashboard not loading: Refresh the page or check your internet connection',
                        '  • Metrics seem incorrect: Verify data sources and check for system issues',
                        '  • Cannot perform admin action: Review your role permissions and contact system administrator if needed'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-checklist',
                    title: 'Master Checklist Management',
                    content: 'Creating and managing the master onboarding checklist template',
                    instructions: [
                        'Understanding Master Checklist Management:',
                        'The master checklist is used as a template for all dealership onboarding processes.',
                        'It defines the standard tasks and structure that new dealerships will use during onboarding.',
                        'Changes to the master checklist affect all new dealerships, but existing dealerships retain their current checklist.',
                        'Accessing Master Checklist:',
                        'Navigate to Admin → Checklist to manage the master checklist template.',
                        'You will see the master checklist interface with options to add, edit, and manage checklist items.',
                        'Checklist Structure:',
                        'Checklist items are organised into two types:',
                        '  • Sales - Tasks related to sales operations and processes',
                        '  • Service - Tasks related to service operations and processes',
                        'This separation helps organise tasks by business function.',
                        'Adding Checklist Items:',
                        'To add a new checklist item:',
                        '1. Click "Add Item" button.',
                        '2. Select the type (Sales or Service).',
                        '3. Enter the task name (required).',
                        '4. Select a category from the dropdown (categories can be managed in Admin → Settings → Checklist Categories).',
                        '5. Configure the item:',
                        '   • Field type - What input method users will see (text, number, date, dropdown, checkbox, etc.)',
                        '   • Info field - Additional information or instructions for users',
                        '   • Required/Optional - Whether the task must be completed',
                        '   • Display order - Order in which the item appears',
                        '6. Click "Save" to add the item.',
                        'Field Types:',
                        'Each item can have a field type that determines the input method:',
                        '  • Text - Single-line text input',
                        '  • Number - Numeric input',
                        '  • Date - Date picker',
                        '  • Dropdown - Select from predefined options',
                        '  • Checkbox - Yes/no selection',
                        '  • Email - Email address input',
                        '  • URL - Web address input',
                        '  • Textarea - Multi-line text input',
                        'Field types determine what users see when completing the task.',
                        'Editing Checklist Items:',
                        'Items can be edited inline:',
                        '1. Find the item in the checklist.',
                        '2. Click the edit icon next to the item.',
                        '3. Modify any of the fields (name, category, field type, info).',
                        '4. Click "Save" to apply changes.',
                        'Deleting Checklist Items:',
                        'Delete items by clicking the delete icon:',
                        '1. Find the item you want to remove.',
                        '2. Click the delete icon.',
                        '3. Confirm the deletion when prompted.',
                        '4. Warning: This removes the item from all dealerships using the template.',
                        '5. Existing dealerships that already have this item will retain it, but new dealerships won\'t get it.',
                        'Impact of Changes:',
                        'Changes to the master checklist:',
                        '  • Affect all new dealerships - New dealerships get the updated template',
                        '  • Do not affect existing dealerships - They retain their current checklist',
                        '  • Can be pushed to existing dealerships - Use "Push Template" if needed',
                        'Organising with Categories:',
                        'Use categories to organise related tasks together:',
                        '  • Group similar tasks under the same category',
                        '  • Makes checklists easier to navigate',
                        '  • Helps users find related tasks',
                        '  • Categories can be managed in Admin → Settings → Checklist Categories',
                        'Best Practices:',
                        '  • Use clear, descriptive task names',
                        '  • Organise tasks logically with categories',
                        '  • Choose appropriate field types for each task',
                        '  • Provide helpful information in the Info field',
                        '  • Test checklist items before deploying',
                        '  • Document the purpose of each item',
                        '  • Review and update the master checklist regularly',
                        '  • Consider impact before deleting items',
                        'Troubleshooting:',
                        '  • Cannot add item: Verify you have Admin permissions',
                        '  • Category not in dropdown: Add category in Admin → Settings → Checklist Categories',
                        '  • Item not appearing: Check that item is saved and checklist is refreshed',
                        '  • Changes not applying: Verify changes are saved and new dealerships are created after the change',
                        '  • Cannot delete item: Check if item is in use and verify your permissions'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-factfind',
                    title: 'Fact Find Template',
                    content: 'Configuring the fact find template for collecting dealership information',
                    instructions: [
                        'Understanding Fact Find Templates:',
                        'The fact find template defines fields and structure for collecting dealership information during onboarding.',
                        'It standardises the information collection process across all dealerships.',
                        'The template is used across all dealership onboarding processes.',
                        'Accessing Fact Find Template:',
                        'Navigate to Admin → Fact Find to manage the fact find template.',
                        'You will see the fact find template interface with options to add, edit, and manage fields.',
                        'Adding Fields:',
                        'Add new fields by clicking "Add Field" and selecting the field type:',
                        '1. Click "Add Field" button.',
                        '2. Select the field type from the dropdown:',
                        '   • Text - Single-line text input',
                        '   • Number - Numeric input',
                        '   • Date - Date picker',
                        '   • Dropdown - Select from predefined options',
                        '   • Checkbox - Yes/no selection',
                        '   • Textarea - Multi-line text input',
                        '   • Email - Email address input',
                        '   • URL - Web address input',
                        '   • And more field types',
                        '3. The field will be added to the template.',
                        'Configuring Field Properties:',
                        'Configure field properties including:',
                        '  • Field name - Internal identifier',
                        '  • Field label - Display name shown to users',
                        '  • Required status - Whether the field must be filled',
                        '  • Default values - Pre-filled values (if applicable)',
                        '  • Validation rules - Format requirements',
                        '  • Help text - Instructions for users',
                        '  • Display order - Position in the form',
                        'Reordering Fields:',
                        'Reorder fields by dragging them to the desired position:',
                        '1. Click and hold on a field.',
                        '2. Drag it to the new position.',
                        '3. Release to drop it.',
                        '4. The new order is saved automatically.',
                        'Organising Fields:',
                        'Fields can be organised into sections or groups for better organisation:',
                        '  • Group related fields together',
                        '  • Create logical sections',
                        '  • Improve form usability',
                        '  • Make forms easier to complete',
                        'Editing Fields:',
                        'Edit existing fields by clicking the edit icon:',
                        '1. Find the field in the template.',
                        '2. Click the edit icon.',
                        '3. Modify field properties.',
                        '4. Click "Save" to apply changes.',
                        'Deleting Fields:',
                        'Delete fields by clicking the delete icon:',
                        '1. Find the field you want to remove.',
                        '2. Click the delete icon.',
                        '3. Confirm the deletion.',
                        '4. Warning: This removes the field from the template.',
                        'Template Impact:',
                        'Changes to the template:',
                        '  • Affect all new dealerships - New dealerships get the updated template',
                        '  • Do not affect existing dealerships - They retain their current fact find',
                        '  • Can be pushed to existing dealerships if needed',
                        'Testing the Template:',
                        'Test the template by viewing it in a dealership setup page:',
                        '1. Navigate to a dealership setup page.',
                        '2. View the fact find section.',
                        '3. Verify fields appear correctly.',
                        '4. Test field functionality.',
                        '5. Make adjustments to the template as needed.',
                        'Best Practices:',
                        '  • Use clear, descriptive field labels',
                        '  • Organise fields logically',
                        '  • Mark only essential fields as required',
                        '  • Provide helpful instructions in help text',
                        '  • Test the template before deploying',
                        '  • Keep the template current with business needs',
                        '  • Document field purposes',
                        'Troubleshooting:',
                        '  • Cannot add field: Verify you have Admin permissions',
                        '  • Field not appearing: Check that field is saved and template is refreshed',
                        '  • Cannot reorder fields: Try refreshing the page or check browser compatibility',
                        '  • Changes not applying: Verify changes are saved and new dealerships are created after the change'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-contact-plan',
                    title: 'Workflow Templates',
                    content: 'Managing reusable workflow templates and message suites for dealership communications',
                    instructions: [
                        'Understanding Workflow Templates:',
                        'Workflow templates define reusable communication flows aligned to brand standards.',
                        'They encapsulate list selection, filters, timing, and message templates.',
                        'Accessing Workflow Templates:',
                        'Navigate to Admin → Workflow Templates.',
                        'Use "Create from Template" in the Workflow Builder to apply them.',
                        'Message Suites:',
                        'Onboarding generates message folders and templates in VoiceBox from master templates.',
                        'This allows full message suites to be created for new customers, not one message at a time.',
                        'Best Practices:',
                        '  • Keep templates modular and focused on a single journey',
                        '  • Use list-based targeting with clear filters',
                        '  • Validate message templates before applying to all locations',
                        'Troubleshooting:',
                        '  • No templates available: Verify workflow templates are created and active',
                        '  • Messages not available: Generate message suite and sync messages from VoiceBox',
                        '  • Templates not applying: Ensure you have Admin permissions and a group selected'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-signoff',
                    title: 'Signoff Templates',
                    content: 'Creating and managing master signoff document templates',
                    instructions: [
                        'Understanding Signoff Templates:',
                        'Signoff templates are used to create formal signoff documents for dealerships.',
                        'They standardise the signoff process and ensure consistency across all dealerships.',
                        'The master template serves as the starting point for all dealership signoffs.',
                        'Accessing Signoff Templates:',
                        'Navigate to Admin → Signoff to manage signoff templates.',
                        'You will see the signoff template interface with Sales and Service types.',
                        'Template Types:',
                        'Templates are separated into two main types:',
                        '  • Sales Signoff - Documents for sales operations',
                        '  • Service Signoff - Documents for service operations',
                        'Each type can have multiple message templates configured.',
                        'Configuring Signoff Fields:',
                        'Configure signoff fields including:',
                        '  • Email address - For the person signing off',
                        '  • Name/Job Title - Name and position of signatory',
                        '  • Signed off checkbox - Confirmation that signoff is complete',
                        '  • Date - Date of signoff',
                        '  • Additional custom fields as needed',
                        'Adding Custom Fields:',
                        'Add custom fields by clicking "Add Field" and configuring properties:',
                        '1. Click "Add Field" button.',
                        '2. Select field type (text, email, checkbox, date, dropdown, etc.).',
                        '3. Configure field properties:',
                        '   • Field name and label',
                        '   • Required/Optional status',
                        '   • Default values',
                        '   • Validation rules',
                        '4. Click "Save" to add the field.',
                        'Editing and Removing Fields:',
                        'Edit field configurations by clicking the edit icon:',
                        '1. Find the field in the template.',
                        '2. Click the edit icon.',
                        '3. Modify field properties.',
                        '4. Save changes.',
                        'Remove fields by clicking the delete icon and confirming.',
                        'Field Requirements:',
                        'Fields can be marked as required or optional:',
                        '  • Required fields must be filled before signoff can be completed',
                        '  • Optional fields can be left blank',
                        'Selecting Message Templates:',
                        'Select which message templates should be included in each signoff type:',
                        '1. In the signoff template configuration, find the "Message Templates" section.',
                        '2. Select from available message templates.',
                        '3. Message templates are configured in Admin → Settings → Message Names.',
                        '4. You can select multiple message templates for a single signoff type.',
                        'Template Application:',
                        'The signoff template is applied to all dealerships and can be customised per dealership:',
                        '  • Standard structure across all dealerships',
                        '  • Dealership-specific customisations when needed',
                        '  • Consistency in signoff processes',
                        '  • Flexibility for unique requirements',
                        'Best Practices:',
                        '  • Keep signoff templates clear and concise',
                        '  • Use required fields only for critical information',
                        '  • Provide clear labels and instructions',
                        '  • Test templates before deploying',
                        '  • Document the purpose of each signoff type',
                        '  • Review and update templates regularly',
                        'Troubleshooting:',
                        '  • Cannot add field: Verify you have Admin permissions',
                        '  • Message templates not available: Configure in Admin → Settings → Message Names',
                        '  • Template not applying: Verify template is saved and dealerships have the latest version',
                        '  • Fields not saving: Check required field properties are filled'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-links',
                    title: 'Useful Links Management',
                    content: 'Managing master useful links templates for dealership resources',
                    instructions: [
                        'Understanding Useful Links Templates:',
                        'Useful links templates provide standard resources that are available to all dealerships.',
                        'They centralise access to training materials, policies, procedures, and other essential resources.',
                        'The master template serves as the starting point for all dealerships.',
                        'Accessing Links Management:',
                        'Navigate to Admin → Links to manage useful links templates.',
                        'You will see the links management interface with Sales and Service categories.',
                        'Link Organisation:',
                        'Links are organised into two main categories:',
                        '  • Sales Links - Resources for sales operations',
                        '  • Service Links - Resources for service operations',
                        'This categorisation helps dealerships find relevant resources quickly.',
                        'Adding Links:',
                        'To add a new link:',
                        '1. Click on the appropriate category tab (Sales or Service).',
                        '2. Click "Add Link" in that category.',
                        '3. Fill in link details:',
                        '   • Link key - Internal identifier (lowercase, underscores)',
                        '   • Link label - Display name shown to users',
                        '   • URL - Full web address (http:// or https://)',
                        '   • Required/Optional - Whether dealerships must access this link',
                        '4. Click "Save" to add the link.',
                        'Reordering Links:',
                        'Links can be reordered by dragging them:',
                        '1. Click and hold on a link item.',
                        '2. Drag it to the desired position.',
                        '3. Release to drop it.',
                        '4. The new order is saved automatically.',
                        'Editing and Deleting Links:',
                        'Edit links by clicking the edit icon:',
                        '1. Find the link in the list.',
                        '2. Click the edit icon.',
                        '3. Modify link details.',
                        '4. Save changes.',
                        'Delete links by clicking the delete icon and confirming.',
                        'Required vs Optional Links:',
                        'Links can be marked as required or optional:',
                        '  • Required links must be accessed by dealerships',
                        '  • Optional links are available but not mandatory',
                        'Template Usage:',
                        'The link template is used across all dealerships:',
                        '  • Standard resources available to all',
                        '  • Each dealership can add their own specific links',
                        '  • Links appear in the dealership dashboard',
                        'Best Practices:',
                        '  • Use clear, descriptive link labels',
                        '  • Keep URLs up to date and test them regularly',
                        '  • Organise links logically by category',
                        '  • Mark critical resources as required',
                        '  • Test links before adding them',
                        '  • Review and update links periodically',
                        '  • Remove broken or outdated links',
                        'Troubleshooting:',
                        '  • Cannot add link: Verify you have Admin permissions',
                        '  • Link not working: Check URL is correct and accessible',
                        '  • Cannot reorder: Try refreshing the page or check browser compatibility',
                        '  • Template not applying: Verify template is saved and pushed to dealerships'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-data-imports',
                    title: 'Data Imports',
                    content: 'Importing data from external sources into ClientBox (Admin)',
                    instructions: [
                        'Understanding Admin Data Imports:',
                        'Admin data imports allow administrators to bulk import data into ClientBox from external sources.',
                        'This is essential for initial system setup, migrating data from other systems, or bulk updates.',
                        'The admin import interface provides additional options and control over the import process.',
                        'Accessing Admin Data Imports:',
                        'Navigate to Admin → Data Imports to import data into the system.',
                        'You will see the admin data import interface with options for different import types.',
                        'Selecting Import Target:',
                        'Select the import target from the dropdown:',
                        '  • Master Checklist - Onboarding checklist items',
                        '  • Contact Messages - Contact plan messages',
                        '  • Dealer Locations - Consolidated dealership locations import',
                        '  • Addresses - Address records',
                        '  • Admin Links - Useful links',
                        '  • Dropdown Options - System dropdown values',
                        '  • And more data types...',
                        'Each import target has specific requirements and field mappings.',
                        'Preparing Data Files:',
                        'Prepare your data file in CSV or Excel format matching the required structure:',
                        '1. Review the data import guide for field requirements.',
                        '2. Ensure required fields are present in your file.',
                        '3. Format dates consistently (YYYY-MM-DD recommended).',
                        '4. Remove formatting or special characters that might cause issues.',
                        '5. Verify data types are correct.',
                        'Uploading Files:',
                        'Click "Choose File" and select your data file to upload:',
                        '1. Click "Choose File" or "Browse" button.',
                        '2. Select your data file from your computer.',
                        '3. The file will be uploaded and processed.',
                        '4. The system will automatically detect data types for each column.',
                        'Data Type Detection:',
                        'Review and adjust data type detection if needed:',
                        '1. The system automatically detects data types (text, number, date, etc.).',
                        '2. Review the detected types.',
                        '3. Adjust if the detection is incorrect.',
                        '4. Correct data types ensure accurate imports.',
                        'Mapping File Columns:',
                        'Map your file columns to system fields using the field mapping interface:',
                        '1. Review available system fields.',
                        '2. Map each file column to the corresponding system field.',
                        '3. Required fields must be mapped for the import to proceed.',
                        '4. Optional fields can be mapped or skipped.',
                        '5. Verify all mappings are correct.',
                        'Previewing Data:',
                        'Preview the mapped data to verify accuracy before importing:',
                        '1. Review the preview table showing how data will be imported.',
                        '2. Check that values look correct.',
                        '3. Verify data types are appropriate.',
                        '4. Look for any warnings or errors.',
                        '5. Make adjustments to mapping if needed.',
                        'Executing the Import:',
                        'Click "Import Data" to execute the import:',
                        '1. Once satisfied with the preview, click "Import Data".',
                        '2. The system will process the import.',
                        '3. You will see progress indicators.',
                        '4. Large imports may take some time.',
                        'Consolidated Dealer Locations Import:',
                        'For consolidated dealer locations import, the system automatically handles:',
                        '  • Creating or updating groups',
                        '  • Creating or updating locations',
                        '  • Creating or updating addresses',
                        '  • Associating brands with locations',
                        'This simplifies importing complex dealership data.',
                        'Updating Existing Records:',
                        'Existing records with matching names will be updated rather than duplicated:',
                        '  • Prevents duplicate records',
                        '  • Updates existing data with new information',
                        '  • Maintains data integrity',
                        'Reviewing Import Results:',
                        'Review import results and address any errors before proceeding:',
                        '  • Number of records successfully imported',
                        '  • Number of records that failed',
                        '  • Details of errors and warnings',
                        '  • List of records that were skipped',
                        'Address errors by correcting data and re-importing failed records.',
                        'Best Practices:',
                        '  • Always keep backup copies of original files before importing',
                        '  • Test imports with small sample files first',
                        '  • Verify data format and structure before importing',
                        '  • Review import preview carefully',
                        '  • Document your import process',
                        '  • Import during low-usage periods for large imports',
                        '  • Verify imported data after import completes',
                        'Troubleshooting:',
                        '  • Import failing: Check file format, required fields, and data types',
                        '  • Mapping errors: Verify column names and system field requirements',
                        '  • Data validation errors: Review error messages and fix data format issues',
                        '  • Import taking too long: Large files may take time; be patient or split into smaller files',
                        '  • Duplicate records: Check for existing records and verify update logic',
                        '  • Cannot access imports: Verify you have Admin role permissions'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-data-exports',
                    title: 'Data Exports',
                    content: 'Exporting system data for reporting, analysis, and backup (Admin)',
                    instructions: [
                        'Understanding Admin Data Exports:',
                        'Admin data exports allow administrators to extract comprehensive data from ClientBox.',
                        'This is useful for reporting, analysis, backup, compliance, and integration with other systems.',
                        'The admin export interface provides full access to all system data.',
                        'Accessing Admin Data Exports:',
                        'Navigate to Admin → Data Exports to export system data.',
                        'You will see the admin export interface with options for different data types and formats.',
                        'Selecting Data Type:',
                        'Select the type of data you want to export from the dropdown:',
                        '  • Users - User accounts and information',
                        '  • Groups - Dealership groups',
                        '  • Locations - Dealership locations',
                        '  • Events - Event records',
                        '  • Appointments - Appointment bookings',
                        '  • Jobs - Job records',
                        '  • Invoices - Invoice records',
                        '  • Clients - Client records',
                        '  • And more data types...',
                        'Each data type has specific fields available for export.',
                        'Choosing Export Format:',
                        'Choose the export format:',
                        '  • CSV (Comma-Separated Values) - Universal format, works with Excel and most applications',
                        '  • Excel (.xlsx) - Native Excel format with formatting support',
                        '  • JSON - Structured data format for integrations',
                        '  • PDF - Formatted reports (for some data types)',
                        'Select the format that best suits your needs.',
                        'Applying Filters:',
                        'Apply filters to export specific subsets of data if needed:',
                        '  • Date range filters',
                        '  • Status filters',
                        '  • Location or group filters',
                        '  • User filters',
                        '  • Custom field filters',
                        'Filters help you export only the data you need, reducing file size and processing time.',
                        'Selecting Fields:',
                        'Select which fields/columns to include in the export:',
                        '1. Review available fields for the selected data type.',
                        '2. Check or uncheck fields to include or exclude them.',
                        '3. Include only fields you need to keep exports focused.',
                        '4. Required fields are typically included by default.',
                        'Generating the Export:',
                        'Click "Export" to generate the export file:',
                        '1. Review your selections (data type, format, filters, fields).',
                        '2. Click "Export" button.',
                        '3. The system will process the export.',
                        '4. Large exports may take some time to process.',
                        '5. You will see a progress indicator.',
                        'Downloading the File:',
                        'The system will process the export and provide a download link:',
                        '1. Once the export is complete, a download link will appear.',
                        '2. Click the download link to save the file to your computer.',
                        '3. The file will be saved in your browser\'s default download location.',
                        '4. You can rename or move the file as needed.',
                        'Using Exported Data:',
                        'Exports can be used for:',
                        '  • Reporting - Create custom reports in Excel or other tools',
                        '  • Analysis - Analyse data in spreadsheet applications',
                        '  • Backup - Keep copies of important data',
                        '  • Integration - Import into other systems',
                        '  • Compliance - Meet data retention requirements',
                        '  • Archival - Long-term data storage',
                        'Large Exports:',
                        'Large exports may take some time to process:',
                        '  • Be patient during processing',
                        '  • Use filters to reduce data volume if possible',
                        '  • Consider exporting during off-peak hours',
                        '  • Split large exports into smaller chunks if needed',
                        'Export Contents:',
                        'Exported files contain all data matching your selected criteria and fields:',
                        '  • All records that match your filters',
                        '  • Only the fields you selected',
                        '  • Formatted according to the export format',
                        '  • Ready for use in other applications',
                        'Best Practices:',
                        '  • Export only the data you need using filters',
                        '  • Select appropriate fields to keep files manageable',
                        '  • Use CSV for maximum compatibility',
                        '  • Use Excel format if you need formatting',
                        '  • Schedule regular exports for important data',
                        '  • Verify exported data after download',
                        '  • Store exports securely, especially if they contain sensitive information',
                        '  • Document your export processes',
                        'Troubleshooting:',
                        '  • Export not generating: Check your permissions and try a smaller date range',
                        '  • Export taking too long: Use filters to reduce data volume or try exporting during off-peak hours',
                        '  • File not downloading: Check browser settings and download permissions',
                        '  • Missing fields: Verify field selections and check if fields are available for the data type',
                        '  • Data format issues: Verify export format is compatible with your intended use',
                        '  • Cannot access exports: Verify you have Admin role permissions'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-database-config',
                    title: 'Database Configuration',
                    content: 'Configuring external database connections for integrations (Admin)',
                    instructions: [
                        'Understanding Database Configuration:',
                        'Database configurations allow ClientBox to connect to external databases for imports, exports, and integrations.',
                        'This enables integration with other systems, data synchronisation, and advanced data operations.',
                        'Only administrators should configure database connections.',
                        'Accessing Database Configuration:',
                        'Navigate to Admin → Database Config to manage external database connections.',
                        'You will see a list of configured database connections and options to add new ones.',
                        'Adding a New Configuration:',
                        'Click "Add Configuration" to create a new database connection:',
                        '1. Click "Add Configuration" button.',
                        '2. Fill in connection details:',
                        '   • Configuration name - Descriptive name for this connection',
                        '   • Database type - Select from: PostgreSQL, MySQL, SQL Server, or Oracle',
                        '   • Host - Database server address or IP',
                        '   • Port - Database port number (defaults provided based on type)',
                        '   • Database name - Name of the database',
                        '   • Username - Database user account',
                        '   • Password - Database password (encrypted when saved)',
                        '3. Additional settings for SQL Server:',
                        '   • Server instance name (if using named instances)',
                        '   • Trust Server Certificate (for self-signed certificates)',
                        '4. Click "Test Connection" to verify it works.',
                        '5. If successful, click "Save" to store the configuration.',
                        'Testing Connections:',
                        'Always test connections before saving:',
                        '1. Fill in all connection details.',
                        '2. Click "Test Connection" button.',
                        '3. The system attempts to connect to the database.',
                        '4. You will see a success or error message.',
                        '5. If successful, proceed to save.',
                        '6. If it fails, check your connection details.',
                        'Managing Configurations:',
                        'View all configurations in the list view:',
                        '  • See all configured database connections',
                        '  • View configuration names and types',
                        '  • Edit existing configurations by clicking the edit icon',
                        '  • Delete configurations that are no longer needed',
                        'Security:',
                        'Database passwords are encrypted and never displayed in plain text:',
                        '  • Passwords are stored securely',
                        '  • Credentials are encrypted',
                        '  • Connections use encrypted protocols when available',
                        '  • Access is restricted to administrators',
                        'Using Database Configurations:',
                        'Use database configurations for:',
                        '  • VoiceBox integration - Connecting to VoiceBox database',
                        '  • Data imports - Importing from external databases',
                        '  • Data exports - Exporting to external databases',
                        '  • Other integrations - Connecting with various systems',
                        'Best Practices:',
                        '  • Use descriptive names for configurations',
                        '  • Always test connections before saving',
                        '  • Keep connection details secure and up to date',
                        '  • Use dedicated database accounts with limited permissions',
                        '  • Document what each configuration is used for',
                        '  • Remove unused configurations',
                        '  • Regularly review and update connection settings',
                        'Troubleshooting:',
                        '  • Connection test failing: Verify host, port, and database name are correct',
                        '  • Authentication errors: Check username and password are correct',
                        '  • Cannot connect: Verify database server is accessible and firewall allows connections',
                        '  • SSL errors: Check SSL/TLS settings and certificate configuration',
                        '  • Cannot save configuration: Verify you have Admin permissions and all required fields are filled'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-table-browser',
                    title: 'Table Browser',
                    content: 'Browsing database table structures and data to understand schemas (Admin)',
                    instructions: [
                        'Understanding the Table Browser:',
                        'The Table Browser allows administrators to explore database structures from external databases.',
                        'It helps understand database schemas, identify fields, and plan data imports or integrations.',
                        'This is a read-only tool that does not modify any data.',
                        'Accessing the Table Browser:',
                        'Navigate to Admin → Table Browser to explore database structures.',
                        'You will see the table browser interface with options to select a database and browse tables.',
                        'Selecting a Database:',
                        'Select a database configuration from the dropdown to browse:',
                        '1. Choose a database configuration (must be set up in Admin → Database Config first).',
                        '2. The system will connect to the selected database.',
                        '3. Available tables will be retrieved and displayed.',
                        'Viewing Available Tables:',
                        'View a list of all tables in the selected database:',
                        '  • Tables are typically organised by schema or database',
                        '  • Click on a table name to view its structure and data',
                        '  • Use search or filter to find specific tables',
                        'Viewing Table Structure:',
                        'Click on a table name to view its structure:',
                        'Table structure shows:',
                        '  • Column names - Field names in the table',
                        '  • Data types - Type of data stored (text, number, date, etc.)',
                        '  • Constraints - Primary keys, foreign keys, unique constraints',
                        '  • Nullable - Whether columns allow null values',
                        '  • Default values - Default values for columns',
                        '  • Column descriptions - Additional information about columns',
                        'Viewing Sample Data:',
                        'View sample data from the table (limited to a reasonable number of rows for performance):',
                        '  • See actual data values to understand content',
                        '  • Verify data format and structure',
                        '  • Identify field mappings for imports',
                        '  • Limited rows to maintain performance',
                        'Use Cases:',
                        'Use the table browser to:',
                        '  • Understand database schemas before importing data',
                        '  • Identify field names and data types for mapping',
                        '  • Plan data imports and integrations',
                        '  • Validate data structure and format',
                        '  • Learn about external database structures',
                        'Safety Considerations:',
                        'Important safety considerations:',
                        '  • This is a read-only tool - no data is modified',
                        '  • Be cautious when browsing production databases',
                        '  • Only administrators should use the table browser',
                        '  • Large tables may take time to load',
                        '  • Sample data is limited for performance',
                        'Best Practices:',
                        '  • Use the table browser to plan imports before executing them',
                        '  • Document table structures for future reference',
                        '  • Verify field names and data types before mapping',
                        '  • Be mindful of performance when browsing large databases',
                        '  • Use appropriate database configurations for each environment',
                        'Troubleshooting:',
                        '  • Cannot connect to database: Verify database configuration is correct and test connection',
                        '  • Tables not appearing: Check database permissions and verify connection is working',
                        '  • Table structure not loading: Check database accessibility and try refreshing',
                        '  • Sample data not showing: Verify table has data and check row limit settings',
                        '  • Cannot access table browser: Verify you have Admin role permissions'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-voicebox-sync',
                    title: 'VoiceBox Integration & Sync',
                    content: 'Configuring VoiceBox database integration and client syncing (Admin)',
                    instructions: [
                        'Understanding VoiceBox Integration:',
                        'VoiceBox integration connects ClientBox with the VoiceBox email marketing platform.',
                        'This enables synchronisation of client data, tracking of email campaigns, and linking marketing efforts to event visitor data.',
                        'The integration helps measure marketing campaign effectiveness and provides a complete view of customer interactions.',
                        'Accessing VoiceBox Integration:',
                        'Navigate to Admin → VoiceBox Sync to configure VoiceBox integration.',
                        'You will see the VoiceBox integration interface with options for database connection and client syncing.',
                        'Database Configuration:',
                        'Select a database configuration that points to your VoiceBox database:',
                        '1. The VoiceBox database must be SQL Server.',
                        '2. Ensure a database configuration exists in Admin → Database Config.',
                        '3. The configuration should point to your VoiceBox database.',
                        '4. Test the connection before proceeding.',
                        'Synchronising Data:',
                        'Click "Sync Groups and Clients" to synchronise data from VoiceBox:',
                        '1. Click "Sync Groups and Clients" button.',
                        '2. The system will retrieve groups and clients from the VoiceBox database.',
                        '3. You will see sync progress indicators.',
                        '4. Sync results will be displayed when complete.',
                        'Reviewing Sync Results:',
                        'Review the sync results showing matched and unmatched records:',
                        '  • Matched records - Successfully linked to existing groups',
                        '  • Unmatched records - Need manual matching',
                        '  • Match confidence scores - Help identify correct matches',
                        '  • Sync statistics - Summary of sync operation',
                        'Manual Matching:',
                        'For unmatched records, manually match them to existing groups:',
                        '1. Review unmatched records in the sync results.',
                        '2. Use match confidence scores to help identify correct matches.',
                        '3. Select the appropriate group for each unmatched record.',
                        '4. Confirm the match.',
                        '5. The record will be linked to the selected group.',
                        'Creating Client Mappings:',
                        'Create client mappings to link VoiceBox clients to system groups:',
                        '1. Configure client mappings with match rules.',
                        '2. Set confidence thresholds for automatic matching.',
                        '3. Define matching criteria (name, email, etc.).',
                        '4. Save the mapping configuration.',
                        'Testing Mappings:',
                        'Test mappings to verify they work correctly:',
                        '1. Use the test function to verify mappings.',
                        '2. Review test results.',
                        '3. Adjust mapping rules if needed.',
                        '4. Re-test until mappings work correctly.',
                        'Managing Mappings:',
                        'View all client mappings in the mappings list:',
                        '  • See all configured mappings',
                        '  • Edit mappings to update match rules or configurations',
                        '  • Delete mappings that are no longer needed',
                        '  • Review mapping effectiveness',
                        'Automatic Synchronisation:',
                        'Once configured, the system can sync client data from VoiceBox automatically:',
                        '  • Scheduled syncs can be configured',
                        '  • Real-time sync options may be available',
                        '  • Sync frequency can be customised',
                        'Synced Data:',
                        'Synced data includes:',
                        '  • Client information - Names, contact details',
                        '  • Lists - Email lists and segments',
                        '  • Campaign data - Campaign information and performance',
                        '  • Engagement data - Opens, clicks, bounces',
                        'Tracking Email Campaigns:',
                        'Use VoiceBox integration to track email campaigns and link them to event visitor data:',
                        '  • Link campaigns to event visitor tracking',
                        '  • Measure email campaign effectiveness',
                        '  • Track click-through rates',
                        '  • Monitor appointment creation from emails',
                        'Measuring Campaign Effectiveness:',
                        'Integration helps measure marketing campaign effectiveness:',
                        '  • Track complete customer journey from email to appointment',
                        '  • Measure ROI of email marketing campaigns',
                        '  • Identify most effective campaigns',
                        '  • Optimise email marketing strategy',
                        'Security:',
                        'Keep database connection details secure and up to date:',
                        '  • Regularly review and update connection settings',
                        '  • Keep credentials secure',
                        '  • Monitor sync status',
                        '  • Review sync results regularly',
                        'Best Practices:',
                        '  • Set up database connection correctly before syncing',
                        '  • Review match confidence scores carefully',
                        '  • Manually verify important client matches',
                        '  • Monitor sync results regularly',
                        '  • Use UTM parameters consistently in email campaigns',
                        '  • Review campaign metrics to optimise marketing',
                        '  • Keep integration documentation up to date',
                        'Troubleshooting:',
                        '  • Cannot connect to VoiceBox: Verify database configuration and test connection',
                        '  • Sync not working: Check database connection and permissions',
                        '  • Clients not matching: Review match rules and confidence thresholds',
                        '  • Campaign data not appearing: Verify UTM parameters are correctly formatted',
                        '  • Integration errors: Review error logs and check database connectivity',
                        '  • Cannot access VoiceBox sync: Verify you have Admin permissions'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-workflows',
                    title: 'Workflows Management',
                    content: 'Viewing and managing all automated workflows in the system (Admin)',
                    instructions: [
                        'Understanding Workflow Management:',
                        'Workflow management allows administrators to view, manage, and monitor all automated workflows in the system.',
                        'This provides centralised control over business process automation and communications.',
                        'Accessing Workflow Management:',
                        'Navigate to Admin → Workflows to view all workflows in the system.',
                        'You will see a comprehensive list of all workflows with their details.',
                        'Viewing Workflow List:',
                        'View workflow list with details including:',
                        '  • Workflow name and description',
                        '  • Workflow type (Email, Data Processing, etc.)',
                        '  • Status (Active, Inactive, Draft)',
                        '  • Target locations (All, Specific Groups, Specific Locations)',
                        '  • Last execution time',
                        '  • Execution statistics',
                        'Filtering Workflows:',
                        'Filter workflows to find specific ones:',
                        '  • By status - Show only Active, Inactive, or Draft workflows',
                        '  • By type - Filter by workflow type',
                        '  • By target - Filter by target locations',
                        'Searching Workflows:',
                        'Search workflows by name or description:',
                        '1. Use the search box to enter keywords.',
                        '2. Results will filter automatically.',
                        '3. Clear search to see all workflows again.',
                        'Creating Workflows:',
                        'Click "Create Workflow" to build a new workflow:',
                        '1. Click "Create Workflow" button.',
                        '2. You will be taken to the Workflow Builder.',
                        '3. Follow the workflow creation process.',
                        'Editing Workflows:',
                        'Click on a workflow name to edit it in the workflow builder:',
                        '1. Find the workflow in the list.',
                        '2. Click on the workflow name.',
                        '3. The Workflow Builder will open with the workflow loaded.',
                        '4. Make your changes.',
                        '5. Save the workflow.',
                        'Activating and Deactivating:',
                        'Activate or deactivate workflows as needed:',
                        '  • Active workflows run and execute when triggered',
                        '  • Inactive workflows are paused and do not execute',
                        '  • Change status to control workflow execution',
                        'Deleting Workflows:',
                        'Delete workflows that are no longer needed:',
                        '1. Find the workflow you want to delete.',
                        '2. Click the delete icon.',
                        '3. Confirm the deletion.',
                        '4. Warning: This action cannot be undone.',
                        'Viewing Execution History:',
                        'View workflow execution history and results:',
                        '  • Execution logs showing when workflows ran',
                        '  • Success and failure counts',
                        '  • Error messages and warnings',
                        '  • Performance metrics',
                        '  • Execution details for each run',
                        'Workflow Targets:',
                        'Workflows can target:',
                        '  • All Locations - Applies to all dealerships',
                        '  • Specific Groups - Targets selected dealership groups',
                        '  • Specific Locations - Targets individual locations',
                        'Monitor Performance:',
                        'Monitor workflow performance and adjust as needed:',
                        '  • Review execution success rates',
                        '  • Identify workflows with errors',
                        '  • Optimise workflow configuration',
                        '  • Adjust triggers and conditions',
                        'Using Templates:',
                        'Use workflow templates to speed up workflow creation:',
                        '  • Start from proven templates',
                        '  • Customise templates for your needs',
                        '  • Ensure best practices',
                        'Best Practices:',
                        '  • Regularly review workflow performance',
                        '  • Monitor execution logs for errors',
                        '  • Keep workflows active only when needed',
                        '  • Document workflow purposes',
                        '  • Use templates for common patterns',
                        '  • Test workflows before activating',
                        'Troubleshooting:',
                        '  • Workflows not appearing: Check filters and verify workflows exist',
                        '  • Cannot create workflow: Verify you have Admin permissions',
                        '  • Execution errors: Review execution logs and error messages',
                        '  • Workflow not executing: Check workflow status and trigger configuration'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-workflow-builder',
                    title: 'Workflow Builder',
                    content: 'Building custom automated workflows using the admin workflow builder',
                    instructions: [
                        'Understanding the Workflow Builder:',
                        'The Workflow Builder is the admin interface for creating and editing automated workflows.',
                        'It provides full control over workflow configuration, triggers, actions, and targeting.',
                        'Accessing the Workflow Builder:',
                        'Navigate to Admin → Workflow Builder to create or edit workflows.',
                        'You can also access it by clicking on a workflow name in Admin → Workflows.',
                        'Entering Basic Information:',
                        'Enter workflow name and description:',
                        '1. Workflow name - Clear, descriptive name',
                        '2. Description - Explain what the workflow does and its purpose',
                        '3. This information helps identify and document workflows.',
                        'Selecting Workflow Type:',
                        'Select workflow type (Email, Data Processing, etc.):',
                        '  • Email - Automated email sending workflows',
                        '  • Data Processing - Data manipulation and updates',
                        '  • Notification - Alerts and notifications',
                        '  • Integration - Connecting with external systems',
                        '  • Reporting - Automated report generation',
                        'Choosing Target Locations:',
                        'Choose target locations:',
                        '  • All Locations - Applies to all dealerships',
                        '  • Specific Groups - Targets selected dealership groups',
                        '  • Specific Locations - Targets individual locations',
                        'This determines which dealerships the workflow affects.',
                        'Configuring Triggers:',
                        'Configure workflow triggers that start the workflow execution:',
                        '  • Event-based triggers - Actions that start the workflow',
                        '  • Schedule-based triggers - Time-based execution',
                        '  • Condition-based triggers - Conditional execution',
                        '  • Manual triggers - On-demand execution',
                        'Adding Workflow Steps:',
                        'Add workflow steps and actions to define what the workflow does:',
                        '1. Click "Add Step" or drag actions onto the canvas.',
                        '2. Configure each step with its parameters.',
                        '3. Arrange steps in the desired order.',
                        '4. Connect steps to define workflow flow.',
                        'Configuring Message Templates:',
                        'Configure message templates for email workflows:',
                        '1. Select message templates from available options.',
                        '2. Configure template variables and mappings.',
                        '3. Set up personalisation.',
                        '4. Test message rendering.',
                        'Setting Up Filters:',
                        'Set up filters to determine which records the workflow processes:',
                        '  • Filter by specific criteria',
                        '  • Narrow down target records',
                        '  • Improve workflow efficiency',
                        'Adding Conditions:',
                        'Add conditions to control workflow logic and branching:',
                        '  • IF/THEN logic',
                        '  • Conditional branching',
                        '  • Complex decision trees',
                        'Using the Visual Builder:',
                        'Use the visual builder to create complex workflow logic:',
                        '  • Drag-and-drop interface',
                        '  • Visual representation of workflow flow',
                        '  • Easy to understand and modify',
                        'Testing Workflows:',
                        'Test the workflow before activating it:',
                        '1. Use the test function.',
                        '2. Run with sample data.',
                        '3. Verify steps execute correctly.',
                        '4. Check for errors.',
                        '5. Review output.',
                        'Saving Workflows:',
                        'Save the workflow as a draft or activate it immediately:',
                        '  • Draft - Save for later editing',
                        '  • Active - Start executing immediately',
                        '  • Choose based on readiness',
                        'Saving as Templates:',
                        'Workflows can be saved as templates for reuse:',
                        '1. When saving, choose "Save as Template".',
                        '2. Enter template details.',
                        '3. Template will be available for others to use.',
                        'Using Variable Mappings:',
                        'Use variable mappings to personalise messages and data:',
                        '1. Click "Variables" button.',
                        '2. Configure mappings.',
                        '3. Connect data sources to message tags.',
                        'See Variable Mappings section for detailed instructions.',
                        'Configuring Scheduling:',
                        'Configure scheduling if the workflow should run on a schedule:',
                        '  • Set frequency (daily, weekly, monthly)',
                        '  • Choose specific times',
                        '  • Set time zones',
                        '  • Configure recurrence patterns',
                        'Reviewing Execution Logs:',
                        'Review workflow execution logs to troubleshoot issues:',
                        '  • View execution history',
                        '  • Check for errors',
                        '  • Monitor performance',
                        '  • Identify issues',
                        'Best Practices:',
                        '  • Start with simple workflows',
                        '  • Test thoroughly before activating',
                        '  • Document workflow purpose',
                        '  • Use templates for common patterns',
                        '  • Monitor execution regularly',
                        '  • Keep workflows active only when needed',
                        'Troubleshooting:',
                        '  • Cannot create workflow: Verify you have Admin permissions',
                        '  • Workflow not executing: Check triggers and status',
                        '  • Steps not working: Verify step configuration',
                        '  • Errors in execution: Review execution logs',
                        '  • Performance issues: Check workflow complexity'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-workflow-templates',
                    title: 'Workflow Templates',
                    content: 'Using and managing workflow templates for standardised automation (Admin)',
                    instructions: [
                        'Understanding Workflow Templates:',
                        'Workflow templates provide pre-built workflows that can be customised for specific needs.',
                        'They standardise workflow creation and ensure best practices across the organisation.',
                        'Templates save time and help maintain consistency.',
                        'Accessing Templates:',
                        'Navigate to Admin → Workflow Templates to view available templates.',
                        'You will see a list of templates organised by category or type.',
                        'Browsing Templates:',
                        'Browse templates by category or search for specific types:',
                        '  • Use category filters to find relevant templates',
                        '  • Search by keywords to find specific templates',
                        '  • View template details including description, category, and type',
                        'Using Templates:',
                        'Click "Use Template" to create a new workflow from a template:',
                        '1. Find a template that matches your needs.',
                        '2. Click "Use Template" button.',
                        '3. The workflow builder will open with the template pre-loaded.',
                        '4. The template structure will be ready for customisation.',
                        'Customising Templates:',
                        'Customise the template settings to match your requirements:',
                        '  • Modify workflow name and description',
                        '  • Adjust triggers and conditions',
                        '  • Customise workflow steps',
                        '  • Update variable mappings',
                        '  • Configure target locations',
                        '  • Test the customised workflow',
                        'Creating Your Own Templates:',
                        'Create your own templates from existing workflows:',
                        '1. Open an existing workflow in the Workflow Builder.',
                        '2. When saving, choose "Save as Template".',
                        '3. Enter template details:',
                        '   • Template name',
                        '   • Description',
                        '   • Category',
                        '4. Save the template.',
                        '5. It will be available for use by others.',
                        'Sharing Templates:',
                        'Templates can be shared across the organisation:',
                        '  • Available to all users with appropriate permissions',
                        '  • Document templates clearly',
                        '  • Use consistent naming conventions',
                        '  • Organise by category',
                        'Updating Templates:',
                        'Update templates as business processes evolve:',
                        '1. Find the template to update.',
                        '2. Edit the template structure.',
                        '3. Save the updated template.',
                        '4. Note: Updates may affect workflows created from the template.',
                        'Deleting Templates:',
                        'Delete templates that are no longer needed:',
                        '1. Find the template.',
                        '2. Click delete.',
                        '3. Confirm deletion.',
                        '4. Template will be removed.',
                        'Benefits:',
                        'Templates help:',
                        '  • Standardise workflow creation',
                        '  • Ensure best practices',
                        '  • Save time in workflow development',
                        '  • Maintain consistency',
                        '  • Provide examples to learn from',
                        'Best Practices:',
                        '  • Use templates for common workflow patterns',
                        '  • Customise templates to match specific needs',
                        '  • Document custom templates clearly',
                        '  • Share useful templates with your team',
                        '  • Keep templates current with process changes',
                        '  • Review and update templates regularly',
                        'Troubleshooting:',
                        '  • Templates not appearing: Verify you have appropriate permissions',
                        '  • Cannot use template: Check template is available and accessible',
                        '  • Template not working: Review template structure and test it',
                        '  • Cannot create template: Verify you have Admin permissions'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'admin-field-integration',
                    title: 'Field Integration Demo',
                    content: 'Testing and demonstrating field mapping between systems (Admin)',
                    instructions: [
                        'Understanding Field Integration Demo:',
                        'The Field Integration Demo tool helps test and demonstrate field mapping between systems.',
                        'It allows administrators to plan, test, and verify field mappings before implementing integrations.',
                        'This is a testing and demonstration tool for integration planning.',
                        'Accessing Field Integration:',
                        'Navigate to Admin → Field Integration to test field mappings.',
                        'You will see the field integration interface with options to configure and test mappings.',
                        'Selecting Systems:',
                        'Select source and target systems to map fields between:',
                        '1. Choose the source system (where data comes from).',
                        '2. Choose the target system (where data goes to).',
                        '3. Systems can include:',
                        '   • External databases',
                        '   • File imports',
                        '   • Other ClientBox modules',
                        '   • Third-party systems',
                        'Configuring Field Mappings:',
                        'Configure field mappings to link fields from one system to another:',
                        '1. Review available fields in the source system.',
                        '2. Review available fields in the target system.',
                        '3. Map each source field to its corresponding target field.',
                        '4. Configure data transformations if needed.',
                        '5. Set up validation rules.',
                        'Testing Mappings:',
                        'Test mappings to verify data transformation works correctly:',
                        '1. Use sample data to test mappings.',
                        '2. Run the mapping test.',
                        '3. Review the transformed output.',
                        '4. Verify data is correctly mapped and transformed.',
                        '5. Make adjustments if needed.',
                        '6. Re-test until mappings work correctly.',
                        'Use Cases:',
                        'Use this tool to:',
                        '  • Plan integrations before implementing them',
                        '  • Test field mappings with sample data',
                        '  • Demonstrate integration concepts',
                        '  • Validate mapping logic',
                        '  • Troubleshoot integration issues',
                        'Field Integration Usage:',
                        'Field integration is used in:',
                        '  • Data imports - Mapping file columns to system fields',
                        '  • System integrations - Connecting different systems',
                        '  • Data synchronisation - Keeping data in sync',
                        '  • Data transformation - Converting data formats',
                        'Best Practices:',
                        '  • Test mappings thoroughly before implementing',
                        '  • Use sample data that represents real scenarios',
                        '  • Document mapping configurations',
                        '  • Verify data transformations are correct',
                        '  • Test edge cases and error conditions',
                        '  • Keep mapping documentation up to date',
                        'Troubleshooting:',
                        '  • Mappings not working: Verify field names and data types match',
                        '  • Data transformation errors: Check transformation rules and logic',
                        '  • Cannot access tool: Verify you have Admin role permissions',
                        '  • Test failing: Review error messages and check mapping configuration'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'audit-logs',
                    title: 'Audit Logs',
                    content: 'Viewing system audit logs for security, compliance, and troubleshooting',
                    instructions: [
                        'Understanding Audit Logs:',
                        'Audit logs track all significant actions in the ClientBox system.',
                        'They provide a complete record of who did what, when, and from where.',
                        'Audit logs are essential for security, compliance, troubleshooting, and maintaining system integrity.',
                        'Accessing Audit Logs:',
                        'Navigate to Admin → Audit Logs to view system activity.',
                        'You will see a list of logged actions with details about each event.',
                        'What is Logged:',
                        'Logs include comprehensive information:',
                        '  • User actions - Logins, logouts, data access',
                        '  • Data changes - Creates, updates, deletes',
                        '  • System events - Configuration changes, errors',
                        '  • Administrative actions - User management, role changes',
                        '  • Security events - Failed login attempts, permission changes',
                        'Each log entry typically includes:',
                        '  • Timestamp - When the action occurred',
                        '  • User - Who performed the action',
                        '  • Action type - What was done',
                        '  • Entity - What was affected',
                        '  • Details - Additional information',
                        '  • IP address - Where the action came from',
                        'Filtering Audit Logs:',
                        'Filter logs to find specific information:',
                        '  • By user - Show actions by specific users',
                        '  • By date range - Filter actions within a time period',
                        '  • By action type - Show specific types of actions',
                        '  • By entity - Filter by what was affected',
                        '  • By IP address - Track actions from specific locations',
                        'Use filters to narrow down logs and find relevant information quickly.',
                        'Viewing Log Details:',
                        'View detailed information about each logged action:',
                        '1. Click on a log entry to view its full details.',
                        '2. See complete information about the action:',
                        '   • Full timestamp',
                        '   • User information',
                        '   • Action description',
                        '   • Before and after values (for updates)',
                        '   • Related records',
                        '   • Error messages (if applicable)',
                        'Security and Compliance:',
                        'Audit logs help with security, compliance, and troubleshooting:',
                        '  • Track user activity for security monitoring',
                        '  • Maintain compliance with data protection regulations',
                        '  • Investigate security incidents',
                        '  • Verify who made specific changes',
                        '  • Identify patterns of suspicious activity',
                        '  • Provide evidence for audits and investigations',
                        'Exporting Audit Logs:',
                        'Export audit logs for compliance reporting if needed:',
                        '1. Apply filters to select the logs you want to export.',
                        '2. Click "Export" or "Download".',
                        '3. Choose export format (CSV, Excel, PDF).',
                        '4. The export will be generated and downloaded.',
                        'Exported logs can be used for:',
                        '  • Compliance reporting',
                        '  • Security audits',
                        '  • Forensic investigations',
                        '  • Archival and record-keeping',
                        'Log Retention:',
                        'Logs are retained according to your system configuration:',
                        '  • Retention period is configured by system administrators',
                        '  • Older logs may be archived or deleted',
                        '  • Important logs should be exported before retention period expires',
                        '  • Check your system configuration for retention policies',
                        'Regular Review:',
                        'Regularly review audit logs to monitor system usage and security:',
                        '  • Review logs on a regular schedule',
                        '  • Look for unusual patterns or activities',
                        '  • Monitor failed login attempts',
                        '  • Track administrative changes',
                        '  • Verify expected user activities',
                        'Tracking Changes:',
                        'Use audit logs to track who made changes and when:',
                        '  • Identify who modified specific records',
                        '  • See what changed and when',
                        '  • Understand the sequence of events',
                        '  • Reconstruct what happened',
                        'Identifying Suspicious Activity:',
                        'Logs help identify unauthorised access or suspicious activity:',
                        '  • Multiple failed login attempts',
                        '  • Unusual access patterns',
                        '  • Actions outside normal business hours',
                        '  • Access from unexpected locations',
                        '  • Unauthorised permission changes',
                        '  • Unexpected data modifications',
                        'Best Practices:',
                        '  • Review audit logs regularly',
                        '  • Set up alerts for critical events',
                        '  • Export important logs for archival',
                        '  • Document investigation procedures',
                        '  • Train staff on log review processes',
                        '  • Keep logs secure and accessible only to authorised personnel',
                        '  • Monitor for patterns and trends',
                        'Troubleshooting:',
                        '  • Logs not appearing: Verify logging is enabled and check date range',
                        '  • Cannot access logs: Verify you have Admin role permissions',
                        '  • Export not working: Check permissions and try a smaller date range',
                        '  • Missing log entries: Verify logging is configured correctly',
                        '  • Logs seem incomplete: Check retention settings and system configuration'
                    ],
                    image: null,
                    video: null
                },
                {
                    id: 'system-settings',
                    title: 'System Settings',
                    content: 'Configuring system-wide settings and global configuration options',
                    instructions: [
                        'Understanding System Settings:',
                        'System settings control global configuration options that affect the entire ClientBox system.',
                        'These settings define dropdown options, categories, message names, and other system-wide configurations.',
                        'Changes to system settings affect all areas where they are used.',
                        'Accessing System Settings:',
                        'Navigate to Admin → Settings to access system settings.',
                        'You will see various sections for different types of settings.',
                        'Managing Dropdown Options:',
                        'Manage dropdown options used throughout the system:',
                        '1. Find the dropdown option type you want to manage.',
                        '2. View existing options in the list.',
                        '3. Add new options by clicking "Add" next to the option type.',
                        '4. Enter the option value and any additional details.',
                        '5. Delete options that are no longer needed.',
                        '6. Reorder options by dragging them to change display order.',
                        'Options are used in dropdowns throughout the system.',
                        'Checklist Categories:',
                        'Configure checklist categories used in onboarding checklists:',
                        '1. Navigate to the Checklist Categories section.',
                        '2. View existing categories.',
                        '3. Add new categories as needed.',
                        '4. Edit or delete categories.',
                        '5. Reorder categories to control display order.',
                        'Categories help organise checklist items logically.',
                        'Message Names:',
                        'Manage sales and service message names for contact plans:',
                        '1. Navigate to Message Names section.',
                        '2. Separate sections for Sales and Service message names.',
                        '3. Add new message names.',
                        '4. Edit or delete existing message names.',
                        '5. Message names are used when configuring contact plans.',
                        'Send When Events:',
                        'Configure "Send When" events for contact plan timing:',
                        '1. Navigate to Send When Events section.',
                        '2. View available events.',
                        '3. Add new events as needed.',
                        '4. Edit or delete events.',
                        '5. Events define when contact plan messages should be sent.',
                        'Common events include:',
                        '  • Onboarding Start',
                        '  • Checklist Complete',
                        '  • Location Added',
                        '  • Custom events',
                        'Feed Types:',
                        'Manage feed types (Email, SMS, etc.) for contact plans:',
                        '1. Navigate to Feed Types section.',
                        '2. View available feed types.',
                        '3. Add new feed types (Email, SMS, Push, etc.).',
                        '4. Edit or delete feed types.',
                        '5. Feed types define communication channels for contact plans.',
                        'Default Values and Preferences:',
                        'Configure default values and system preferences:',
                        '  • Set system-wide defaults for new records',
                        '  • Configure default time zones',
                        '  • Set default date formats',
                        '  • Configure default currency and number formats',
                        '  • Set default language preferences',
                        'System-Wide Defaults:',
                        'Set system-wide defaults for new records:',
                        '  • Default status values',
                        '  • Default priority levels',
                        '  • Default assignment rules',
                        '  • Default notification settings',
                        'Customising Labels and Terminology:',
                        'Customise system labels and terminology if needed:',
                        '  • Change field labels',
                        '  • Update section names',
                        '  • Modify button text',
                        '  • Adjust terminology to match your organisation',
                        'Note: Customisation options may vary based on system configuration.',
                        'Saving Settings:',
                        'Save settings changes to apply them system-wide:',
                        '1. Make your changes to settings.',
                        '2. Review changes before saving.',
                        '3. Click "Save" or "Apply" to save changes.',
                        '4. Changes take effect immediately.',
                        '5. Some changes may require a page refresh to see.',
                        'Reviewing Settings:',
                        'Review settings periodically to ensure they match current requirements:',
                        '  • Check that dropdown options are current',
                        '  • Verify categories are still relevant',
                        '  • Update message names as needed',
                        '  • Remove unused options',
                        '  • Add new options as requirements change',
                        'Best Practices:',
                        '  • Document changes to system settings',
                        '  • Review settings regularly',
                        '  • Remove unused options to keep lists clean',
                        '  • Use consistent naming conventions',
                        '  • Test changes in a safe environment when possible',
                        '  • Coordinate with team members on significant changes',
                        '  • Keep settings organised and well-documented',
                        'Troubleshooting:',
                        '  • Cannot access settings: Verify you have Admin role permissions',
                        '  • Settings not saving: Check required fields are filled and try again',
                        '  • Options not appearing: Verify options are saved and page is refreshed',
                        '  • Changes not applying: Check that changes are saved and refresh the page',
                        '  • Cannot delete option: Verify option is not in use and check permissions'
                    ],
                    image: null,
                    video: null
                }
            ]
        }
    ];

    // Get all subsections for search results
    const getAllSubsections = () => {
        const allSubsections = [];
        sections.forEach(section => {
            section.subsections.forEach(subsection => {
                allSubsections.push({
                    ...subsection,
                    sectionId: section.id,
                    sectionTitle: section.title,
                    sectionIcon: section.icon
                });
            });
        });
        return allSubsections;
    };

    const handleSectionClick = (section) => {
        const isCurrentlyExpanded = expandedSections.has(section.id);
        setSelectedSection(section);
        setSelectedSubsection(null);
        // Toggle expanded state - if not expanded, expand it; if expanded and clicking same section, collapse
        if (!isCurrentlyExpanded || selectedSection?.id !== section.id) {
            setExpandedSections(prev => new Set(prev).add(section.id));
        } else {
            setExpandedSections(prev => {
                const newSet = new Set(prev);
                newSet.delete(section.id);
                return newSet;
            });
        }
    };

    const handleSubsectionClick = (section, subsection) => {
        setSelectedSection(section);
        setSelectedSubsection(subsection.id);
        // Ensure section is expanded
        setExpandedSections(prev => new Set(prev).add(section.id));
        // Scroll to subsection after a brief delay
        setTimeout(() => {
            const element = document.getElementById(`subsection-${subsection.id}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    const handleSearchResultClick = (subsection) => {
        const section = sections.find(s => s.id === subsection.sectionId);
        if (section) {
            handleSubsectionClick(section, subsection);
            setSearchTerm(''); // Clear search after selection
        }
    };

    const filteredSections = sections.filter(section => {
        if (isClient && !CLIENT_HELP_SECTIONS.has(section.id)) return false;
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return section.title.toLowerCase().includes(searchLower) ||
               section.description.toLowerCase().includes(searchLower) ||
               section.subsections.some(sub => 
                   sub.title.toLowerCase().includes(searchLower) ||
                   sub.content.toLowerCase().includes(searchLower) ||
                   (sub.instructions && sub.instructions.some(inst => inst.toLowerCase().includes(searchLower)))
               );
    });

    // Filter subsections for search results
    const filteredSubsections = searchTerm ? getAllSubsections().filter(subsection => {
        const searchLower = searchTerm.toLowerCase();
        return subsection.title.toLowerCase().includes(searchLower) ||
               subsection.content.toLowerCase().includes(searchLower) ||
               (subsection.instructions && subsection.instructions.some(inst => inst.toLowerCase().includes(searchLower)));
    }) : [];

    const renderContent = (section) => {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <section.icon size={24} className="text-blue-600" />
                        <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                    </div>
                    <p className="text-gray-600 mb-6">{section.description}</p>
                    
                    <div className="space-y-6">
                        {section.subsections.map((subsection) => (
                            <div 
                                id={`subsection-${subsection.id}`}
                                key={subsection.id} 
                                className={`border rounded-lg p-6 transition-colors ${
                                    selectedSubsection === subsection.id 
                                        ? 'border-blue-500 bg-blue-50 shadow-md' 
                                        : 'border-gray-200 hover:border-blue-300'
                                }`}
                            >
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">{subsection.title}</h3>
                                <p className="text-gray-600 mb-4">{subsection.content}</p>
                                
                                {/* Detailed Instructions */}
                                {subsection.instructions && subsection.instructions.length > 0 && (
                                    <div className="mb-6">
                                        <h4 className="text-lg font-medium text-gray-900 mb-4">Detailed Guide</h4>
                                        <div className="prose prose-sm max-w-none">
                                            {subsection.instructions.map((instruction, idx) => (
                                                <div key={idx}>
                                                    {renderInstructionText(instruction)}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Image */}
                                {subsection.image && (
                                    <HelpImage 
                                        src={subsection.image} 
                                        alt={`Screenshot for ${subsection.title}`}
                                        caption={`Screenshot showing ${subsection.title.toLowerCase()}`}
                                    />
                                )}
                                
                                {/* Video */}
                                {subsection.video && (
                                    <HelpVideo 
                                        src={subsection.video.src || subsection.video}
                                        type={subsection.video.type || 'youtube'}
                                        title={subsection.video.title || `Video tutorial for ${subsection.title}`}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate(-1)}
                                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                            >
                                <ArrowLeft size={20} />
                                <span>Back</span>
                            </button>
                            <div className="h-6 w-px bg-gray-300"></div>
                            <div className="flex items-center gap-3">
                                <Book size={24} className="text-blue-600" />
                                <h1 className="text-2xl font-bold text-gray-900">Help & Documentation</h1>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow p-4 sticky top-24">
                            {/* Search */}
                            <div className="mb-4">
                                <div className="relative">
                                    <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search help..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Search Results */}
                            {searchTerm && filteredSubsections.length > 0 && (
                                <div className="mb-4 pb-4 border-b border-gray-200">
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Search Results</h3>
                                    <div className="space-y-1 max-h-64 overflow-y-auto">
                                        {filteredSubsections.slice(0, 10).map((subsection) => (
                                            <button
                                                key={`${subsection.sectionId}-${subsection.id}`}
                                                onClick={() => handleSearchResultClick(subsection)}
                                                className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-blue-50 transition-colors"
                                            >
                                                <div className="font-medium text-gray-900 truncate">{subsection.title}</div>
                                                <div className="text-gray-500 text-xs truncate">{subsection.sectionTitle}</div>
                                            </button>
                                        ))}
                                        {filteredSubsections.length > 10 && (
                                            <div className="text-xs text-gray-500 px-2 py-1">
                                                +{filteredSubsections.length - 10} more results
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Sections List */}
                            <div className="space-y-1 max-h-[calc(100vh-200px)] overflow-y-auto">
                                {filteredSections.map((section) => {
                                    const isExpanded = expandedSections.has(section.id);
                                    const isSelected = selectedSection?.id === section.id;
                                    
                                    return (
                                        <div key={section.id} className="space-y-1">
                                            <button
                                                onClick={() => handleSectionClick(section)}
                                                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                                                    isSelected
                                                        ? 'bg-blue-100 text-blue-700 font-semibold'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                <section.icon size={18} />
                                                <span className="flex-1 text-sm">{section.title}</span>
                                                <ChevronRight 
                                                    size={16} 
                                                    className={`transition-transform ${
                                                        isExpanded ? 'transform rotate-90' : ''
                                                    } ${isSelected ? 'text-blue-700' : 'text-gray-400'}`} 
                                                />
                                            </button>
                                            
                                            {/* Subsections */}
                                            {isExpanded && (
                                                <div className="ml-8 space-y-1 border-l-2 border-gray-200 pl-2">
                                                    {section.subsections.map((subsection) => (
                                                        <button
                                                            key={subsection.id}
                                                            onClick={() => handleSubsectionClick(section, subsection)}
                                                            className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                                                                selectedSubsection === subsection.id
                                                                    ? 'bg-blue-200 text-blue-800 font-medium'
                                                                    : 'text-gray-600 hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            {subsection.title}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="lg:col-span-3">
                        {selectedSection ? (
                            renderContent(selectedSection)
                        ) : (
                            <div className="bg-white rounded-lg shadow p-12 text-center">
                                <Book size={64} className="mx-auto text-gray-400 mb-4" />
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Help & Documentation</h2>
                                <p className="text-gray-600 mb-6">
                                    Select a section from the sidebar to view detailed documentation, step-by-step instructions, screenshots, and video tutorials.
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
                                    {filteredSections.slice(0, 6).map((section) => (
                                        <button
                                            key={section.id}
                                            onClick={() => handleSectionClick(section)}
                                            className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                                        >
                                            <section.icon size={24} className="text-blue-600 mb-2" />
                                            <h3 className="font-semibold text-gray-900">{section.title}</h3>
                                            <p className="text-sm text-gray-600 mt-1">{section.description}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
